import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { FaChevronRight, FaFire, FaPercent, FaBolt } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import { globalLogout } from "../../../Utils/Logout";
const DealsPage = () => {
  const [categories, setCategories] = useState([]);
  const [discountedProducts, setDiscountedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [featuredDeal, setFeaturedDeal] = useState(null);
  const [categoryDiscounts, setCategoryDiscounts] = useState({});
  const [categoryMap, setCategoryMap] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch categories and discounted products simultaneously
        const [categoriesResponse, discountedResponse] = await Promise.all([
          axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/seller/category/list`),
          axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/seller/product/get-by-discount`)
        ]);
        
        if (categoriesResponse.data.success) {
          const processedCategories = categoriesResponse.data.categories
            .filter(cat => cat.published)
            .map(category => {
              const topLevelSubs = (category.subcategories || [])
                .filter(sub => sub.published)
                .map(sub => ({
                  ...sub,
                  price: Math.floor(Math.random() * 10000) + 1000,
                  discount: 0 // Initialize with 0, will be updated later
                }));

              let allSubcategories = [...topLevelSubs];
              if (topLevelSubs.length < 6) {
                const getNestedSubs = (subs) => {
                  let result = [];
                  subs.forEach(sub => {
                    if (sub.subcategories && sub.subcategories.length > 0) {
                      const nested = sub.subcategories
                        .filter(nestedSub => nestedSub.published)
                        .map(nestedSub => ({
                          ...nestedSub,
                          price: Math.floor(Math.random() * 10000) + 1000,
                          discount: 0
                        }));
                      result = result.concat(nested);
                    }
                  });
                  return result;
                };

                const nestedSubs = getNestedSubs(category.subcategories || []);
                const needed = 6 - topLevelSubs.length;
                allSubcategories = allSubcategories.concat(nestedSubs.slice(0, needed));
              }

              return {
                ...category,
                subcategories: allSubcategories.slice(0, 6) // Limit to 6 subcategories
              };
            })
            .filter(category => category.subcategories.length > 0);
          
          setCategories(processedCategories);
          
          // Create a mapping of category IDs to category names for easier lookup
          const catMap = {};
          processedCategories.forEach(category => {
            catMap[category.id] = category.name;
            (category.subcategories || []).forEach(subcategory => {
              catMap[subcategory.id] = subcategory.name;
            });
          });
          setCategoryMap(catMap);
          
          if (processedCategories.length > 0) {
            const validCategories = processedCategories.filter(c => c.subcategories.length > 0);
            if (validCategories.length > 0) {
              const randomCategory = validCategories[
                Math.floor(Math.random() * validCategories.length)
              ];
              const randomSubcategory = randomCategory.subcategories[
                Math.floor(Math.random() * randomCategory.subcategories.length)
              ];
              setFeaturedDeal({
                ...randomSubcategory,
                categoryName: randomCategory.name
              });
            }
          }
        }
        
        // Process discounted products
        if (discountedResponse.data.success && discountedResponse.data.products) {
          setDiscountedProducts(discountedResponse.data.products);
          
          // Calculate max discount for each category
          const discounts = {};
          discountedResponse.data.products.forEach(product => {
            if (product.category && product.variants && product.variants.length > 0) {
              const categoryId = product.category;
              const discount = Math.max(...product.variants.map(v => v.discount || 0));
              
              if (!discounts[categoryId] || discount > discounts[categoryId]) {
                discounts[categoryId] = discount;
              }
            }
          });
          
          setCategoryDiscounts(discounts);
        }
        
        setLoading(false);
      } catch (error) {
        globalLogout(error)
        console.error("Error fetching data:", error);
        setError("Failed to load data. Please try again later.");
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Function to get the maximum discount for a category
  const getCategoryDiscount = (categoryId) => {
    return categoryDiscounts[categoryId] || 0;
  };

  // Function to get discounted products for a specific category and its subcategories
  const getDiscountedProductsForCategoryTree = (category) => {
    const allCategoryIds = [category.id];
    
    // Recursively get all subcategory IDs
    const getSubcategoryIds = (subcategories) => {
      subcategories.forEach(sub => {
        allCategoryIds.push(sub.id);
        if (sub.subcategories && sub.subcategories.length > 0) {
          getSubcategoryIds(sub.subcategories);
        }
      });
    };
    
    if (category.subcategories && category.subcategories.length > 0) {
      getSubcategoryIds(category.subcategories);
    }
    
    return discountedProducts.filter(product => 
      allCategoryIds.includes(product.category)
    );
  };

  const generateTag = (discount, productCount) => {
    if (discount > 0) {
      return {
        text: `Upto ${discount}% off`, 
        icon: <FaPercent className="mr-1" />, 
        color: "bg-gradient-to-r from-red-500 to-orange-500 text-white"
      };
    }
    
    // Show tags even when there are no discounts but products exist
    if (productCount > 0) {
      const staticTags = [
        { text: "Hot Deals", icon: <FaFire className="mr-1" />, color: "bg-gradient-to-r from-red-500 to-orange-500 text-white" },
        { text: "Limited Time", icon: <FaBolt className="mr-1" />, color: "bg-gradient-to-r from-purple-500 to-pink-500 text-white" },
        { text: "Popular", icon: <FaFire className="mr-1" />, color: "bg-gradient-to-r from-blue-500 to-cyan-500 text-white" },
        { text: "Trending", icon: <FaBolt className="mr-1" />, color: "bg-gradient-to-r from-green-500 to-emerald-500 text-white" }
      ];
      return staticTags[Math.floor(Math.random() * staticTags.length)];
    }
    
    return null;  // Only return null if there are no products at all
  };

  const getValidImageUrl = (url) => {
    if (!url) return "https://via.placeholder.com/150?text=No+Image";
    if (url.includes('google.com') || url.includes('googleusercontent') || 
        url.includes('www.google.com')) {
      return "https://via.placeholder.com/150?text=No+Image";
    }
    return url;
  };

  if (loading) {
    return (
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="bg-gradient-to-b from-gray-50 to-gray-100 py-12"
      >
        <div className="container mx-auto px-4 space-y-12">
          {[...Array(3)].map((_, i) => (
            <div key={i}>
              <div className="h-8 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full w-1/4 mb-8 animate-pulse"></div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-6">
                {[...Array(6)].map((_, j) => (
                  <motion.div 
                    key={j} 
                    initial={{ opacity: 0.5 }}
                    animate={{ opacity: 0.8 }}
                    transition={{ repeat: Infinity, repeatType: "reverse", duration: 1, delay: j * 0.1 }}
                    className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl h-64 shadow-sm"
                  ></motion.div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </motion.div>
    );
  }

  if (error) {
    return (
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="bg-gradient-to-b from-gray-50 to-gray-100 py-12 container mx-auto px-4 text-center"
      >
        <div className="max-w-md mx-auto bg-white p-8 rounded-xl shadow-lg">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h3 className="text-xl font-bold text-gray-800 mb-2">Oops! Something went wrong</h3>
          <p className="text-gray-600 mb-6">{error}</p>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-6 py-3 rounded-lg font-medium shadow-md"
            onClick={() => window.location.reload()}
          >
            Try Again
          </motion.button>
        </div>
      </motion.div>
    );
  }

  if (categories.length === 0) {
    return (
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="bg-gradient-to-b from-gray-50 to-gray-100 py-12 container mx-auto px-4 text-center"
      >
        <div className="max-w-md mx-auto bg-white p-8 rounded-xl shadow-lg">
          <div className="text-gray-400 text-5xl mb-4">🛍️</div>
          <h3 className="text-xl font-bold text-gray-800 mb-2">No Deals Available</h3>
          <p className="text-gray-600 mb-6">Check back later for exciting offers!</p>
          <Link to="/">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-6 py-3 rounded-lg font-medium shadow-md"
            >
              Go to Home
            </motion.button>
          </Link>
        </div>
      </motion.div>
    );
  }

//   return (
//     <motion.div 
//       initial={{ opacity: 0 }}
//       animate={{ opacity: 1 }}
//       transition={{ duration: 0.5 }}
//       className="bg-gradient-to-b from-gray-50 to-gray-100 py-12"
//     >
//       <div className="container mx-auto px-4 space-y-12">
//         {/* Featured Deals Banner */}
//         {featuredDeal && (
//           <motion.div
//             initial={{ opacity: 0, y: 20 }}
//             animate={{ opacity: 1, y: 0 }}
//             transition={{ duration: 0.6 }}
//             className="relative bg-gradient-to-r from-indigo-600 to-purple-700 rounded-2xl p-8 text-white overflow-hidden shadow-xl"
//           >
//             <div className="absolute inset-0 bg-noise opacity-10"></div>
//             <div className="absolute -right-20 -top-20 w-64 h-64 bg-white bg-opacity-10 rounded-full"></div>
//             <div className="absolute -left-20 -bottom-20 w-80 h-80 bg-white bg-opacity-5 rounded-full"></div>
            
//             <div className="relative z-10 flex flex-col md:flex-row items-center">
//               <div className="md:w-1/2 mb-8 md:mb-0">
//                 <motion.div 
//                   initial={{ x: -20 }}
//                   animate={{ x: 0 }}
//                   transition={{ delay: 0.2 }}
//                   className="inline-block px-4 py-2 bg-white bg-opacity-20 rounded-full text-sm mb-6 backdrop-blur-sm"
//                 >
//                   {featuredDeal.categoryName}
//                 </motion.div>
//                 <motion.h3 
//                   initial={{ opacity: 0, y: 10 }}
//                   animate={{ opacity: 1, y: 0 }}
//                   transition={{ delay: 0.3 }}
//                   className="text-3xl md:text-4xl font-bold mb-4 leading-tight"
//                 >
//                   {featuredDeal.name}
//                 </motion.h3>
//                 <motion.p 
//                   initial={{ opacity: 0 }}
//                   animate={{ opacity: 1 }}
//                   transition={{ delay: 0.4 }}
//                   className="mb-6 text-white text-opacity-90 max-w-md"
//                 >
//                   Don't miss out on this exclusive offer! Limited time only.
//                 </motion.p>
//                 <motion.div
//                   initial={{ opacity: 0 }}
//                   animate={{ opacity: 1 }}
//                   transition={{ delay: 0.5 }}
//                 >
//                   <Link 
//                     to={`/subcategory/${featuredDeal.id}`}
//                     className="inline-flex items-center bg-white text-indigo-600 px-8 py-3 rounded-xl font-bold hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
//                   >
//                     Shop Now <FaChevronRight className="ml-2" />
//                   </Link>
//                 </motion.div>
//               </div>
//               <div className="md:w-1/2 flex justify-center">
//                 <motion.div
//                   initial={{ scale: 0.9, rotate: -5 }}
//                   animate={{ scale: 1, rotate: 0 }}
//                   transition={{ type: "spring", stiffness: 100, damping: 10 }}
//                   whileHover={{ scale: 1.05, rotate: 5 }}
//                   className="relative"
//                 >
//                   <div className="absolute -inset-4 bg-white bg-opacity-20 rounded-2xl blur-xl"></div>
//                   <img
//                     src={getValidImageUrl(featuredDeal.media)}
//                     alt={featuredDeal.name}
//                     className="relative z-10 w-72 h-72 object-contain drop-shadow-2xl"
//                   />
//                 </motion.div>
//               </div>
//             </div>
//           </motion.div>
//         )}

//         {/* Category Sections */}
//         <AnimatePresence>
//           {categories.map((category, index) => {
//             const categoryDiscount = getCategoryDiscount(category.id);
//             const discountedProductsInCategory = getDiscountedProductsForCategoryTree(category);
            
//             return (
//               <motion.div 
//                 key={category.id}
//                 initial={{ opacity: 0, y: 30 }}
//                 animate={{ opacity: 1, y: 0 }}
//                 transition={{ delay: index * 0.1, type: "spring" }}
//                 className="bg-white p-8 rounded-2xl shadow-md border border-gray-100 overflow-hidden"
//               >
//                 {/* Section Header */}
//                 <div className="flex justify-between items-center mb-8">
//                   <div className="flex items-center">
//                     <motion.h2 
//                       whileHover={{ x: 5 }}
//                       className="text-3xl font-bold text-gray-900 flex items-center"
//                     >
//                       <span className="w-3 h-8 bg-gradient-to-b from-indigo-500 to-purple-600 rounded-full mr-4"></span>
//                       {category.name}
//                     </motion.h2>
                    
//                     {/* Display category discount if available */}
//                     {categoryDiscount > 0 && (
//                       <motion.div 
//                         initial={{ scale: 0 }}
//                         animate={{ scale: 1 }}
//                         className="ml-4 bg-gradient-to-r from-red-500 to-orange-500 text-white px-3 py-1 rounded-full text-sm font-bold flex items-center"
//                       >
//                         <FaPercent className="mr-1" /> Save up to {categoryDiscount}%
//                       </motion.div>
//                     )}
//                   </div>
                  
//                   <Link 
//                     to={`/category/${category.id}`} 
//                     className="flex items-center text-indigo-600 font-medium hover:text-indigo-800 transition-colors group"
//                   >
//                     <motion.span 
//                       animate={{ x: [0, 3, 0] }}
//                       transition={{ repeat: Infinity, duration: 1.5 }}
//                       className="mr-2 group-hover:mr-3 transition-all"
//                     >
//                       View All
//                     </motion.span>
//                     <FaChevronRight className="group-hover:translate-x-1 transition-transform" />
//                   </Link>
//                 </div>
                
//                 {/* Discounted Products Preview (if available) */}
//                 {/* {discountedProductsInCategory.length > 0 && (
//                   <motion.div 
//                     initial={{ opacity: 0, height: 0 }}
//                     animate={{ opacity: 1, height: "auto" }}
//                     className="mb-6 bg-gradient-to-r from-orange-50 to-red-50 p-4 rounded-lg border border-orange-200"
//                   >
//                     <h3 className="text-lg font-semibold text-orange-800 mb-2 flex items-center">
//                       <FaFire className="mr-2 text-orange-500" /> Hot Deals in {category.name}
//                     </h3>
//                     <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
//                       {discountedProductsInCategory.slice(0, 4).map(product => (
//                         <motion.div 
//                           key={product._id}
//                           whileHover={{ y: -5 }}
//                           className="bg-white p-3 rounded-lg border border-orange-100 shadow-sm flex items-center"
//                         >
//                           <img 
//                             src={getValidImageUrl(product.variants?.[0]?.images?.[0])} 
//                             alt={product.name}
//                             className="w-12 h-12 object-contain mr-3"
//                           />
//                           <div>
//                             <p className="text-sm font-medium text-gray-800 line-clamp-1">{product.name}</p>
//                             <p className="text-xs text-orange-600 font-bold">
//                               {Math.max(...product.variants.map(v => v.discount || 0))}% OFF
//                             </p>
//                           </div>
//                         </motion.div>
//                       ))}
//                     </div>
//                   </motion.div>
//                 )} */}
                
//                 {/* Subcategories Grid - Now showing exactly 6 items */}
//                 <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-6">
//                   {category.subcategories.map((subcategory) => {
//                     // Find products in this subcategory to determine discount
//                     const subcategoryProducts = discountedProducts.filter(
//                       product => product.category === subcategory.id
//                     );
                    
//                     const subcategoryDiscount = subcategoryProducts.length > 0 
//                       ? Math.max(...subcategoryProducts.map(p => 
//                           Math.max(...p.variants.map(v => v.discount || 0))
//                         ))
//                       : 0;
                    
//                     // Count products in this subcategory
//                     const productCount = subcategoryProducts.length;
                    
//                     // Generate tag based on actual data
//                     const tag = generateTag(subcategoryDiscount, productCount);
                    
//                     return (
//                       <motion.div
//                         key={subcategory.id}
//                         initial={{ opacity: 0, scale: 0.9 }}
//                         animate={{ opacity: 1, scale: 1 }}
//                         transition={{ type: "spring" }}
//                         whileHover={{ y: -8, scale: 1.02 }}
//                         whileTap={{ scale: 0.98 }}
//                         className="relative"
//                       >
//                         <div className="absolute inset-0 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
//                         <Link
//                           to={`/subcategory/${subcategory.id}`}
//                           className="group block p-5 rounded-xl transition-all duration-300 flex flex-col h-full bg-white relative z-10 border border-gray-200 hover:border-transparent hover:shadow-lg"
//                         >
//                           {/* Product Image */}
//                           <div className="w-full h-40 mb-4 overflow-hidden flex items-center justify-center">
//                             <motion.img
//                               src={getValidImageUrl(subcategory.media)}
//                               alt={subcategory.name}
//                               className="max-h-full max-w-full object-contain transition-transform duration-500 group-hover:scale-110"
//                               initial={{ opacity: 0 }}
//                               animate={{ opacity: 1 }}
//                               transition={{ duration: 0.5 }}
//                             />
//                           </div>
                          
//                           {/* Product Title */}
//                           <h3 className="text-lg font-semibold text-gray-800 mb-2 line-clamp-2 text-center group-hover:text-indigo-600 transition-colors">
//                             {subcategory.name}
//                           </h3>
                          
//                           {/* Discount Badge */}
//                           {/* Discount Badge - shown only when there's a discount */}
// {subcategoryDiscount > 0 && (
//   <motion.div 
//     initial={{ scale: 0 }}
//     animate={{ scale: 1 }}
//     className="text-center mt-auto mb-2"
//   >
//     <span className="inline-block bg-gradient-to-r from-red-500 to-orange-500 text-white text-sm font-bold px-3 py-1 rounded-full">
//      Save Up To {subcategoryDiscount}% OFF
//     </span>
//   </motion.div>
// )}

// {/* Promotional Tag - shown when there are products, regardless of discount */}
// {tag && subcategoryDiscount === 0 && (
//   <motion.div 
//     initial={{ scale: 0 }}
//     animate={{ scale: 1 }}
//     className="text-center mt-auto"
//   >
//     <div className={`text-xs px-3 py-1.5 rounded-full ${tag.color} shadow-sm inline-flex items-center`}>
//       {tag.icon}
//       <span className="ml-1">{tag.text}</span>
//     </div>
//   </motion.div>
// )}
//                         </Link>
//                       </motion.div>
//                     );
//                   })}
//                 </div>
//               </motion.div>
//             );
//           })}
//         </AnimatePresence>
//       </div>
//     </motion.div>
//   );

return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="bg-gradient-to-b from-gray-50 to-gray-100 py-8 md:py-12"
    >
      <div className="container mx-auto px-3 md:px-4 space-y-8 md:space-y-12">
        {/* Featured Deals Banner - Responsive adjustments */}
        {featuredDeal && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="relative bg-gradient-to-r from-indigo-600 to-purple-700 rounded-xl md:rounded-2xl p-4 md:p-8 text-white overflow-hidden shadow-lg md:shadow-xl"
          >
            {/* ... (background elements remain the same) */}
            
            <div className="relative z-10 flex flex-col md:flex-row items-center">
              <div className="md:w-1/2 mb-6 md:mb-0">
                <motion.div 
                  initial={{ x: -20 }}
                  animate={{ x: 0 }}
                  transition={{ delay: 0.2 }}
                  className="inline-block px-3 py-1 md:px-4 md:py-2 bg-white bg-opacity-20 rounded-full text-xs md:text-sm mb-4 md:mb-6 backdrop-blur-sm"
                >
                  {featuredDeal.categoryName}
                </motion.div>
                <motion.h3 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="text-xl md:text-3xl lg:text-4xl font-bold mb-3 md:mb-4 leading-tight"
                >
                  {featuredDeal.name}
                </motion.h3>
                <motion.p 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4 }}
                  className="mb-4 md:mb-6 text-white text-opacity-90 max-w-md text-sm md:text-base"
                >
                  Don't miss out on this exclusive offer! Limited time only.
                </motion.p>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 }}
                >
                  <Link 
                    to={`/subcategory/${featuredDeal.id}`}
                    className="inline-flex items-center bg-white text-indigo-600 px-5 py-2 md:px-8 md:py-3 rounded-lg md:rounded-xl font-bold hover:bg-gray-100 transition-all duration-300 shadow-md hover:shadow-lg transform hover:-translate-y-1 text-sm md:text-base"
                  >
                    Shop Now <FaChevronRight className="ml-1 md:ml-2" />
                  </Link>
                </motion.div>
              </div>
              <div className="md:w-1/2 flex justify-center mt-4 md:mt-0">
                <motion.div
                  initial={{ scale: 0.9, rotate: -5 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ type: "spring", stiffness: 100, damping: 10 }}
                  whileHover={{ scale: 1.05, rotate: 5 }}
                  className="relative"
                >
                  <div className="absolute -inset-2 md:-inset-4 bg-white bg-opacity-20 rounded-xl md:rounded-2xl blur-xl"></div>
                  <img
                    src={getValidImageUrl(featuredDeal.media)}
                    alt={featuredDeal.name}
                    className="relative z-10 w-40 h-40 md:w-72 md:h-72 object-contain drop-shadow-xl md:drop-shadow-2xl"
                  />
                </motion.div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Category Sections */}
        <AnimatePresence>
          {categories.map((category, index) => {
            const categoryDiscount = getCategoryDiscount(category.id);
            const discountedProductsInCategory = getDiscountedProductsForCategoryTree(category);
            
            return (
              <motion.div 
                key={category.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, type: "spring" }}
                className="bg-white p-4 md:p-6 lg:p-8 rounded-xl md:rounded-2xl shadow-sm md:shadow-md border border-gray-100 overflow-hidden"
              >
                {/* Section Header */}
                <div className="flex justify-between items-center mb-6 md:mb-8">
                  <div className="flex items-center">
                    <motion.h2 
                      whileHover={{ x: 5 }}
                      className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 flex items-center"
                    >
                      <span className="w-2 h-6 md:w-3 md:h-8 bg-gradient-to-b from-indigo-500 to-purple-600 rounded-full mr-3 md:mr-4"></span>
                      {category.name}
                    </motion.h2>
                    
                    {/* Display category discount if available */}
                    {categoryDiscount > 0 && (
                      <motion.div 
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="ml-3 bg-gradient-to-r from-red-500 to-orange-500 text-white px-2 py-1 rounded-full text-xs md:text-sm font-bold flex items-center"
                      >
                        <FaPercent className="mr-1" /> Up to {categoryDiscount}%
                      </motion.div>
                    )}
                  </div>
                  
                  <Link 
                    to={`/category/${category.id}`} 
                    className="flex items-center text-indigo-600 font-medium hover:text-indigo-800 transition-colors group text-sm md:text-base"
                  >
                    <motion.span 
                      animate={{ x: [0, 3, 0] }}
                      transition={{ repeat: Infinity, duration: 1.5 }}
                      className="mr-1 md:mr-2 group-hover:mr-2 md:group-hover:mr-3 transition-all"
                    >
                      View All
                    </motion.span>
                    <FaChevronRight className="text-xs md:text-sm group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
                
                {/* Subcategories Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3 md:gap-4 lg:gap-6">
                  {category.subcategories.map((subcategory) => {
                    const subcategoryProducts = discountedProducts.filter(
                      product => product.category === subcategory.id
                    );
                    
                    const subcategoryDiscount = subcategoryProducts.length > 0 
                      ? Math.max(...subcategoryProducts.map(p => 
                          Math.max(...p.variants.map(v => v.discount || 0))
                        ))
                      : 0;
                    
                    const productCount = subcategoryProducts.length;
                    const tag = generateTag(subcategoryDiscount, productCount);
                    
                    return (
                      <motion.div
                        key={subcategory.id}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ type: "spring" }}
                        whileHover={{ y: -4, scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="relative"
                      >
                        <div className="absolute inset-0 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg md:rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                        <Link
                          to={`/subcategory/${subcategory.id}`}
                          className="group block p-3 md:p-4 rounded-lg md:rounded-xl transition-all duration-300 flex flex-col h-full bg-white relative z-10 border border-gray-200 hover:border-transparent hover:shadow-md"
                        >
                          {/* Product Image - Reduced height on mobile */}
                          <div className="w-full h-24 md:h-32 lg:h-40 mb-2 md:mb-3 overflow-hidden flex items-center justify-center">
                            <motion.img
                              src={getValidImageUrl(subcategory.media)}
                              alt={subcategory.name}
                              className="max-h-full max-w-full object-contain transition-transform duration-500 group-hover:scale-110"
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              transition={{ duration: 0.5 }}
                            />
                          </div>
                          
                          {/* Product Title - Adjusted font size */}
                          <h3 className="text-sm md:text-base font-semibold text-gray-800 mb-1 md:mb-2 line-clamp-2 text-center group-hover:text-indigo-600 transition-colors leading-tight">
                            {subcategory.name}
                          </h3>
                          
                          {/* Discount Badge - Adjusted spacing and font size */}
                          {subcategoryDiscount > 0 && (
                            <motion.div 
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              className="text-center mt-auto mb-1 md:mb-2"
                            >
                              <span className="inline-block bg-gradient-to-r from-red-500 to-orange-500 text-white text-xs md:text-sm font-bold px-2 py-1 rounded-full">
                                Save Upto {subcategoryDiscount}%
                              </span>
                            </motion.div>
                          )}

                          {/* Promotional Tag */}
                          {tag && subcategoryDiscount === 0 && (
                            <motion.div 
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              className="text-center mt-auto"
                            >
                              <div className={`text-xs px-2 py-1 rounded-full ${tag.color} shadow-sm inline-flex items-center`}>
                                {tag.icon}
                                <span className="ml-1">{tag.text}</span>
                              </div>
                            </motion.div>
                          )}
                        </Link>
                      </motion.div>
                    );
                  })}
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

export default DealsPage;

// import React, { useState, useEffect } from "react";
// import { Link } from "react-router-dom";
// import axios from "axios";
// import { FaChevronRight, FaFire, FaPercent, FaBolt } from "react-icons/fa";
// import { motion, AnimatePresence } from "framer-motion";

// const DealsPage = () => {
//   const [categories, setCategories] = useState([]);
//   const [discountedProducts, setDiscountedProducts] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [featuredDeal, setFeaturedDeal] = useState(null);
//   const [categoryDiscounts, setCategoryDiscounts] = useState({});
//   const [categoryMap, setCategoryMap] = useState({});

//   useEffect(() => {
//     const fetchData = async () => {
//       try {
//         setLoading(true);
        
//         // Fetch categories and discounted products simultaneously
//         const [categoriesResponse, discountedResponse] = await Promise.all([
//           axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/seller/category/list`),
//           axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/seller/product/get-by-discount`)
//         ]);
        
//         if (categoriesResponse.data.success) {
//           const processedCategories = categoriesResponse.data.categories
//             .filter(cat => cat.published)
//             .map(category => {
//               const topLevelSubs = (category.subcategories || [])
//                 .filter(sub => sub.published)
//                 .map(sub => ({
//                   ...sub,
//                   price: Math.floor(Math.random() * 10000) + 1000,
//                   discount: 0 // Initialize with 0, will be updated later
//                 }));

//               let allSubcategories = [...topLevelSubs];
//               if (topLevelSubs.length < 6) {
//                 const getNestedSubs = (subs) => {
//                   let result = [];
//                   subs.forEach(sub => {
//                     if (sub.subcategories && sub.subcategories.length > 0) {
//                       const nested = sub.subcategories
//                         .filter(nestedSub => nestedSub.published)
//                         .map(nestedSub => ({
//                           ...nestedSub,
//                           price: Math.floor(Math.random() * 10000) + 1000,
//                           discount: 0
//                         }));
//                       result = result.concat(nested);
//                     }
//                   });
//                   return result;
//                 };

//                 const nestedSubs = getNestedSubs(category.subcategories || []);
//                 const needed = 6 - topLevelSubs.length;
//                 allSubcategories = allSubcategories.concat(nestedSubs.slice(0, needed));
//               }

//               return {
//                 ...category,
//                 subcategories: allSubcategories.slice(0, 6) // Limit to 6 subcategories
//               };
//             })
//             .filter(category => category.subcategories.length > 0);
          
//           setCategories(processedCategories);
          
//           // Create a mapping of category IDs to category names for easier lookup
//           const catMap = {};
//           processedCategories.forEach(category => {
//             catMap[category.id] = category.name;
//             (category.subcategories || []).forEach(subcategory => {
//               catMap[subcategory.id] = subcategory.name;
//             });
//           });
//           setCategoryMap(catMap);
          
//           if (processedCategories.length > 0) {
//             const validCategories = processedCategories.filter(c => c.subcategories.length > 0);
//             if (validCategories.length > 0) {
//               const randomCategory = validCategories[
//                 Math.floor(Math.random() * validCategories.length)
//               ];
//               const randomSubcategory = randomCategory.subcategories[
//                 Math.floor(Math.random() * randomCategory.subcategories.length)
//               ];
//               setFeaturedDeal({
//                 ...randomSubcategory,
//                 categoryName: randomCategory.name
//               });
//             }
//           }
//         }
        
//         // Process discounted products
//         if (discountedResponse.data.success && discountedResponse.data.products) {
//           setDiscountedProducts(discountedResponse.data.products);
          
//           // Calculate max discount for each category
//           const discounts = {};
//           discountedResponse.data.products.forEach(product => {
//             if (product.category && product.variants && product.variants.length > 0) {
//               const categoryId = product.category;
//               const discount = Math.max(...product.variants.map(v => v.discount || 0));
              
//               if (!discounts[categoryId] || discount > discounts[categoryId]) {
//                 discounts[categoryId] = discount;
//               }
//             }
//           });
          
//           setCategoryDiscounts(discounts);
//         }
        
//         setLoading(false);
//       } catch (error) {
//         console.error("Error fetching data:", error);
//         setError("Failed to load data. Please try again later.");
//         setLoading(false);
//       }
//     };

//     fetchData();
//   }, []);

//   // Function to get the maximum discount for a category
//   const getCategoryDiscount = (categoryId) => {
//     return categoryDiscounts[categoryId] || 0;
//   };

//   // Function to get discounted products for a specific category and its subcategories
//   const getDiscountedProductsForCategoryTree = (category) => {
//     const allCategoryIds = [category.id];
    
//     // Recursively get all subcategory IDs
//     const getSubcategoryIds = (subcategories) => {
//       subcategories.forEach(sub => {
//         allCategoryIds.push(sub.id);
//         if (sub.subcategories && sub.subcategories.length > 0) {
//           getSubcategoryIds(sub.subcategories);
//         }
//       });
//     };
    
//     if (category.subcategories && category.subcategories.length > 0) {
//       getSubcategoryIds(category.subcategories);
//     }
    
//     return discountedProducts.filter(product => 
//       allCategoryIds.includes(product.category)
//     );
//   };

//   const generateTag = (discount, productCount) => {
//     if (discount > 0) {
//       return {
//         text: `Upto ${discount}% off`, 
//         icon: <FaPercent className="mr-1" />, 
//         color: "bg-gradient-to-r from-red-500 to-orange-500 text-white"
//       };
//     }
    
//     // Show limited static tags only when there are no discounts
//     if (productCount > 0) {
//       const staticTags = [
//         { text: "Hot Deals", icon: <FaFire className="mr-1" />, color: "bg-gradient-to-r from-red-500 to-orange-500 text-white" },
//         { text: "Limited Time", icon: <FaBolt className="mr-1" />, color: "bg-gradient-to-r from-purple-500 to-pink-500 text-white" }
//       ];
//       return staticTags[Math.floor(Math.random() * staticTags.length)];
//     }
    
//     return null;
//   };

//   const getValidImageUrl = (url) => {
//     if (!url) return "https://via.placeholder.com/150?text=No+Image";
//     if (url.includes('google.com') || url.includes('googleusercontent') || 
//         url.includes('www.google.com')) {
//       return "https://via.placeholder.com/150?text=No+Image";
//     }
//     return url;
//   };

//   if (loading) {
//     return (
//       <motion.div 
//         initial={{ opacity: 0 }}
//         animate={{ opacity: 1 }}
//         className="bg-gradient-to-b from-gray-50 to-gray-100 py-12"
//       >
//         <div className="container mx-auto px-4 space-y-12">
//           {[...Array(3)].map((_, i) => (
//             <div key={i}>
//               <div className="h-8 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full w-1/4 mb-8 animate-pulse"></div>
//               <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-6">
//                 {[...Array(6)].map((_, j) => (
//                   <motion.div 
//                     key={j} 
//                     initial={{ opacity: 0.5 }}
//                     animate={{ opacity: 0.8 }}
//                     transition={{ repeat: Infinity, repeatType: "reverse", duration: 1, delay: j * 0.1 }}
//                     className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl h-64 shadow-sm"
//                   ></motion.div>
//                 ))}
//               </div>
//             </div>
//           ))}
//         </div>
//       </motion.div>
//     );
//   }

//   if (error) {
//     return (
//       <motion.div 
//         initial={{ opacity: 0 }}
//         animate={{ opacity: 1 }}
//         className="bg-gradient-to-b from-gray-50 to-gray-100 py-12 container mx-auto px-4 text-center"
//       >
//         <div className="max-w-md mx-auto bg-white p-8 rounded-xl shadow-lg">
//           <div className="text-red-500 text-5xl mb-4">⚠️</div>
//           <h3 className="text-xl font-bold text-gray-800 mb-2">Oops! Something went wrong</h3>
//           <p className="text-gray-600 mb-6">{error}</p>
//           <motion.button
//             whileHover={{ scale: 1.05 }}
//             whileTap={{ scale: 0.95 }}
//             className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-6 py-3 rounded-lg font-medium shadow-md"
//             onClick={() => window.location.reload()}
//           >
//             Try Again
//           </motion.button>
//         </div>
//       </motion.div>
//     );
//   }

//   if (categories.length === 0) {
//     return (
//       <motion.div 
//         initial={{ opacity: 0 }}
//         animate={{ opacity: 1 }}
//         className="bg-gradient-to-b from-gray-50 to-gray-100 py-12 container mx-auto px-4 text-center"
//       >
//         <div className="max-w-md mx-auto bg-white p-8 rounded-xl shadow-lg">
//           <div className="text-gray-400 text-5xl mb-4">🛍️</div>
//           <h3 className="text-xl font-bold text-gray-800 mb-2">No Deals Available</h3>
//           <p className="text-gray-600 mb-6">Check back later for exciting offers!</p>
//           <Link to="/">
//             <motion.button
//               whileHover={{ scale: 1.05 }}
//               whileTap={{ scale: 0.95 }}
//               className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-6 py-3 rounded-lg font-medium shadow-md"
//             >
//               Go to Home
//             </motion.button>
//           </Link>
//         </div>
//       </motion.div>
//     );
//   }

//   return (
//     <motion.div 
//       initial={{ opacity: 0 }}
//       animate={{ opacity: 1 }}
//       transition={{ duration: 0.5 }}
//       className="bg-gradient-to-b from-gray-50 to-gray-100 py-12"
//     >
//       <div className="container mx-auto px-4 space-y-12">
//         {/* Featured Deals Banner */}
//         {featuredDeal && (
//           <motion.div
//             initial={{ opacity: 0, y: 20 }}
//             animate={{ opacity: 1, y: 0 }}
//             transition={{ duration: 0.6 }}
//             className="relative bg-gradient-to-r from-indigo-600 to-purple-700 rounded-2xl p-8 text-white overflow-hidden shadow-xl"
//           >
//             <div className="absolute inset-0 bg-noise opacity-10"></div>
//             <div className="absolute -right-20 -top-20 w-64 h-64 bg-white bg-opacity-10 rounded-full"></div>
//             <div className="absolute -left-20 -bottom-20 w-80 h-80 bg-white bg-opacity-5 rounded-full"></div>
            
//             <div className="relative z-10 flex flex-col md:flex-row items-center">
//               <div className="md:w-1/2 mb-8 md:mb-0">
//                 <motion.div 
//                   initial={{ x: -20 }}
//                   animate={{ x: 0 }}
//                   transition={{ delay: 0.2 }}
//                   className="inline-block px-4 py-2 bg-white bg-opacity-20 rounded-full text-sm mb-6 backdrop-blur-sm"
//                 >
//                   {featuredDeal.categoryName}
//                 </motion.div>
//                 <motion.h3 
//                   initial={{ opacity: 0, y: 10 }}
//                   animate={{ opacity: 1, y: 0 }}
//                   transition={{ delay: 0.3 }}
//                   className="text-3xl md:text-4xl font-bold mb-4 leading-tight"
//                 >
//                   {featuredDeal.name}
//                 </motion.h3>
//                 <motion.p 
//                   initial={{ opacity: 0 }}
//                   animate={{ opacity: 1 }}
//                   transition={{ delay: 0.4 }}
//                   className="mb-6 text-white text-opacity-90 max-w-md"
//                 >
//                   Don't miss out on this exclusive offer! Limited time only.
//                 </motion.p>
//                 <motion.div
//                   initial={{ opacity: 0 }}
//                   animate={{ opacity: 1 }}
//                   transition={{ delay: 0.5 }}
//                 >
//                   <Link 
//                     to={`/subcategory/${featuredDeal.id}`}
//                     className="inline-flex items-center bg-white text-indigo-600 px-8 py-3 rounded-xl font-bold hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
//                   >
//                     Shop Now <FaChevronRight className="ml-2" />
//                   </Link>
//                 </motion.div>
//               </div>
//               <div className="md:w-1/2 flex justify-center">
//                 <motion.div
//                   initial={{ scale: 0.9, rotate: -5 }}
//                   animate={{ scale: 1, rotate: 0 }}
//                   transition={{ type: "spring", stiffness: 100, damping: 10 }}
//                   whileHover={{ scale: 1.05, rotate: 5 }}
//                   className="relative"
//                 >
//                   <div className="absolute -inset-4 bg-white bg-opacity-20 rounded-2xl blur-xl"></div>
//                   <img
//                     src={getValidImageUrl(featuredDeal.media)}
//                     alt={featuredDeal.name}
//                     className="relative z-10 w-72 h-72 object-contain drop-shadow-2xl"
//                   />
//                 </motion.div>
//               </div>
//             </div>
//           </motion.div>
//         )}

//         {/* Category Sections */}
//         <AnimatePresence>
//           {categories.map((category, index) => {
//             const categoryDiscount = getCategoryDiscount(category.id);
//             const discountedProductsInCategory = getDiscountedProductsForCategoryTree(category);
            
//             return (
//               <motion.div 
//                 key={category.id}
//                 initial={{ opacity: 0, y: 30 }}
//                 animate={{ opacity: 1, y: 0 }}
//                 transition={{ delay: index * 0.1, type: "spring" }}
//                 className="bg-white p-8 rounded-2xl shadow-md border border-gray-100 overflow-hidden"
//               >
//                 {/* Section Header */}
//                 <div className="flex justify-between items-center mb-8">
//                   <div className="flex items-center">
//                     <motion.h2 
//                       whileHover={{ x: 5 }}
//                       className="text-3xl font-bold text-gray-900 flex items-center"
//                     >
//                       <span className="w-3 h-8 bg-gradient-to-b from-indigo-500 to-purple-600 rounded-full mr-4"></span>
//                       {category.name}
//                     </motion.h2>
                    
//                     {/* Display category discount if available */}
//                     {categoryDiscount > 0 && (
//                       <motion.div 
//                         initial={{ scale: 0 }}
//                         animate={{ scale: 1 }}
//                         className="ml-4 bg-gradient-to-r from-red-500 to-orange-500 text-white px-3 py-1 rounded-full text-sm font-bold flex items-center"
//                       >
//                         <FaPercent className="mr-1" /> Save up to {categoryDiscount}%
//                       </motion.div>
//                     )}
//                   </div>
                  
//                   <Link 
//                     to={`/category/${category.id}`} 
//                     className="flex items-center text-indigo-600 font-medium hover:text-indigo-800 transition-colors group"
//                   >
//                     <motion.span 
//                       animate={{ x: [0, 3, 0] }}
//                       transition={{ repeat: Infinity, duration: 1.5 }}
//                       className="mr-2 group-hover:mr-3 transition-all"
//                     >
//                       View All
//                     </motion.span>
//                     <FaChevronRight className="group-hover:translate-x-1 transition-transform" />
//                   </Link>
//                 </div>
                
//                 {/* Discounted Products Preview (if available) */}
//                 {discountedProductsInCategory.length > 0 && (
//                   <motion.div 
//                     initial={{ opacity: 0, height: 0 }}
//                     animate={{ opacity: 1, height: "auto" }}
//                     className="mb-6 bg-gradient-to-r from-orange-50 to-red-50 p-4 rounded-lg border border-orange-200"
//                   >
//                     <h3 className="text-lg font-semibold text-orange-800 mb-2 flex items-center">
//                       <FaFire className="mr-2 text-orange-500" /> Hot Deals in {category.name}
//                     </h3>
//                     <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
//                       {discountedProductsInCategory.slice(0, 4).map(product => (
//                         <motion.div 
//                           key={product._id}
//                           whileHover={{ y: -5 }}
//                           className="bg-white p-3 rounded-lg border border-orange-100 shadow-sm flex items-center"
//                         >
//                           <img 
//                             src={getValidImageUrl(product.variants?.[0]?.images?.[0])} 
//                             alt={product.name}
//                             className="w-12 h-12 object-contain mr-3"
//                           />
//                           <div>
//                             <p className="text-sm font-medium text-gray-800 line-clamp-1">{product.name}</p>
//                             <p className="text-xs text-orange-600 font-bold">
//                               {Math.max(...product.variants.map(v => v.discount || 0))}% OFF
//                             </p>
//                           </div>
//                         </motion.div>
//                       ))}
//                     </div>
//                   </motion.div>
//                 )}
                
//                 {/* Subcategories Grid - Now showing exactly 6 items */}
//                 <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-6">
//                   {category.subcategories.map((subcategory) => {
//                     // Find products in this subcategory to determine discount
//                     const subcategoryProducts = discountedProducts.filter(
//                       product => product.category === subcategory.id
//                     );
                    
//                     const subcategoryDiscount = subcategoryProducts.length > 0 
//                       ? Math.max(...subcategoryProducts.map(p => 
//                           Math.max(...p.variants.map(v => v.discount || 0))
//                         ))
//                       : 0;
                    
//                     // Count products in this subcategory
//                     const productCount = subcategoryProducts.length;
                    
//                     // Generate tag based on actual data
//                     const tag = generateTag(subcategoryDiscount, productCount);
                    
//                     return (
//                       <motion.div
//                         key={subcategory.id}
//                         initial={{ opacity: 0, scale: 0.9 }}
//                         animate={{ opacity: 1, scale: 1 }}
//                         transition={{ type: "spring" }}
//                         whileHover={{ y: -8, scale: 1.02 }}
//                         whileTap={{ scale: 0.98 }}
//                         className="relative"
//                       >
//                         <div className="absolute inset-0 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
//                         <Link
//                           to={`/subcategory/${subcategory.id}`}
//                           className="group block p-5 rounded-xl transition-all duration-300 flex flex-col h-full bg-white relative z-10 border border-gray-200 hover:border-transparent hover:shadow-lg"
//                         >
//                           {/* Product Image */}
//                           <div className="w-full h-40 mb-4 overflow-hidden flex items-center justify-center">
//                             <motion.img
//                               src={getValidImageUrl(subcategory.media)}
//                               alt={subcategory.name}
//                               className="max-h-full max-w-full object-contain transition-transform duration-500 group-hover:scale-110"
//                               initial={{ opacity: 0 }}
//                               animate={{ opacity: 1 }}
//                               transition={{ duration: 0.5 }}
//                             />
//                           </div>
                          
//                           {/* Product Title */}
//                           <h3 className="text-lg font-semibold text-gray-800 mb-2 line-clamp-2 text-center group-hover:text-indigo-600 transition-colors">
//                             {subcategory.name}
//                           </h3>
                          
//                           {/* Discount Badge */}
//                           {subcategoryDiscount > 0 && (
//                             <motion.div 
//                               initial={{ scale: 0 }}
//                               animate={{ scale: 1 }}
//                               className="text-center mt-auto mb-2"
//                             >
//                               <span className="inline-block bg-gradient-to-r from-red-500 to-orange-500 text-white text-sm font-bold px-3 py-1 rounded-full">
//                                 Up to {subcategoryDiscount}% OFF
//                               </span>
//                             </motion.div>
//                           )}
                          
//                           {/* Tag - only shown if there's a discount or products */}
//                           {tag && (
//                             <motion.div 
//                               whileHover={{ scale: 1.05 }}
//                               className={`text-xs text-center mt-3 px-3 py-1.5 rounded-full ${tag.color} shadow-sm`}
//                             >
//                               <div className="flex items-center justify-center">
//                                 {tag.icon}
//                                 {tag.text}
//                               </div>
//                             </motion.div>
//                           )}
//                         </Link>
//                       </motion.div>
//                     );
//                   })}
//                 </div>
//               </motion.div>
//             );
//           })}
//         </AnimatePresence>
//       </div>
//     </motion.div>
//   );
// };

// export default DealsPage;

// import React, { useState, useEffect } from "react";
// import { Link } from "react-router-dom";
// import axios from "axios";
// import { FaChevronRight, FaFire, FaPercent } from "react-icons/fa";
// import { motion, AnimatePresence } from "framer-motion";

// const DealsPage = () => {
//   const [categories, setCategories] = useState([]);
//   const [discountedProducts, setDiscountedProducts] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [featuredDeal, setFeaturedDeal] = useState(null);
//   const [categoryDiscounts, setCategoryDiscounts] = useState({});
//   const [categoryMap, setCategoryMap] = useState({});

//   useEffect(() => {
//     const fetchData = async () => {
//       try {
//         setLoading(true);
        
//         // Fetch categories and discounted products simultaneously
//         const [categoriesResponse, discountedResponse] = await Promise.all([
//           axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/seller/category/list`),
//           axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/seller/product/get-by-discount`)
//         ]);
        
//         if (categoriesResponse.data.success) {
//           const processedCategories = categoriesResponse.data.categories
//             .filter(cat => cat.published)
//             .map(category => {
//               const topLevelSubs = (category.subcategories || [])
//                 .filter(sub => sub.published)
//                 .map(sub => ({
//                   ...sub,
//                   price: Math.floor(Math.random() * 10000) + 1000,
//                   discount: 0 // Initialize with 0, will be updated later
//                 }));

//               let allSubcategories = [...topLevelSubs];
//               if (topLevelSubs.length < 6) {
//                 const getNestedSubs = (subs) => {
//                   let result = [];
//                   subs.forEach(sub => {
//                     if (sub.subcategories && sub.subcategories.length > 0) {
//                       const nested = sub.subcategories
//                         .filter(nestedSub => nestedSub.published)
//                         .map(nestedSub => ({
//                           ...nestedSub,
//                           price: Math.floor(Math.random() * 10000) + 1000,
//                           discount: 0
//                         }));
//                       result = result.concat(nested);
//                     }
//                   });
//                   return result;
//                 };

//                 const nestedSubs = getNestedSubs(category.subcategories || []);
//                 const needed = 6 - topLevelSubs.length;
//                 allSubcategories = allSubcategories.concat(nestedSubs.slice(0, needed));
//               }

//               return {
//                 ...category,
//                 subcategories: allSubcategories.slice(0, 6) // Limit to 6 subcategories
//               };
//             })
//             .filter(category => category.subcategories.length > 0);
          
//           setCategories(processedCategories);
          
//           // Create a mapping of category IDs to category names for easier lookup
//           const catMap = {};
//           processedCategories.forEach(category => {
//             catMap[category.id] = category.name;
//             (category.subcategories || []).forEach(subcategory => {
//               catMap[subcategory.id] = subcategory.name;
//             });
//           });
//           setCategoryMap(catMap);
          
//           if (processedCategories.length > 0) {
//             const validCategories = processedCategories.filter(c => c.subcategories.length > 0);
//             if (validCategories.length > 0) {
//               const randomCategory = validCategories[
//                 Math.floor(Math.random() * validCategories.length)
//               ];
//               const randomSubcategory = randomCategory.subcategories[
//                 Math.floor(Math.random() * randomCategory.subcategories.length)
//               ];
//               setFeaturedDeal({
//                 ...randomSubcategory,
//                 categoryName: randomCategory.name
//               });
//             }
//           }
//         }
        
//         // Process discounted products
//         if (discountedResponse.data.success && discountedResponse.data.products) {
//           setDiscountedProducts(discountedResponse.data.products);
          
//           // Calculate max discount for each category
//           const discounts = {};
//           discountedResponse.data.products.forEach(product => {
//             if (product.category && product.variants && product.variants.length > 0) {
//               const categoryId = product.category;
//               const discount = Math.max(...product.variants.map(v => v.discount || 0));
              
//               if (!discounts[categoryId] || discount > discounts[categoryId]) {
//                 discounts[categoryId] = discount;
//               }
//             }
//           });
          
//           setCategoryDiscounts(discounts);
//         }
        
//         setLoading(false);
//       } catch (error) {
//         console.error("Error fetching data:", error);
//         setError("Failed to load data. Please try again later.");
//         setLoading(false);
//       }
//     };

//     fetchData();
//   }, []);

//   // Function to get the maximum discount for a category
//   const getCategoryDiscount = (categoryId) => {
//     return categoryDiscounts[categoryId] || 0;
//   };

//   // Function to get discounted products for a specific category and its subcategories
//   const getDiscountedProductsForCategoryTree = (category) => {
//     const allCategoryIds = [category.id];
    
//     // Recursively get all subcategory IDs
//     const getSubcategoryIds = (subcategories) => {
//       subcategories.forEach(sub => {
//         allCategoryIds.push(sub.id);
//         if (sub.subcategories && sub.subcategories.length > 0) {
//           getSubcategoryIds(sub.subcategories);
//         }
//       });
//     };
    
//     if (category.subcategories && category.subcategories.length > 0) {
//       getSubcategoryIds(category.subcategories);
//     }
    
//     return discountedProducts.filter(product => 
//       allCategoryIds.includes(product.category)
//     );
//   };

//   const generateTag = (discount, productCount) => {
//     if (discount > 0) {
//       return {
//         text: `Upto ${discount}% off`, 
//         icon: <FaPercent className="mr-1" />, 
//         color: "bg-gradient-to-r from-red-500 to-orange-500 text-white"
//       };
//     }
    
//     if (productCount > 0) {
//       return {
//         text: `${productCount}+ products`,
//         icon: <FaFire className="mr-1" />,
//         color: "bg-gradient-to-r from-blue-500 to-indigo-500 text-white"
//       };
//     }
    
//     return null;
//   };

//   const getValidImageUrl = (url) => {
//     if (!url) return "https://via.placeholder.com/150?text=No+Image";
//     if (url.includes('google.com') || url.includes('googleusercontent') || 
//         url.includes('www.google.com')) {
//       return "https://via.placeholder.com/150?text=No+Image";
//     }
//     return url;
//   };

//   if (loading) {
//     return (
//       <motion.div 
//         initial={{ opacity: 0 }}
//         animate={{ opacity: 1 }}
//         className="bg-gradient-to-b from-gray-50 to-gray-100 py-12"
//       >
//         <div className="container mx-auto px-4 space-y-12">
//           {[...Array(3)].map((_, i) => (
//             <div key={i}>
//               <div className="h-8 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full w-1/4 mb-8 animate-pulse"></div>
//               <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-6">
//                 {[...Array(6)].map((_, j) => (
//                   <motion.div 
//                     key={j} 
//                     initial={{ opacity: 0.5 }}
//                     animate={{ opacity: 0.8 }}
//                     transition={{ repeat: Infinity, repeatType: "reverse", duration: 1, delay: j * 0.1 }}
//                     className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl h-64 shadow-sm"
//                   ></motion.div>
//                 ))}
//               </div>
//             </div>
//           ))}
//         </div>
//       </motion.div>
//     );
//   }

//   if (error) {
//     return (
//       <motion.div 
//         initial={{ opacity: 0 }}
//         animate={{ opacity: 1 }}
//         className="bg-gradient-to-b from-gray-50 to-gray-100 py-12 container mx-auto px-4 text-center"
//       >
//         <div className="max-w-md mx-auto bg-white p-8 rounded-xl shadow-lg">
//           <div className="text-red-500 text-5xl mb-4">⚠️</div>
//           <h3 className="text-xl font-bold text-gray-800 mb-2">Oops! Something went wrong</h3>
//           <p className="text-gray-600 mb-6">{error}</p>
//           <motion.button
//             whileHover={{ scale: 1.05 }}
//             whileTap={{ scale: 0.95 }}
//             className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-6 py-3 rounded-lg font-medium shadow-md"
//             onClick={() => window.location.reload()}
//           >
//             Try Again
//           </motion.button>
//         </div>
//       </motion.div>
//     );
//   }

//   if (categories.length === 0) {
//     return (
//       <motion.div 
//         initial={{ opacity: 0 }}
//         animate={{ opacity: 1 }}
//         className="bg-gradient-to-b from-gray-50 to-gray-100 py-12 container mx-auto px-4 text-center"
//       >
//         <div className="max-w-md mx-auto bg-white p-8 rounded-xl shadow-lg">
//           <div className="text-gray-400 text-5xl mb-4">🛍️</div>
//           <h3 className="text-xl font-bold text-gray-800 mb-2">No Deals Available</h3>
//           <p className="text-gray-600 mb-6">Check back later for exciting offers!</p>
//           <Link to="/">
//             <motion.button
//               whileHover={{ scale: 1.05 }}
//               whileTap={{ scale: 0.95 }}
//               className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-6 py-3 rounded-lg font-medium shadow-md"
//             >
//               Go to Home
//             </motion.button>
//           </Link>
//         </div>
//       </motion.div>
//     );
//   }

//   return (
//     <motion.div 
//       initial={{ opacity: 0 }}
//       animate={{ opacity: 1 }}
//       transition={{ duration: 0.5 }}
//       className="bg-gradient-to-b from-gray-50 to-gray-100 py-12"
//     >
//       <div className="container mx-auto px-4 space-y-12">
//         {/* Featured Deals Banner */}
//         {featuredDeal && (
//           <motion.div
//             initial={{ opacity: 0, y: 20 }}
//             animate={{ opacity: 1, y: 0 }}
//             transition={{ duration: 0.6 }}
//             className="relative bg-gradient-to-r from-indigo-600 to-purple-700 rounded-2xl p-8 text-white overflow-hidden shadow-xl"
//           >
//             <div className="absolute inset-0 bg-noise opacity-10"></div>
//             <div className="absolute -right-20 -top-20 w-64 h-64 bg-white bg-opacity-10 rounded-full"></div>
//             <div className="absolute -left-20 -bottom-20 w-80 h-80 bg-white bg-opacity-5 rounded-full"></div>
            
//             <div className="relative z-10 flex flex-col md:flex-row items-center">
//               <div className="md:w-1/2 mb-8 md:mb-0">
//                 <motion.div 
//                   initial={{ x: -20 }}
//                   animate={{ x: 0 }}
//                   transition={{ delay: 0.2 }}
//                   className="inline-block px-4 py-2 bg-white bg-opacity-20 rounded-full text-sm mb-6 backdrop-blur-sm"
//                 >
//                   {featuredDeal.categoryName}
//                 </motion.div>
//                 <motion.h3 
//                   initial={{ opacity: 0, y: 10 }}
//                   animate={{ opacity: 1, y: 0 }}
//                   transition={{ delay: 0.3 }}
//                   className="text-3xl md:text-4xl font-bold mb-4 leading-tight"
//                 >
//                   {featuredDeal.name}
//                 </motion.h3>
//                 <motion.p 
//                   initial={{ opacity: 0 }}
//                   animate={{ opacity: 1 }}
//                   transition={{ delay: 0.4 }}
//                   className="mb-6 text-white text-opacity-90 max-w-md"
//                 >
//                   Don't miss out on this exclusive offer! Limited time only.
//                 </motion.p>
//                 <motion.div
//                   initial={{ opacity: 0 }}
//                   animate={{ opacity: 1 }}
//                   transition={{ delay: 0.5 }}
//                 >
//                   <Link 
//                     to={`/subcategory/${featuredDeal.id}`}
//                     className="inline-flex items-center bg-white text-indigo-600 px-8 py-3 rounded-xl font-bold hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
//                   >
//                     Shop Now <FaChevronRight className="ml-2" />
//                   </Link>
//                 </motion.div>
//               </div>
//               <div className="md:w-1/2 flex justify-center">
//                 <motion.div
//                   initial={{ scale: 0.9, rotate: -5 }}
//                   animate={{ scale: 1, rotate: 0 }}
//                   transition={{ type: "spring", stiffness: 100, damping: 10 }}
//                   whileHover={{ scale: 1.05, rotate: 5 }}
//                   className="relative"
//                 >
//                   <div className="absolute -inset-4 bg-white bg-opacity-20 rounded-2xl blur-xl"></div>
//                   <img
//                     src={getValidImageUrl(featuredDeal.media)}
//                     alt={featuredDeal.name}
//                     className="relative z-10 w-72 h-72 object-contain drop-shadow-2xl"
//                   />
//                 </motion.div>
//               </div>
//             </div>
//           </motion.div>
//         )}

//         {/* Category Sections */}
//         <AnimatePresence>
//           {categories.map((category, index) => {
//             const categoryDiscount = getCategoryDiscount(category.id);
//             const discountedProductsInCategory = getDiscountedProductsForCategoryTree(category);
            
//             return (
//               <motion.div 
//                 key={category.id}
//                 initial={{ opacity: 0, y: 30 }}
//                 animate={{ opacity: 1, y: 0 }}
//                 transition={{ delay: index * 0.1, type: "spring" }}
//                 className="bg-white p-8 rounded-2xl shadow-md border border-gray-100 overflow-hidden"
//               >
//                 {/* Section Header */}
//                 <div className="flex justify-between items-center mb-8">
//                   <div className="flex items-center">
//                     <motion.h2 
//                       whileHover={{ x: 5 }}
//                       className="text-3xl font-bold text-gray-900 flex items-center"
//                     >
//                       <span className="w-3 h-8 bg-gradient-to-b from-indigo-500 to-purple-600 rounded-full mr-4"></span>
//                       {category.name}
//                     </motion.h2>
                    
//                     {/* Display category discount if available */}
//                     {categoryDiscount > 0 && (
//                       <motion.div 
//                         initial={{ scale: 0 }}
//                         animate={{ scale: 1 }}
//                         className="ml-4 bg-gradient-to-r from-red-500 to-orange-500 text-white px-3 py-1 rounded-full text-sm font-bold flex items-center"
//                       >
//                         <FaPercent className="mr-1" /> Save up to {categoryDiscount}%
//                       </motion.div>
//                     )}
//                   </div>
                  
//                   <Link 
//                     to={`/category/${category.id}`} 
//                     className="flex items-center text-indigo-600 font-medium hover:text-indigo-800 transition-colors group"
//                   >
//                     <motion.span 
//                       animate={{ x: [0, 3, 0] }}
//                       transition={{ repeat: Infinity, duration: 1.5 }}
//                       className="mr-2 group-hover:mr-3 transition-all"
//                     >
//                       View All
//                     </motion.span>
//                     <FaChevronRight className="group-hover:translate-x-1 transition-transform" />
//                   </Link>
//                 </div>
                
//                 {/* Discounted Products Preview (if available) */}
//                 {discountedProductsInCategory.length > 0 && (
//                   <motion.div 
//                     initial={{ opacity: 0, height: 0 }}
//                     animate={{ opacity: 1, height: "auto" }}
//                     className="mb-6 bg-gradient-to-r from-orange-50 to-red-50 p-4 rounded-lg border border-orange-200"
//                   >
//                     <h3 className="text-lg font-semibold text-orange-800 mb-2 flex items-center">
//                       <FaFire className="mr-2 text-orange-500" /> Hot Deals in {category.name}
//                     </h3>
//                     <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
//                       {discountedProductsInCategory.slice(0, 4).map(product => (
//                         <motion.div 
//                           key={product._id}
//                           whileHover={{ y: -5 }}
//                           className="bg-white p-3 rounded-lg border border-orange-100 shadow-sm flex items-center"
//                         >
//                           <img 
//                             src={getValidImageUrl(product.variants?.[0]?.images?.[0])} 
//                             alt={product.name}
//                             className="w-12 h-12 object-contain mr-3"
//                           />
//                           <div>
//                             <p className="text-sm font-medium text-gray-800 line-clamp-1">{product.name}</p>
//                             <p className="text-xs text-orange-600 font-bold">
//                               {Math.max(...product.variants.map(v => v.discount || 0))}% OFF
//                             </p>
//                           </div>
//                         </motion.div>
//                       ))}
//                     </div>
//                   </motion.div>
//                 )}
                
//                 {/* Subcategories Grid - Now showing exactly 6 items */}
//                 <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-6">
//                   {category.subcategories.map((subcategory) => {
//                     // Find products in this subcategory to determine discount
//                     const subcategoryProducts = discountedProducts.filter(
//                       product => product.category === subcategory.id
//                     );
                    
//                     const subcategoryDiscount = subcategoryProducts.length > 0 
//                       ? Math.max(...subcategoryProducts.map(p => 
//                           Math.max(...p.variants.map(v => v.discount || 0))
//                         ))
//                       : 0;
                    
//                     // Count products in this subcategory
//                     const productCount = subcategoryProducts.length;
                    
//                     // Generate tag based on actual data
//                     const tag = generateTag(subcategoryDiscount, productCount);
                    
//                     return (
//                       <motion.div
//                         key={subcategory.id}
//                         initial={{ opacity: 0, scale: 0.9 }}
//                         animate={{ opacity: 1, scale: 1 }}
//                         transition={{ type: "spring" }}
//                         whileHover={{ y: -8, scale: 1.02 }}
//                         whileTap={{ scale: 0.98 }}
//                         className="relative"
//                       >
//                         <div className="absolute inset-0 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
//                         <Link
//                           to={`/subcategory/${subcategory.id}`}
//                           className="group block p-5 rounded-xl transition-all duration-300 flex flex-col h-full bg-white relative z-10 border border-gray-200 hover:border-transparent hover:shadow-lg"
//                         >
//                           {/* Product Image */}
//                           <div className="w-full h-40 mb-4 overflow-hidden flex items-center justify-center">
//                             <motion.img
//                               src={getValidImageUrl(subcategory.media)}
//                               alt={subcategory.name}
//                               className="max-h-full max-w-full object-contain transition-transform duration-500 group-hover:scale-110"
//                               initial={{ opacity: 0 }}
//                               animate={{ opacity: 1 }}
//                               transition={{ duration: 0.5 }}
//                             />
//                           </div>
                          
//                           {/* Product Title */}
//                           <h3 className="text-lg font-semibold text-gray-800 mb-2 line-clamp-2 text-center group-hover:text-indigo-600 transition-colors">
//                             {subcategory.name}
//                           </h3>
                          
//                           {/* Discount Badge */}
//                           {subcategoryDiscount > 0 && (
//                             <motion.div 
//                               initial={{ scale: 0 }}
//                               animate={{ scale: 1 }}
//                               className="text-center mt-auto mb-2"
//                             >
//                               <span className="inline-block bg-gradient-to-r from-red-500 to-orange-500 text-white text-sm font-bold px-3 py-1 rounded-full">
//                                 Up to {subcategoryDiscount}% OFF
//                               </span>
//                             </motion.div>
//                           )}
                          
//                           {/* Tag - only shown if there's a discount or products */}
//                           {tag && (
//                             <motion.div 
//                               whileHover={{ scale: 1.05 }}
//                               className={`text-xs text-center mt-3 px-3 py-1.5 rounded-full ${tag.color} shadow-sm`}
//                             >
//                               <div className="flex items-center justify-center">
//                                 {tag.icon}
//                                 {tag.text}
//                               </div>
//                             </motion.div>
//                           )}
//                         </Link>
//                       </motion.div>
//                     );
//                   })}
//                 </div>
//               </motion.div>
//             );
//           })}
//         </AnimatePresence>
//       </div>
//     </motion.div>
//   );
// };

// export default DealsPage;

// import React, { useState, useEffect } from "react";
// import { Link } from "react-router-dom";
// import axios from "axios";
// import { FaChevronRight, FaFire, FaTag, FaStar, FaBolt, FaPercent } from "react-icons/fa";
// import { motion, AnimatePresence } from "framer-motion";

// const DealsPage = () => {
//   const [categories, setCategories] = useState([]);
//   const [discountedProducts, setDiscountedProducts] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [featuredDeal, setFeaturedDeal] = useState(null);
//   const [categoryDiscounts, setCategoryDiscounts] = useState({});
//   const [categoryMap, setCategoryMap] = useState({});

//   useEffect(() => {
//     const fetchData = async () => {
//       try {
//         setLoading(true);
        
//         // Fetch categories and discounted products simultaneously
//         const [categoriesResponse, discountedResponse] = await Promise.all([
//           axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/seller/category/list`),
//           axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/seller/product/get-by-discount`)
//         ]);
        
//         if (categoriesResponse.data.success) {
//           const processedCategories = categoriesResponse.data.categories
//             .filter(cat => cat.published)
//             .map(category => {
//               const topLevelSubs = (category.subcategories || [])
//                 .filter(sub => sub.published)
//                 .map(sub => ({
//                   ...sub,
//                   price: Math.floor(Math.random() * 10000) + 1000,
//                   discount: 0, // Initialize with 0, will be updated later
//                   tag: null
//                 }));

//               let allSubcategories = [...topLevelSubs];
//               if (topLevelSubs.length < 6) {
//                 const getNestedSubs = (subs) => {
//                   let result = [];
//                   subs.forEach(sub => {
//                     if (sub.subcategories && sub.subcategories.length > 0) {
//                       const nested = sub.subcategories
//                         .filter(nestedSub => nestedSub.published)
//                         .map(nestedSub => ({
//                           ...nestedSub,
//                           price: Math.floor(Math.random() * 10000) + 1000,
//                           discount: 0,
//                           tag: null
//                         }));
//                       result = result.concat(nested);
//                     }
//                   });
//                   return result;
//                 };

//                 const nestedSubs = getNestedSubs(category.subcategories || []);
//                 const needed = 6 - topLevelSubs.length;
//                 allSubcategories = allSubcategories.concat(nestedSubs.slice(0, needed));
//               }

//               return {
//                 ...category,
//                 subcategories: allSubcategories.slice(0, 6) // Limit to 6 subcategories
//               };
//             })
//             .filter(category => category.subcategories.length > 0);
          
//           setCategories(processedCategories);
          
//           // Create a mapping of category IDs to category names for easier lookup
//           const catMap = {};
//           processedCategories.forEach(category => {
//             catMap[category.id] = category.name;
//             (category.subcategories || []).forEach(subcategory => {
//               catMap[subcategory.id] = subcategory.name;
//             });
//           });
//           setCategoryMap(catMap);
          
//           if (processedCategories.length > 0) {
//             const validCategories = processedCategories.filter(c => c.subcategories.length > 0);
//             if (validCategories.length > 0) {
//               const randomCategory = validCategories[
//                 Math.floor(Math.random() * validCategories.length)
//               ];
//               const randomSubcategory = randomCategory.subcategories[
//                 Math.floor(Math.random() * randomCategory.subcategories.length)
//               ];
//               setFeaturedDeal({
//                 ...randomSubcategory,
//                 categoryName: randomCategory.name
//               });
//             }
//           }
//         }
        
//         // Process discounted products
//         if (discountedResponse.data.success && discountedResponse.data.products) {
//           setDiscountedProducts(discountedResponse.data.products);
          
//           // Calculate max discount for each category
//           const discounts = {};
//           discountedResponse.data.products.forEach(product => {
//             if (product.category && product.variants && product.variants.length > 0) {
//               const categoryId = product.category;
//               const discount = Math.max(...product.variants.map(v => v.discount || 0));
              
//               if (!discounts[categoryId] || discount > discounts[categoryId]) {
//                 discounts[categoryId] = discount;
//               }
//             }
//           });
          
//           setCategoryDiscounts(discounts);
//         }
        
//         setLoading(false);
//       } catch (error) {
//         console.error("Error fetching data:", error);
//         setError("Failed to load data. Please try again later.");
//         setLoading(false);
//       }
//     };

//     fetchData();
//   }, []);

//   // Function to get the maximum discount for a category
//   const getCategoryDiscount = (categoryId) => {
//     return categoryDiscounts[categoryId] || 0;
//   };

//   // Function to get discounted products for a specific category
//   const getDiscountedProductsForCategory = (categoryId) => {
//     return discountedProducts.filter(product => product.category === categoryId);
//   };

//   // Function to get discounted products for a specific category and its subcategories
//   const getDiscountedProductsForCategoryTree = (category) => {
//     const allCategoryIds = [category.id];
    
//     // Recursively get all subcategory IDs
//     const getSubcategoryIds = (subcategories) => {
//       subcategories.forEach(sub => {
//         allCategoryIds.push(sub.id);
//         if (sub.subcategories && sub.subcategories.length > 0) {
//           getSubcategoryIds(sub.subcategories);
//         }
//       });
//     };
    
//     if (category.subcategories && category.subcategories.length > 0) {
//       getSubcategoryIds(category.subcategories);
//     }
    
//     return discountedProducts.filter(product => 
//       allCategoryIds.includes(product.category)
//     );
//   };

//   const generateRandomTag = (discount) => {
//     if (discount > 0) {
//       return {
//         text: `Upto ${discount}% off`, 
//         icon: <FaPercent className="mr-1" />, 
//         color: "bg-gradient-to-r from-red-500 to-orange-500 text-white"
//       };
//     }
    
//     const tags = [
//       { text: "Hot Deal", icon: <FaFire className="mr-1" />, color: "bg-gradient-to-r from-red-500 to-orange-500 text-white" },
//       { text: `From ₹${Math.floor(Math.random() * 5000) + 500}`, icon: <FaTag className="mr-1" />, color: "bg-gradient-to-r from-blue-500 to-indigo-500 text-white" },
//       { text: "Limited Time", icon: <FaBolt className="mr-1" />, color: "bg-gradient-to-r from-purple-500 to-pink-500 text-white" },
//       { text: `Upto ${Math.floor(Math.random() * 70) + 10}% off`, icon: null, color: "bg-gradient-to-r from-green-500 to-teal-500 text-white" }
//     ];
//     return tags[Math.floor(Math.random() * tags.length)];
//   };

//   const getValidImageUrl = (url) => {
//     if (!url) return "https://via.placeholder.com/150?text=No+Image";
//     if (url.includes('google.com') || url.includes('googleusercontent') || 
//         url.includes('www.google.com')) {
//       return "https://via.placeholder.com/150?text=No+Image";
//     }
//     return url;
//   };

//   if (loading) {
//     return (
//       <motion.div 
//         initial={{ opacity: 0 }}
//         animate={{ opacity: 1 }}
//         className="bg-gradient-to-b from-gray-50 to-gray-100 py-12"
//       >
//         <div className="container mx-auto px-4 space-y-12">
//           {[...Array(3)].map((_, i) => (
//             <div key={i}>
//               <div className="h-8 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full w-1/4 mb-8 animate-pulse"></div>
//               <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-6">
//                 {[...Array(6)].map((_, j) => (
//                   <motion.div 
//                     key={j} 
//                     initial={{ opacity: 0.5 }}
//                     animate={{ opacity: 0.8 }}
//                     transition={{ repeat: Infinity, repeatType: "reverse", duration: 1, delay: j * 0.1 }}
//                     className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl h-64 shadow-sm"
//                   ></motion.div>
//                 ))}
//               </div>
//             </div>
//           ))}
//         </div>
//       </motion.div>
//     );
//   }

//   if (error) {
//     return (
//       <motion.div 
//         initial={{ opacity: 0 }}
//         animate={{ opacity: 1 }}
//         className="bg-gradient-to-b from-gray-50 to-gray-100 py-12 container mx-auto px-4 text-center"
//       >
//         <div className="max-w-md mx-auto bg-white p-8 rounded-xl shadow-lg">
//           <div className="text-red-500 text-5xl mb-4">⚠️</div>
//           <h3 className="text-xl font-bold text-gray-800 mb-2">Oops! Something went wrong</h3>
//           <p className="text-gray-600 mb-6">{error}</p>
//           <motion.button
//             whileHover={{ scale: 1.05 }}
//             whileTap={{ scale: 0.95 }}
//             className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-6 py-3 rounded-lg font-medium shadow-md"
//             onClick={() => window.location.reload()}
//           >
//             Try Again
//           </motion.button>
//         </div>
//       </motion.div>
//     );
//   }

//   if (categories.length === 0) {
//     return (
//       <motion.div 
//         initial={{ opacity: 0 }}
//         animate={{ opacity: 1 }}
//         className="bg-gradient-to-b from-gray-50 to-gray-100 py-12 container mx-auto px-4 text-center"
//       >
//         <div className="max-w-md mx-auto bg-white p-8 rounded-xl shadow-lg">
//           <div className="text-gray-400 text-5xl mb-4">🛍️</div>
//           <h3 className="text-xl font-bold text-gray-800 mb-2">No Deals Available</h3>
//           <p className="text-gray-600 mb-6">Check back later for exciting offers!</p>
//           <Link to="/">
//             <motion.button
//               whileHover={{ scale: 1.05 }}
//               whileTap={{ scale: 0.95 }}
//               className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-6 py-3 rounded-lg font-medium shadow-md"
//             >
//               Go to Home
//             </motion.button>
//           </Link>
//         </div>
//       </motion.div>
//     );
//   }

//   return (
//     <motion.div 
//       initial={{ opacity: 0 }}
//       animate={{ opacity: 1 }}
//       transition={{ duration: 0.5 }}
//       className="bg-gradient-to-b from-gray-50 to-gray-100 py-12"
//     >
//       <div className="container mx-auto px-4 space-y-12">
//         {/* Featured Deals Banner */}
//         {featuredDeal && (
//           <motion.div
//             initial={{ opacity: 0, y: 20 }}
//             animate={{ opacity: 1, y: 0 }}
//             transition={{ duration: 0.6 }}
//             className="relative bg-gradient-to-r from-indigo-600 to-purple-700 rounded-2xl p-8 text-white overflow-hidden shadow-xl"
//           >
//             <div className="absolute inset-0 bg-noise opacity-10"></div>
//             <div className="absolute -right-20 -top-20 w-64 h-64 bg-white bg-opacity-10 rounded-full"></div>
//             <div className="absolute -left-20 -bottom-20 w-80 h-80 bg-white bg-opacity-5 rounded-full"></div>
            
//             <div className="relative z-10 flex flex-col md:flex-row items-center">
//               <div className="md:w-1/2 mb-8 md:mb-0">
//                 <motion.div 
//                   initial={{ x: -20 }}
//                   animate={{ x: 0 }}
//                   transition={{ delay: 0.2 }}
//                   className="inline-block px-4 py-2 bg-white bg-opacity-20 rounded-full text-sm mb-6 backdrop-blur-sm"
//                 >
//                   {featuredDeal.categoryName}
//                 </motion.div>
//                 <motion.h3 
//                   initial={{ opacity: 0, y: 10 }}
//                   animate={{ opacity: 1, y: 0 }}
//                   transition={{ delay: 0.3 }}
//                   className="text-3xl md:text-4xl font-bold mb-4 leading-tight"
//                 >
//                   {featuredDeal.name}
//                 </motion.h3>
//                 <motion.p 
//                   initial={{ opacity: 0 }}
//                   animate={{ opacity: 1 }}
//                   transition={{ delay: 0.4 }}
//                   className="mb-6 text-white text-opacity-90 max-w-md"
//                 >
//                   Don't miss out on this exclusive offer! Limited time only.
//                 </motion.p>
//                 <motion.div
//                   initial={{ opacity: 0 }}
//                   animate={{ opacity: 1 }}
//                   transition={{ delay: 0.5 }}
//                 >
//                   <Link 
//                     to={`/subcategory/${featuredDeal.id}`}
//                     className="inline-flex items-center bg-white text-indigo-600 px-8 py-3 rounded-xl font-bold hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
//                   >
//                     Shop Now <FaChevronRight className="ml-2" />
//                   </Link>
//                 </motion.div>
//               </div>
//               <div className="md:w-1/2 flex justify-center">
//                 <motion.div
//                   initial={{ scale: 0.9, rotate: -5 }}
//                   animate={{ scale: 1, rotate: 0 }}
//                   transition={{ type: "spring", stiffness: 100, damping: 10 }}
//                   whileHover={{ scale: 1.05, rotate: 5 }}
//                   className="relative"
//                 >
//                   <div className="absolute -inset-4 bg-white bg-opacity-20 rounded-2xl blur-xl"></div>
//                   <img
//                     src={getValidImageUrl(featuredDeal.media)}
//                     alt={featuredDeal.name}
//                     className="relative z-10 w-72 h-72 object-contain drop-shadow-2xl"
//                   />
//                 </motion.div>
//               </div>
//             </div>
//           </motion.div>
//         )}

//         {/* Category Sections */}
//         <AnimatePresence>
//           {categories.map((category, index) => {
//             const categoryDiscount = getCategoryDiscount(category.id);
//             const discountedProductsInCategory = getDiscountedProductsForCategoryTree(category);
            
//             return (
//               <motion.div 
//                 key={category.id}
//                 initial={{ opacity: 0, y: 30 }}
//                 animate={{ opacity: 1, y: 0 }}
//                 transition={{ delay: index * 0.1, type: "spring" }}
//                 className="bg-white p-8 rounded-2xl shadow-md border border-gray-100 overflow-hidden"
//               >
//                 {/* Section Header */}
//                 <div className="flex justify-between items-center mb-8">
//                   <div className="flex items-center">
//                     <motion.h2 
//                       whileHover={{ x: 5 }}
//                       className="text-3xl font-bold text-gray-900 flex items-center"
//                     >
//                       <span className="w-3 h-8 bg-gradient-to-b from-indigo-500 to-purple-600 rounded-full mr-4"></span>
//                       {category.name}
//                     </motion.h2>
                    
//                     {/* Display category discount if available */}
//                     {categoryDiscount > 0 && (
//                       <motion.div 
//                         initial={{ scale: 0 }}
//                         animate={{ scale: 1 }}
//                         className="ml-4 bg-gradient-to-r from-red-500 to-orange-500 text-white px-3 py-1 rounded-full text-sm font-bold flex items-center"
//                       >
//                         <FaPercent className="mr-1" /> Save up to {categoryDiscount}%
//                       </motion.div>
//                     )}
//                   </div>
                  
//                   <Link 
//                     to={`/category/${category.id}`} 
//                     className="flex items-center text-indigo-600 font-medium hover:text-indigo-800 transition-colors group"
//                   >
//                     <motion.span 
//                       animate={{ x: [0, 3, 0] }}
//                       transition={{ repeat: Infinity, duration: 1.5 }}
//                       className="mr-2 group-hover:mr-3 transition-all"
//                     >
//                       View All
//                     </motion.span>
//                     <FaChevronRight className="group-hover:translate-x-1 transition-transform" />
//                   </Link>
//                 </div>
                
//                 {/* Discounted Products Preview (if available) */}
//                 {discountedProductsInCategory.length > 0 && (
//                   <motion.div 
//                     initial={{ opacity: 0, height: 0 }}
//                     animate={{ opacity: 1, height: "auto" }}
//                     className="mb-6 bg-gradient-to-r from-orange-50 to-red-50 p-4 rounded-lg border border-orange-200"
//                   >
//                     <h3 className="text-lg font-semibold text-orange-800 mb-2 flex items-center">
//                       <FaFire className="mr-2 text-orange-500" /> Hot Deals in {category.name}
//                     </h3>
//                     <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
//                       {discountedProductsInCategory.slice(0, 4).map(product => (
//                         <motion.div 
//                           key={product._id}
//                           whileHover={{ y: -5 }}
//                           className="bg-white p-3 rounded-lg border border-orange-100 shadow-sm flex items-center"
//                         >
//                           <img 
//                             src={getValidImageUrl(product.variants?.[0]?.images?.[0])} 
//                             alt={product.name}
//                             className="w-12 h-12 object-contain mr-3"
//                           />
//                           <div>
//                             <p className="text-sm font-medium text-gray-800 line-clamp-1">{product.name}</p>
//                             <p className="text-xs text-orange-600 font-bold">
//                               {Math.max(...product.variants.map(v => v.discount || 0))}% OFF
//                             </p>
//                           </div>
//                         </motion.div>
//                       ))}
//                     </div>
//                   </motion.div>
//                 )}
                
//                 {/* Subcategories Grid - Now showing exactly 6 items */}
//                 <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-6">
//                   {category.subcategories.map((subcategory) => {
//                     // Find products in this subcategory to determine discount
//                     const subcategoryProducts = discountedProducts.filter(
//                       product => product.category === subcategory.id
//                     );
                    
//                     const subcategoryDiscount = subcategoryProducts.length > 0 
//                       ? Math.max(...subcategoryProducts.map(p => 
//                           Math.max(...p.variants.map(v => v.discount || 0))
//                         ))
//                       : 0;
                    
//                     // Update subcategory with real discount
//                     const updatedSubcategory = {
//                       ...subcategory,
//                       discount: subcategoryDiscount,
//                       tag: generateRandomTag(subcategoryDiscount)
//                     };
                    
//                     return (
//                       <motion.div
//                         key={updatedSubcategory.id}
//                         initial={{ opacity: 0, scale: 0.9 }}
//                         animate={{ opacity: 1, scale: 1 }}
//                         transition={{ type: "spring" }}
//                         whileHover={{ y: -8, scale: 1.02 }}
//                         whileTap={{ scale: 0.98 }}
//                         className="relative"
//                       >
//                         <div className="absolute inset-0 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
//                         <Link
//                           to={`/subcategory/${updatedSubcategory.id}`}
//                           className="group block p-5 rounded-xl transition-all duration-300 flex flex-col h-full bg-white relative z-10 border border-gray-200 hover:border-transparent hover:shadow-lg"
//                         >
//                           {/* Product Image */}
//                           <div className="w-full h-40 mb-4 overflow-hidden flex items-center justify-center">
//                             <motion.img
//                               src={getValidImageUrl(updatedSubcategory.media)}
//                               alt={updatedSubcategory.name}
//                               className="max-h-full max-w-full object-contain transition-transform duration-500 group-hover:scale-110"
//                               initial={{ opacity: 0 }}
//                               animate={{ opacity: 1 }}
//                               transition={{ duration: 0.5 }}
//                             />
//                           </div>
                          
//                           {/* Product Title */}
//                           <h3 className="text-lg font-semibold text-gray-800 mb-2 line-clamp-2 text-center group-hover:text-indigo-600 transition-colors">
//                             {updatedSubcategory.name}
//                           </h3>
                          
//                           {/* Discount Badge */}
//                           {updatedSubcategory.discount > 0 && (
//                             <motion.div 
//                               initial={{ scale: 0 }}
//                               animate={{ scale: 1 }}
//                               className="text-center mt-auto mb-2"
//                             >
//                               <span className="inline-block bg-gradient-to-r from-red-500 to-orange-500 text-white text-sm font-bold px-3 py-1 rounded-full">
//                                 Up to {updatedSubcategory.discount}% OFF
//                               </span>
//                             </motion.div>
//                           )}
                          
//                           {/* Tag */}
//                           {updatedSubcategory.tag && (
//                             <motion.div 
//                               whileHover={{ scale: 1.05 }}
//                               className={`text-xs text-center mt-3 px-3 py-1.5 rounded-full ${updatedSubcategory.tag.color} shadow-sm`}
//                             >
//                               <div className="flex items-center justify-center">
//                                 {updatedSubcategory.tag.icon}
//                                 {updatedSubcategory.tag.text}
//                               </div>
//                             </motion.div>
//                           )}
//                         </Link>
//                       </motion.div>
//                     );
//                   })}
//                 </div>
//               </motion.div>
//             );
//           })}
//         </AnimatePresence>
//       </div>
//     </motion.div>
//   );
// };

// export default DealsPage;

// import React, { useState, useEffect } from "react";
// import { Link } from "react-router-dom";
// import axios from "axios";
// import { FaChevronRight, FaFire, FaTag, FaStar, FaBolt, FaPercent } from "react-icons/fa";
// import { motion, AnimatePresence } from "framer-motion";

// const DealsPage = () => {
//   const [categories, setCategories] = useState([]);
//   const [discountedProducts, setDiscountedProducts] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [featuredDeal, setFeaturedDeal] = useState(null);
//   const [categoryDiscounts, setCategoryDiscounts] = useState({});

//   useEffect(() => {
//     const fetchData = async () => {
//       try {
//         setLoading(true);
        
//         // Fetch categories and discounted products simultaneously
//         const [categoriesResponse, discountedResponse] = await Promise.all([
//           axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/seller/category/list`),
//           axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/seller/product/get-by-discount`)
//         ]);
        
//         if (categoriesResponse.data.success) {
//           const processedCategories = categoriesResponse.data.categories
//             .filter(cat => cat.published)
//             .map(category => {
//               const topLevelSubs = (category.subcategories || [])
//                 .filter(sub => sub.published)
//                 .map(sub => ({
//                   ...sub,
//                   price: Math.floor(Math.random() * 10000) + 1000,
//                   discount: 0, // Initialize with 0, will be updated later
//                   tag: null
//                 }));

//               let allSubcategories = [...topLevelSubs];
//               if (topLevelSubs.length < 6) {
//                 const getNestedSubs = (subs) => {
//                   let result = [];
//                   subs.forEach(sub => {
//                     if (sub.subcategories && sub.subcategories.length > 0) {
//                       const nested = sub.subcategories
//                         .filter(nestedSub => nestedSub.published)
//                         .map(nestedSub => ({
//                           ...nestedSub,
//                           price: Math.floor(Math.random() * 10000) + 1000,
//                           discount: 0,
//                           tag: null
//                         }));
//                       result = result.concat(nested);
//                     }
//                   });
//                   return result;
//                 };

//                 const nestedSubs = getNestedSubs(category.subcategories || []);
//                 const needed = 6 - topLevelSubs.length;
//                 allSubcategories = allSubcategories.concat(nestedSubs.slice(0, needed));
//               }

//               return {
//                 ...category,
//                 subcategories: allSubcategories.slice(0, 6) // Limit to 6 subcategories
//               };
//             })
//             .filter(category => category.subcategories.length > 0);
          
//           setCategories(processedCategories);
          
//           if (processedCategories.length > 0) {
//             const validCategories = processedCategories.filter(c => c.subcategories.length > 0);
//             if (validCategories.length > 0) {
//               const randomCategory = validCategories[
//                 Math.floor(Math.random() * validCategories.length)
//               ];
//               const randomSubcategory = randomCategory.subcategories[
//                 Math.floor(Math.random() * randomCategory.subcategories.length)
//               ];
//               setFeaturedDeal({
//                 ...randomSubcategory,
//                 categoryName: randomCategory.name
//               });
//             }
//           }
//         }
        
//         // Process discounted products
//         if (discountedResponse.data.success && discountedResponse.data.products) {
//           setDiscountedProducts(discountedResponse.data.products);
          
//           // Calculate max discount for each category
//           const discounts = {};
//           discountedResponse.data.products.forEach(product => {
//             if (product.category && product.variants && product.variants.length > 0) {
//               const categoryId = product.category;
//               // Get the maximum discount from all variants
//               const maxDiscount = Math.max(...product.variants.map(v => v.discount || 0));
              
//               if (!discounts[categoryId] || maxDiscount > discounts[categoryId]) {
//                 discounts[categoryId] = maxDiscount;
//               }
//             }
//           });
          
//           setCategoryDiscounts(discounts);
//         }
        
//         setLoading(false);
//       } catch (error) {
//         console.error("Error fetching data:", error);
//         setError("Failed to load data. Please try again later.");
//         setLoading(false);
//       }
//     };

//     fetchData();
//   }, []);

//   // Function to get the maximum discount for a category
//   const getCategoryDiscount = (categoryId) => {
//     return categoryDiscounts[categoryId] || 0;
//   };

//   // Function to get discounted products for a specific category
//   const getDiscountedProductsForCategory = (categoryId) => {
//     return discountedProducts.filter(product => product.category === categoryId);
//   };

//   // Function to get discounted products for a specific subcategory
//   const getDiscountedProductsForSubcategory = (subcategoryId) => {
//     return discountedProducts.filter(product => {
//       // Check if product belongs to this subcategory
//       // This would need to be implemented based on your actual data structure
//       // For now, we'll return an empty array
//       return false;
//     });
//   };

//   const generateRandomTag = (discount) => {
//     if (discount > 0) {
//       return {
//         text: `Upto ${discount}% off`, 
//         icon: <FaPercent className="mr-1" />, 
//         color: "bg-gradient-to-r from-red-500 to-orange-500 text-white"
//       };
//     }
    
//     const tags = [
//       { text: "Hot Deal", icon: <FaFire className="mr-1" />, color: "bg-gradient-to-r from-red-500 to-orange-500 text-white" },
//       { text: `From ₹${Math.floor(Math.random() * 5000) + 500}`, icon: <FaTag className="mr-1" />, color: "bg-gradient-to-r from-blue-500 to-indigo-500 text-white" },
//       { text: "Limited Time", icon: <FaBolt className="mr-1" />, color: "bg-gradient-to-r from-purple-500 to-pink-500 text-white" },
//       { text: `Upto ${Math.floor(Math.random() * 70) + 10}% off`, icon: null, color: "bg-gradient-to-r from-green-500 to-teal-500 text-white" }
//     ];
//     return tags[Math.floor(Math.random() * tags.length)];
//   };

//   const getValidImageUrl = (url) => {
//     if (!url) return "https://via.placeholder.com/150?text=No+Image";
//     if (url.includes('google.com') || url.includes('googleusercontent')) {
//       return "https://via.placeholder.com/150?text=No+Image";
//     }
//     return url;
//   };

//   if (loading) {
//     return (
//       <motion.div 
//         initial={{ opacity: 0 }}
//         animate={{ opacity: 1 }}
//         className="bg-gradient-to-b from-gray-50 to-gray-100 py-12"
//       >
//         <div className="container mx-auto px-4 space-y-12">
//           {[...Array(3)].map((_, i) => (
//             <div key={i}>
//               <div className="h-8 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full w-1/4 mb-8 animate-pulse"></div>
//               <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-6">
//                 {[...Array(6)].map((_, j) => (
//                   <motion.div 
//                     key={j} 
//                     initial={{ opacity: 0.5 }}
//                     animate={{ opacity: 0.8 }}
//                     transition={{ repeat: Infinity, repeatType: "reverse", duration: 1, delay: j * 0.1 }}
//                     className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl h-64 shadow-sm"
//                   ></motion.div>
//                 ))}
//               </div>
//             </div>
//           ))}
//         </div>
//       </motion.div>
//     );
//   }

//   if (error) {
//     return (
//       <motion.div 
//         initial={{ opacity: 0 }}
//         animate={{ opacity: 1 }}
//         className="bg-gradient-to-b from-gray-50 to-gray-100 py-12 container mx-auto px-4 text-center"
//       >
//         <div className="max-w-md mx-auto bg-white p-8 rounded-xl shadow-lg">
//           <div className="text-red-500 text-5xl mb-4">⚠️</div>
//           <h3 className="text-xl font-bold text-gray-800 mb-2">Oops! Something went wrong</h3>
//           <p className="text-gray-600 mb-6">{error}</p>
//           <motion.button
//             whileHover={{ scale: 1.05 }}
//             whileTap={{ scale: 0.95 }}
//             className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-6 py-3 rounded-lg font-medium shadow-md"
//             onClick={() => window.location.reload()}
//           >
//             Try Again
//           </motion.button>
//         </div>
//       </motion.div>
//     );
//   }

//   if (categories.length === 0) {
//     return (
//       <motion.div 
//         initial={{ opacity: 0 }}
//         animate={{ opacity: 1 }}
//         className="bg-gradient-to-b from-gray-50 to-gray-100 py-12 container mx-auto px-4 text-center"
//       >
//         <div className="max-w-md mx-auto bg-white p-8 rounded-xl shadow-lg">
//           <div className="text-gray-400 text-5xl mb-4">🛍️</div>
//           <h3 className="text-xl font-bold text-gray-800 mb-2">No Deals Available</h3>
//           <p className="text-gray-600 mb-6">Check back later for exciting offers!</p>
//           <Link to="/">
//             <motion.button
//               whileHover={{ scale: 1.05 }}
//               whileTap={{ scale: 0.95 }}
//               className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-6 py-3 rounded-lg font-medium shadow-md"
//             >
//               Go to Home
//             </motion.button>
//           </Link>
//         </div>
//       </motion.div>
//     );
//   }

//   return (
//     <motion.div 
//       initial={{ opacity: 0 }}
//       animate={{ opacity: 1 }}
//       transition={{ duration: 0.5 }}
//       className="bg-gradient-to-b from-gray-50 to-gray-100 py-12"
//     >
//       <div className="container mx-auto px-4 space-y-12">
//         {/* Featured Deals Banner */}
//         {featuredDeal && (
//           <motion.div
//             initial={{ opacity: 0, y: 20 }}
//             animate={{ opacity: 1, y: 0 }}
//             transition={{ duration: 0.6 }}
//             className="relative bg-gradient-to-r from-indigo-600 to-purple-700 rounded-2xl p-8 text-white overflow-hidden shadow-xl"
//           >
//             <div className="absolute inset-0 bg-noise opacity-10"></div>
//             <div className="absolute -right-20 -top-20 w-64 h-64 bg-white bg-opacity-10 rounded-full"></div>
//             <div className="absolute -left-20 -bottom-20 w-80 h-80 bg-white bg-opacity-5 rounded-full"></div>
            
//             <div className="relative z-10 flex flex-col md:flex-row items-center">
//               <div className="md:w-1/2 mb-8 md:mb-0">
//                 <motion.div 
//                   initial={{ x: -20 }}
//                   animate={{ x: 0 }}
//                   transition={{ delay: 0.2 }}
//                   className="inline-block px-4 py-2 bg-white bg-opacity-20 rounded-full text-sm mb-6 backdrop-blur-sm"
//                 >
//                   {featuredDeal.categoryName}
//                 </motion.div>
//                 <motion.h3 
//                   initial={{ opacity: 0, y: 10 }}
//                   animate={{ opacity: 1, y: 0 }}
//                   transition={{ delay: 0.3 }}
//                   className="text-3xl md:text-4xl font-bold mb-4 leading-tight"
//                 >
//                   {featuredDeal.name}
//                 </motion.h3>
//                 <motion.p 
//                   initial={{ opacity: 0 }}
//                   animate={{ opacity: 1 }}
//                   transition={{ delay: 0.4 }}
//                   className="mb-6 text-white text-opacity-90 max-w-md"
//                 >
//                   Don't miss out on this exclusive offer! Limited time only.
//                 </motion.p>
//                 <motion.div
//                   initial={{ opacity: 0 }}
//                   animate={{ opacity: 1 }}
//                   transition={{ delay: 0.5 }}
//                 >
//                   <Link 
//                     to={`/subcategory/${featuredDeal.id}`}
//                     className="inline-flex items-center bg-white text-indigo-600 px-8 py-3 rounded-xl font-bold hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
//                   >
//                     Shop Now <FaChevronRight className="ml-2" />
//                   </Link>
//                 </motion.div>
//               </div>
//               <div className="md:w-1/2 flex justify-center">
//                 <motion.div
//                   initial={{ scale: 0.9, rotate: -5 }}
//                   animate={{ scale: 1, rotate: 0 }}
//                   transition={{ type: "spring", stiffness: 100, damping: 10 }}
//                   whileHover={{ scale: 1.05, rotate: 5 }}
//                   className="relative"
//                 >
//                   <div className="absolute -inset-4 bg-white bg-opacity-20 rounded-2xl blur-xl"></div>
//                   <img
//                     src={getValidImageUrl(featuredDeal.media)}
//                     alt={featuredDeal.name}
//                     className="relative z-10 w-72 h-72 object-contain drop-shadow-2xl"
//                   />
//                 </motion.div>
//               </div>
//             </div>
//           </motion.div>
//         )}

//         {/* Category Sections */}
//         <AnimatePresence>
//           {categories.map((category, index) => {
//             const categoryDiscount = getCategoryDiscount(category.id);
//             const discountedProductsInCategory = getDiscountedProductsForCategory(category.id);
            
//             return (
//               <motion.div 
//                 key={category.id}
//                 initial={{ opacity: 0, y: 30 }}
//                 animate={{ opacity: 1, y: 0 }}
//                 transition={{ delay: index * 0.1, type: "spring" }}
//                 className="bg-white p-8 rounded-2xl shadow-md border border-gray-100 overflow-hidden"
//               >
//                 {/* Section Header */}
//                 <div className="flex justify-between items-center mb-8">
//                   <div className="flex items-center">
//                     <motion.h2 
//                       whileHover={{ x: 5 }}
//                       className="text-3xl font-bold text-gray-900 flex items-center"
//                     >
//                       <span className="w-3 h-8 bg-gradient-to-b from-indigo-500 to-purple-600 rounded-full mr-4"></span>
//                       {category.name}
//                     </motion.h2>
                    
//                     {/* Display category discount if available */}
//                     {categoryDiscount > 0 && (
//                       <motion.div 
//                         initial={{ scale: 0 }}
//                         animate={{ scale: 1 }}
//                         className="ml-4 bg-gradient-to-r from-red-500 to-orange-500 text-white px-3 py-1 rounded-full text-sm font-bold flex items-center"
//                       >
//                         <FaPercent className="mr-1" /> Save up to {categoryDiscount}%
//                       </motion.div>
//                     )}
//                   </div>
                  
//                   <Link 
//                     to={`/category/${category.id}`} 
//                     className="flex items-center text-indigo-600 font-medium hover:text-indigo-800 transition-colors group"
//                   >
//                     <motion.span 
//                       animate={{ x: [0, 3, 0] }}
//                       transition={{ repeat: Infinity, duration: 1.5 }}
//                       className="mr-2 group-hover:mr-3 transition-all"
//                     >
//                       View All
//                     </motion.span>
//                     <FaChevronRight className="group-hover:translate-x-1 transition-transform" />
//                   </Link>
//                 </div>
                
//                 {/* Discounted Products Preview (if available) */}
//                 {discountedProductsInCategory.length > 0 && (
//                   <motion.div 
//                     initial={{ opacity: 0, height: 0 }}
//                     animate={{ opacity: 1, height: "auto" }}
//                     className="mb-6 bg-gradient-to-r from-orange-50 to-red-50 p-4 rounded-lg border border-orange-200"
//                   >
//                     <h3 className="text-lg font-semibold text-orange-800 mb-2 flex items-center">
//                       <FaFire className="mr-2 text-orange-500" /> Hot Deals in {category.name}
//                     </h3>
//                     <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
//                       {discountedProductsInCategory.slice(0, 4).map(product => (
//                         <motion.div 
//                           key={product._id}
//                           whileHover={{ y: -5 }}
//                           className="bg-white p-3 rounded-lg border border-orange-100 shadow-sm flex items-center"
//                         >
//                           <img 
//                             src={getValidImageUrl(product.variants?.[0]?.images?.[0])} 
//                             alt={product.name}
//                             className="w-12 h-12 object-contain mr-3"
//                           />
//                           <div>
//                             <p className="text-sm font-medium text-gray-800 line-clamp-1">{product.name}</p>
//                             <p className="text-xs text-orange-600 font-bold">
//                               {Math.max(...product.variants.map(v => v.discount || 0))}% OFF
//                             </p>
//                           </div>
//                         </motion.div>
//                       ))}
//                     </div>
//                   </motion.div>
//                 )}
                
//                 {/* Subcategories Grid - Now showing exactly 6 items */}
//                 <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-6">
//                   {category.subcategories.map((subcategory) => {
//                     // Find products in this subcategory to determine discount
//                     const subcategoryProducts = discountedProducts.filter(
//                       product => product.category === category.id
//                     );
                    
//                     const subcategoryDiscount = subcategoryProducts.length > 0 
//                       ? Math.max(...subcategoryProducts.map(p => 
//                           Math.max(...p.variants.map(v => v.discount || 0))
//                         ))
//                       : 0;
                    
//                     // Update subcategory with real discount
//                     const updatedSubcategory = {
//                       ...subcategory,
//                       discount: subcategoryDiscount,
//                       tag: generateRandomTag(subcategoryDiscount)
//                     };
                    
//                     return (
//                       <motion.div
//                         key={updatedSubcategory.id}
//                         initial={{ opacity: 0, scale: 0.9 }}
//                         animate={{ opacity: 1, scale: 1 }}
//                         transition={{ type: "spring" }}
//                         whileHover={{ y: -8, scale: 1.02 }}
//                         whileTap={{ scale: 0.98 }}
//                         className="relative"
//                       >
//                         <div className="absolute inset-0 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
//                         <Link
//                           to={`/subcategory/${updatedSubcategory.id}`}
//                           className="group block p-5 rounded-xl transition-all duration-300 flex flex-col h-full bg-white relative z-10 border border-gray-200 hover:border-transparent hover:shadow-lg"
//                         >
//                           {/* Product Image */}
//                           <div className="w-full h-40 mb-4 overflow-hidden flex items-center justify-center">
//                             <motion.img
//                               src={getValidImageUrl(updatedSubcategory.media)}
//                               alt={updatedSubcategory.name}
//                               className="max-h-full max-w-full object-contain transition-transform duration-500 group-hover:scale-110"
//                               initial={{ opacity: 0 }}
//                               animate={{ opacity: 1 }}
//                               transition={{ duration: 0.5 }}
//                             />
//                           </div>
                          
//                           {/* Product Title */}
//                           <h3 className="text-lg font-semibold text-gray-800 mb-2 line-clamp-2 text-center group-hover:text-indigo-600 transition-colors">
//                             {updatedSubcategory.name}
//                           </h3>
                          
//                           {/* Discount Badge */}
//                           {updatedSubcategory.discount > 0 && (
//                             <motion.div 
//                               initial={{ scale: 0 }}
//                               animate={{ scale: 1 }}
//                               className="text-center mt-auto mb-2"
//                             >
//                               <span className="inline-block bg-gradient-to-r from-red-500 to-orange-500 text-white text-sm font-bold px-3 py-1 rounded-full">
//                                 Up to {updatedSubcategory.discount}% OFF
//                               </span>
//                             </motion.div>
//                           )}
                          
//                           {/* Tag */}
//                           {updatedSubcategory.tag && (
//                             <motion.div 
//                               whileHover={{ scale: 1.05 }}
//                               className={`text-xs text-center mt-3 px-3 py-1.5 rounded-full ${updatedSubcategory.tag.color} shadow-sm`}
//                             >
//                               <div className="flex items-center justify-center">
//                                 {updatedSubcategory.tag.icon}
//                                 {updatedSubcategory.tag.text}
//                               </div>
//                             </motion.div>
//                           )}
//                         </Link>
//                       </motion.div>
//                     );
//                   })}
//                 </div>
//               </motion.div>
//             );
//           })}
//         </AnimatePresence>
//       </div>
//     </motion.div>
//   );
// };

// export default DealsPage;

// import React, { useState, useEffect } from "react";
// import { Link } from "react-router-dom";
// import axios from "axios";
// import { FaChevronRight, FaFire, FaTag, FaStar, FaBolt, FaPercent } from "react-icons/fa";
// import { motion, AnimatePresence } from "framer-motion";

// const DealsPage = () => {
//   const [categories, setCategories] = useState([]);
//   const [discountedProducts, setDiscountedProducts] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [featuredDeal, setFeaturedDeal] = useState(null);
//   const [categoryDiscounts, setCategoryDiscounts] = useState({});

//   useEffect(() => {
//     const fetchData = async () => {
//       try {
//         setLoading(true);
        
//         // Fetch categories and discounted products simultaneously
//         const [categoriesResponse, discountedResponse] = await Promise.all([
//           axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/seller/category/list`),
//           axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/seller/product/get-by-discount`)
//         ]);
        
//         if (categoriesResponse.data.success) {
//           const processedCategories = categoriesResponse.data.categories
//             .filter(cat => cat.published)
//             .map(category => {
//               const topLevelSubs = (category.subcategories || [])
//                 .filter(sub => sub.published)
//                 .map(sub => ({
//                   ...sub,
//                   price: Math.floor(Math.random() * 10000) + 1000,
//                   discount: 0, // Initialize with 0, will be updated later
//                   tag: null
//                 }));

//               let allSubcategories = [...topLevelSubs];
//               if (topLevelSubs.length < 6) {
//                 const getNestedSubs = (subs) => {
//                   let result = [];
//                   subs.forEach(sub => {
//                     if (sub.subcategories && sub.subcategories.length > 0) {
//                       const nested = sub.subcategories
//                         .filter(nestedSub => nestedSub.published)
//                         .map(nestedSub => ({
//                           ...nestedSub,
//                           price: Math.floor(Math.random() * 10000) + 1000,
//                           discount: 0,
//                           tag: null
//                         }));
//                       result = result.concat(nested);
//                     }
//                   });
//                   return result;
//                 };

//                 const nestedSubs = getNestedSubs(category.subcategories || []);
//                 const needed = 6 - topLevelSubs.length;
//                 allSubcategories = allSubcategories.concat(nestedSubs.slice(0, needed));
//               }

//               return {
//                 ...category,
//                 subcategories: allSubcategories.slice(0, 6) // Limit to 6 subcategories
//               };
//             })
//             .filter(category => category.subcategories.length > 0);
          
//           setCategories(processedCategories);
          
//           if (processedCategories.length > 0) {
//             const validCategories = processedCategories.filter(c => c.subcategories.length > 0);
//             if (validCategories.length > 0) {
//               const randomCategory = validCategories[
//                 Math.floor(Math.random() * validCategories.length)
//               ];
//               const randomSubcategory = randomCategory.subcategories[
//                 Math.floor(Math.random() * randomCategory.subcategories.length)
//               ];
//               setFeaturedDeal({
//                 ...randomSubcategory,
//                 categoryName: randomCategory.name
//               });
//             }
//           }
//         }
        
//         // Process discounted products
//         if (discountedResponse.data.success && discountedResponse.data.products) {
//           setDiscountedProducts(discountedResponse.data.products);
          
//           // Calculate max discount for each category
//           const discounts = {};
//           discountedResponse.data.products.forEach(product => {
//             if (product.category && product.variants && product.variants.length > 0) {
//               const categoryId = product.category;
//               const discount = product.variants[0].discount;
              
//               if (!discounts[categoryId] || discount > discounts[categoryId]) {
//                 discounts[categoryId] = discount;
//               }
//             }
//           });
          
//           setCategoryDiscounts(discounts);
//         }
        
//         setLoading(false);
//       } catch (error) {
//         console.error("Error fetching data:", error);
//         setError("Failed to load data. Please try again later.");
//         setLoading(false);
//       }
//     };

//     fetchData();
//   }, []);

//   // Function to get the maximum discount for a category
//   const getCategoryDiscount = (categoryId) => {
//     return categoryDiscounts[categoryId] || 0;
//   };

//   // Function to get discounted products for a specific category
//   const getDiscountedProductsForCategory = (categoryId) => {
//     return discountedProducts.filter(product => product.category === categoryId);
//   };

//   // Function to get discounted products for a specific subcategory
//   const getDiscountedProductsForSubcategory = (subcategoryId) => {
//     return discountedProducts.filter(product => {
//       // Check if product belongs to this subcategory
//       // This would require a more complex check if your products have subcategory field
//       // For now, we'll just return an empty array as the API response doesn't include subcategory info
//       return false;
//     });
//   };

//   const generateRandomTag = (discount) => {
//     if (discount > 0) {
//       return {
//         text: `Upto ${discount}% off`, 
//         icon: <FaPercent className="mr-1" />, 
//         color: "bg-gradient-to-r from-red-500 to-orange-500 text-white"
//       };
//     }
    
//     const tags = [
//       { text: "Hot Deal", icon: <FaFire className="mr-1" />, color: "bg-gradient-to-r from-red-500 to-orange-500 text-white" },
//       { text: `From ₹${Math.floor(Math.random() * 5000) + 500}`, icon: <FaTag className="mr-1" />, color: "bg-gradient-to-r from-blue-500 to-indigo-500 text-white" },
//       { text: "Limited Time", icon: <FaBolt className="mr-1" />, color: "bg-gradient-to-r from-purple-500 to-pink-500 text-white" },
//       { text: `Upto ${Math.floor(Math.random() * 70) + 10}% off`, icon: null, color: "bg-gradient-to-r from-green-500 to-teal-500 text-white" }
//     ];
//     return tags[Math.floor(Math.random() * tags.length)];
//   };

//   const getValidImageUrl = (url) => {
//     if (!url) return "https://via.placeholder.com/150?text=No+Image";
//     if (url.includes('google.com') || url.includes('googleusercontent')) {
//       return "https://via.placeholder.com/150?text=No+Image";
//     }
//     return url;
//   };

//   if (loading) {
//     return (
//       <motion.div 
//         initial={{ opacity: 0 }}
//         animate={{ opacity: 1 }}
//         className="bg-gradient-to-b from-gray-50 to-gray-100 py-12"
//       >
//         <div className="container mx-auto px-4 space-y-12">
//           {[...Array(3)].map((_, i) => (
//             <div key={i}>
//               <div className="h-8 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full w-1/4 mb-8 animate-pulse"></div>
//               <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-6">
//                 {[...Array(6)].map((_, j) => (
//                   <motion.div 
//                     key={j} 
//                     initial={{ opacity: 0.5 }}
//                     animate={{ opacity: 0.8 }}
//                     transition={{ repeat: Infinity, repeatType: "reverse", duration: 1, delay: j * 0.1 }}
//                     className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl h-64 shadow-sm"
//                   ></motion.div>
//                 ))}
//               </div>
//             </div>
//           ))}
//         </div>
//       </motion.div>
//     );
//   }

//   if (error) {
//     return (
//       <motion.div 
//         initial={{ opacity: 0 }}
//         animate={{ opacity: 1 }}
//         className="bg-gradient-to-b from-gray-50 to-gray-100 py-12 container mx-auto px-4 text-center"
//       >
//         <div className="max-w-md mx-auto bg-white p-8 rounded-xl shadow-lg">
//           <div className="text-red-500 text-5xl mb-4">⚠️</div>
//           <h3 className="text-xl font-bold text-gray-800 mb-2">Oops! Something went wrong</h3>
//           <p className="text-gray-600 mb-6">{error}</p>
//           <motion.button
//             whileHover={{ scale: 1.05 }}
//             whileTap={{ scale: 0.95 }}
//             className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-6 py-3 rounded-lg font-medium shadow-md"
//             onClick={() => window.location.reload()}
//           >
//             Try Again
//           </motion.button>
//         </div>
//       </motion.div>
//     );
//   }

//   if (categories.length === 0) {
//     return (
//       <motion.div 
//         initial={{ opacity: 0 }}
//         animate={{ opacity: 1 }}
//         className="bg-gradient-to-b from-gray-50 to-gray-100 py-12 container mx-auto px-4 text-center"
//       >
//         <div className="max-w-md mx-auto bg-white p-8 rounded-xl shadow-lg">
//           <div className="text-gray-400 text-5xl mb-4">🛍️</div>
//           <h3 className="text-xl font-bold text-gray-800 mb-2">No Deals Available</h3>
//           <p className="text-gray-600 mb-6">Check back later for exciting offers!</p>
//           <Link to="/">
//             <motion.button
//               whileHover={{ scale: 1.05 }}
//               whileTap={{ scale: 0.95 }}
//               className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-6 py-3 rounded-lg font-medium shadow-md"
//             >
//               Go to Home
//             </motion.button>
//           </Link>
//         </div>
//       </motion.div>
//     );
//   }

//   return (
//     <motion.div 
//       initial={{ opacity: 0 }}
//       animate={{ opacity: 1 }}
//       transition={{ duration: 0.5 }}
//       className="bg-gradient-to-b from-gray-50 to-gray-100 py-12"
//     >
//       <div className="container mx-auto px-4 space-y-12">
//         {/* Featured Deals Banner */}
//         {featuredDeal && (
//           <motion.div
//             initial={{ opacity: 0, y: 20 }}
//             animate={{ opacity: 1, y: 0 }}
//             transition={{ duration: 0.6 }}
//             className="relative bg-gradient-to-r from-indigo-600 to-purple-700 rounded-2xl p-8 text-white overflow-hidden shadow-xl"
//           >
//             <div className="absolute inset-0 bg-noise opacity-10"></div>
//             <div className="absolute -right-20 -top-20 w-64 h-64 bg-white bg-opacity-10 rounded-full"></div>
//             <div className="absolute -left-20 -bottom-20 w-80 h-80 bg-white bg-opacity-5 rounded-full"></div>
            
//             <div className="relative z-10 flex flex-col md:flex-row items-center">
//               <div className="md:w-1/2 mb-8 md:mb-0">
//                 <motion.div 
//                   initial={{ x: -20 }}
//                   animate={{ x: 0 }}
//                   transition={{ delay: 0.2 }}
//                   className="inline-block px-4 py-2 bg-white bg-opacity-20 rounded-full text-sm mb-6 backdrop-blur-sm"
//                 >
//                   {featuredDeal.categoryName}
//                 </motion.div>
//                 <motion.h3 
//                   initial={{ opacity: 0, y: 10 }}
//                   animate={{ opacity: 1, y: 0 }}
//                   transition={{ delay: 0.3 }}
//                   className="text-3xl md:text-4xl font-bold mb-4 leading-tight"
//                 >
//                   {featuredDeal.name}
//                 </motion.h3>
//                 <motion.p 
//                   initial={{ opacity: 0 }}
//                   animate={{ opacity: 1 }}
//                   transition={{ delay: 0.4 }}
//                   className="mb-6 text-white text-opacity-90 max-w-md"
//                 >
//                   Don't miss out on this exclusive offer! Limited time only.
//                 </motion.p>
//                 <motion.div
//                   initial={{ opacity: 0 }}
//                   animate={{ opacity: 1 }}
//                   transition={{ delay: 0.5 }}
//                 >
//                   <Link 
//                     to={`/subcategory/${featuredDeal.id}`}
//                     className="inline-flex items-center bg-white text-indigo-600 px-8 py-3 rounded-xl font-bold hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
//                   >
//                     Shop Now <FaChevronRight className="ml-2" />
//                   </Link>
//                 </motion.div>
//               </div>
//               <div className="md:w-1/2 flex justify-center">
//                 <motion.div
//                   initial={{ scale: 0.9, rotate: -5 }}
//                   animate={{ scale: 1, rotate: 0 }}
//                   transition={{ type: "spring", stiffness: 100, damping: 10 }}
//                   whileHover={{ scale: 1.05, rotate: 5 }}
//                   className="relative"
//                 >
//                   <div className="absolute -inset-4 bg-white bg-opacity-20 rounded-2xl blur-xl"></div>
//                   <img
//                     src={getValidImageUrl(featuredDeal.media)}
//                     alt={featuredDeal.name}
//                     className="relative z-10 w-72 h-72 object-contain drop-shadow-2xl"
//                   />
//                 </motion.div>
//               </div>
//             </div>
//           </motion.div>
//         )}

//         {/* Category Sections */}
//         <AnimatePresence>
//           {categories.map((category, index) => {
//             const categoryDiscount = getCategoryDiscount(category.id);
//             const discountedProductsInCategory = getDiscountedProductsForCategory(category.id);
            
//             return (
//               <motion.div 
//                 key={category.id}
//                 initial={{ opacity: 0, y: 30 }}
//                 animate={{ opacity: 1, y: 0 }}
//                 transition={{ delay: index * 0.1, type: "spring" }}
//                 className="bg-white p-8 rounded-2xl shadow-md border border-gray-100 overflow-hidden"
//               >
//                 {/* Section Header */}
//                 <div className="flex justify-between items-center mb-8">
//                   <div className="flex items-center">
//                     <motion.h2 
//                       whileHover={{ x: 5 }}
//                       className="text-3xl font-bold text-gray-900 flex items-center"
//                     >
//                       <span className="w-3 h-8 bg-gradient-to-b from-indigo-500 to-purple-600 rounded-full mr-4"></span>
//                       {category.name}
//                     </motion.h2>
                    
//                     {/* Display category discount if available */}
//                     {categoryDiscount > 0 && (
//                       <motion.div 
//                         initial={{ scale: 0 }}
//                         animate={{ scale: 1 }}
//                         className="ml-4 bg-gradient-to-r from-red-500 to-orange-500 text-white px-3 py-1 rounded-full text-sm font-bold flex items-center"
//                       >
//                         <FaPercent className="mr-1" /> Save up to {categoryDiscount}%
//                       </motion.div>
//                     )}
//                   </div>
                  
//                   <Link 
//                     to={`/category/${category.id}`} 
//                     className="flex items-center text-indigo-600 font-medium hover:text-indigo-800 transition-colors group"
//                   >
//                     <motion.span 
//                       animate={{ x: [0, 3, 0] }}
//                       transition={{ repeat: Infinity, duration: 1.5 }}
//                       className="mr-2 group-hover:mr-3 transition-all"
//                     >
//                       View All
//                     </motion.span>
//                     <FaChevronRight className="group-hover:translate-x-1 transition-transform" />
//                   </Link>
//                 </div>
                
//                 {/* Discounted Products Preview (if available) */}
//                 {discountedProductsInCategory.length > 0 && (
//                   <motion.div 
//                     initial={{ opacity: 0, height: 0 }}
//                     animate={{ opacity: 1, height: "auto" }}
//                     className="mb-6 bg-gradient-to-r from-orange-50 to-red-50 p-4 rounded-lg border border-orange-200"
//                   >
//                     <h3 className="text-lg font-semibold text-orange-800 mb-2 flex items-center">
//                       <FaFire className="mr-2 text-orange-500" /> Hot Deals in {category.name}
//                     </h3>
//                     <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
//                       {discountedProductsInCategory.slice(0, 4).map(product => (
//                         <motion.div 
//                           key={product._id}
//                           whileHover={{ y: -5 }}
//                           className="bg-white p-3 rounded-lg border border-orange-100 shadow-sm flex items-center"
//                         >
//                           <img 
//                             src={getValidImageUrl(product.variants?.[0]?.images?.[0])} 
//                             alt={product.name}
//                             className="w-12 h-12 object-contain mr-3"
//                           />
//                           <div>
//                             <p className="text-sm font-medium text-gray-800 line-clamp-1">{product.name}</p>
//                             <p className="text-xs text-orange-600 font-bold">
//                               {product.variants?.[0]?.discount}% OFF
//                             </p>
//                           </div>
//                         </motion.div>
//                       ))}
//                     </div>
//                   </motion.div>
//                 )}
                
//                 {/* Subcategories Grid - Now showing exactly 6 items */}
//                 <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-6">
//                   {category.subcategories.map((subcategory) => {
//                     // Find products in this subcategory to determine discount
//                     const subcategoryProducts = discountedProducts.filter(
//                       product => product.category === category.id
//                     );
                    
//                     const subcategoryDiscount = subcategoryProducts.length > 0 
//                       ? Math.max(...subcategoryProducts.map(p => p.variants?.[0]?.discount || 0))
//                       : 0;
                    
//                     // Update subcategory with real discount
//                     const updatedSubcategory = {
//                       ...subcategory,
//                       discount: subcategoryDiscount,
//                       tag: generateRandomTag(subcategoryDiscount)
//                     };
                    
//                     return (
//                       <motion.div
//                         key={updatedSubcategory.id}
//                         initial={{ opacity: 0, scale: 0.9 }}
//                         animate={{ opacity: 1, scale: 1 }}
//                         transition={{ type: "spring" }}
//                         whileHover={{ y: -8, scale: 1.02 }}
//                         whileTap={{ scale: 0.98 }}
//                         className="relative"
//                       >
//                         <div className="absolute inset-0 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
//                         <Link
//                           to={`/subcategory/${updatedSubcategory.id}`}
//                           className="group block p-5 rounded-xl transition-all duration-300 flex flex-col h-full bg-white relative z-10 border border-gray-200 hover:border-transparent hover:shadow-lg"
//                         >
//                           {/* Product Image */}
//                           <div className="w-full h-40 mb-4 overflow-hidden flex items-center justify-center">
//                             <motion.img
//                               src={getValidImageUrl(updatedSubcategory.media)}
//                               alt={updatedSubcategory.name}
//                               className="max-h-full max-w-full object-contain transition-transform duration-500 group-hover:scale-110"
//                               initial={{ opacity: 0 }}
//                               animate={{ opacity: 1 }}
//                               transition={{ duration: 0.5 }}
//                             />
//                           </div>
                          
//                           {/* Product Title */}
//                           <h3 className="text-lg font-semibold text-gray-800 mb-2 line-clamp-2 text-center group-hover:text-indigo-600 transition-colors">
//                             {updatedSubcategory.name}
//                           </h3>
                          
//                           {/* Discount Badge */}
//                           {updatedSubcategory.discount > 0 && (
//                             <motion.div 
//                               initial={{ scale: 0 }}
//                               animate={{ scale: 1 }}
//                               className="text-center mt-auto mb-2"
//                             >
//                               <span className="inline-block bg-gradient-to-r from-red-500 to-orange-500 text-white text-sm font-bold px-3 py-1 rounded-full">
//                                 Up to {updatedSubcategory.discount}% OFF
//                               </span>
//                             </motion.div>
//                           )}
                          
//                           {/* Tag */}
//                           {updatedSubcategory.tag && (
//                             <motion.div 
//                               whileHover={{ scale: 1.05 }}
//                               className={`text-xs text-center mt-3 px-3 py-1.5 rounded-full ${updatedSubcategory.tag.color} shadow-sm`}
//                             >
//                               <div className="flex items-center justify-center">
//                                 {updatedSubcategory.tag.icon}
//                                 {updatedSubcategory.tag.text}
//                               </div>
//                             </motion.div>
//                           )}
//                         </Link>
//                       </motion.div>
//                     );
//                   })}
//                 </div>
//               </motion.div>
//             );
//           })}
//         </AnimatePresence>
//       </div>
//     </motion.div>
//   );
// };

// export default DealsPage;

// import React, { useState, useEffect } from "react";
// import { Link } from "react-router-dom";
// import axios from "axios";
// import { FaChevronRight, FaFire, FaTag, FaStar, FaBolt, FaPercent } from "react-icons/fa";
// import { motion, AnimatePresence } from "framer-motion";

// const DealsPage = () => {
//   const [categories, setCategories] = useState([]);
//   const [discountedProducts, setDiscountedProducts] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [featuredDeal, setFeaturedDeal] = useState(null);
//   const [categoryDiscounts, setCategoryDiscounts] = useState({});

//   useEffect(() => {
//     const fetchData = async () => {
//       try {
//         setLoading(true);
        
//         // Fetch categories and discounted products simultaneously
//         const [categoriesResponse, discountedResponse] = await Promise.all([
//           axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/seller/category/list`),
//           axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/seller/product/get-by-discount`)
//         ]);
        
//         if (categoriesResponse.data.success) {
//           const processedCategories = categoriesResponse.data.categories
//             .filter(cat => cat.published)
//             .map(category => {
//               const topLevelSubs = (category.subcategories || [])
//                 .filter(sub => sub.published)
//                 .map(sub => ({
//                   ...sub,
//                   price: Math.floor(Math.random() * 10000) + 1000,
//                   discount: 0, // Initialize with 0, will be updated later
//                   tag: null
//                 }));

//               let allSubcategories = [...topLevelSubs];
//               if (topLevelSubs.length < 6) {
//                 const getNestedSubs = (subs) => {
//                   let result = [];
//                   subs.forEach(sub => {
//                     if (sub.subcategories && sub.subcategories.length > 0) {
//                       const nested = sub.subcategories
//                         .filter(nestedSub => nestedSub.published)
//                         .map(nestedSub => ({
//                           ...nestedSub,
//                           price: Math.floor(Math.random() * 10000) + 1000,
//                           discount: 0,
//                           tag: null
//                         }));
//                       result = result.concat(nested);
//                     }
//                   });
//                   return result;
//                 };

//                 const nestedSubs = getNestedSubs(category.subcategories || []);
//                 const needed = 6 - topLevelSubs.length;
//                 allSubcategories = allSubcategories.concat(nestedSubs.slice(0, needed));
//               }

//               return {
//                 ...category,
//                 subcategories: allSubcategories.slice(0, 6) // Limit to 6 subcategories
//               };
//             })
//             .filter(category => category.subcategories.length > 0);
          
//           setCategories(processedCategories);
          
//           if (processedCategories.length > 0) {
//             const validCategories = processedCategories.filter(c => c.subcategories.length > 0);
//             if (validCategories.length > 0) {
//               const randomCategory = validCategories[
//                 Math.floor(Math.random() * validCategories.length)
//               ];
//               const randomSubcategory = randomCategory.subcategories[
//                 Math.floor(Math.random() * randomCategory.subcategories.length)
//               ];
//               setFeaturedDeal({
//                 ...randomSubcategory,
//                 categoryName: randomCategory.name
//               });
//             }
//           }
//         }
        
//         // Process discounted products
//         if (discountedResponse.data.success && discountedResponse.data.products) {
//           setDiscountedProducts(discountedResponse.data.products);
          
//           // Calculate max discount for each category
//           const discounts = {};
//           discountedResponse.data.products.forEach(product => {
//             if (product.category && product.variants && product.variants.length > 0) {
//               const categoryId = product.category;
//               const discount = product.variants[0].discount;
              
//               if (!discounts[categoryId] || discount > discounts[categoryId]) {
//                 discounts[categoryId] = discount;
//               }
//             }
//           });
          
//           setCategoryDiscounts(discounts);
//         }
        
//         setLoading(false);
//       } catch (error) {
//         console.error("Error fetching data:", error);
//         setError("Failed to load data. Please try again later.");
//         setLoading(false);
//       }
//     };

//     fetchData();
//   }, []);

//   // Function to get the maximum discount for a category
//   const getCategoryDiscount = (categoryId) => {
//     return categoryDiscounts[categoryId] || 0;
//   };

//   // Function to get discounted products for a specific category
//   const getDiscountedProductsForCategory = (categoryId) => {
//     return discountedProducts.filter(product => product.category === categoryId);
//   };

//   const generateRandomTag = (discount) => {
//     if (discount > 0) {
//       return {
//         text: `Upto ${discount}% off`, 
//         icon: <FaPercent className="mr-1" />, 
//         color: "bg-gradient-to-r from-red-500 to-orange-500 text-white"
//       };
//     }
    
//     const tags = [
//       { text: "Hot Deal", icon: <FaFire className="mr-1" />, color: "bg-gradient-to-r from-red-500 to-orange-500 text-white" },
//       { text: `From ₹${Math.floor(Math.random() * 5000) + 500}`, icon: <FaTag className="mr-1" />, color: "bg-gradient-to-r from-blue-500 to-indigo-500 text-white" },
//       { text: "Limited Time", icon: <FaBolt className="mr-1" />, color: "bg-gradient-to-r from-purple-500 to-pink-500 text-white" },
//       { text: `Upto ${Math.floor(Math.random() * 70) + 10}% off`, icon: null, color: "bg-gradient-to-r from-green-500 to-teal-500 text-white" }
//     ];
//     return tags[Math.floor(Math.random() * tags.length)];
//   };

//   const getValidImageUrl = (url) => {
//     if (!url) return "https://via.placeholder.com/150?text=No+Image";
//     if (url.includes('google.com') || url.includes('googleusercontent')) {
//       return "https://via.placeholder.com/150?text=No+Image";
//     }
//     return url;
//   };

//   if (loading) {
//     return (
//       <motion.div 
//         initial={{ opacity: 0 }}
//         animate={{ opacity: 1 }}
//         className="bg-gradient-to-b from-gray-50 to-gray-100 py-12"
//       >
//         <div className="container mx-auto px-4 space-y-12">
//           {[...Array(3)].map((_, i) => (
//             <div key={i}>
//               <div className="h-8 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full w-1/4 mb-8 animate-pulse"></div>
//               <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-6">
//                 {[...Array(6)].map((_, j) => (
//                   <motion.div 
//                     key={j} 
//                     initial={{ opacity: 0.5 }}
//                     animate={{ opacity: 0.8 }}
//                     transition={{ repeat: Infinity, repeatType: "reverse", duration: 1, delay: j * 0.1 }}
//                     className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl h-64 shadow-sm"
//                   ></motion.div>
//                 ))}
//               </div>
//             </div>
//           ))}
//         </div>
//       </motion.div>
//     );
//   }

//   if (error) {
//     return (
//       <motion.div 
//         initial={{ opacity: 0 }}
//         animate={{ opacity: 1 }}
//         className="bg-gradient-to-b from-gray-50 to-gray-100 py-12 container mx-auto px-4 text-center"
//       >
//         <div className="max-w-md mx-auto bg-white p-8 rounded-xl shadow-lg">
//           <div className="text-red-500 text-5xl mb-4">⚠️</div>
//           <h3 className="text-xl font-bold text-gray-800 mb-2">Oops! Something went wrong</h3>
//           <p className="text-gray-600 mb-6">{error}</p>
//           <motion.button
//             whileHover={{ scale: 1.05 }}
//             whileTap={{ scale: 0.95 }}
//             className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-6 py-3 rounded-lg font-medium shadow-md"
//             onClick={() => window.location.reload()}
//           >
//             Try Again
//           </motion.button>
//         </div>
//       </motion.div>
//     );
//   }

//   if (categories.length === 0) {
//     return (
//       <motion.div 
//         initial={{ opacity: 0 }}
//         animate={{ opacity: 1 }}
//         className="bg-gradient-to-b from-gray-50 to-gray-100 py-12 container mx-auto px-4 text-center"
//       >
//         <div className="max-w-md mx-auto bg-white p-8 rounded-xl shadow-lg">
//           <div className="text-gray-400 text-5xl mb-4">🛍️</div>
//           <h3 className="text-xl font-bold text-gray-800 mb-2">No Deals Available</h3>
//           <p className="text-gray-600 mb-6">Check back later for exciting offers!</p>
//           <Link to="/">
//             <motion.button
//               whileHover={{ scale: 1.05 }}
//               whileTap={{ scale: 0.95 }}
//               className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-6 py-3 rounded-lg font-medium shadow-md"
//             >
//               Go to Home
//             </motion.button>
//           </Link>
//         </div>
//       </motion.div>
//     );
//   }

//   return (
//     <motion.div 
//       initial={{ opacity: 0 }}
//       animate={{ opacity: 1 }}
//       transition={{ duration: 0.5 }}
//       className="bg-gradient-to-b from-gray-50 to-gray-100 py-12"
//     >
//       <div className="container mx-auto px-4 space-y-12">
//         {/* Featured Deals Banner */}
//         {featuredDeal && (
//           <motion.div
//             initial={{ opacity: 0, y: 20 }}
//             animate={{ opacity: 1, y: 0 }}
//             transition={{ duration: 0.6 }}
//             className="relative bg-gradient-to-r from-indigo-600 to-purple-700 rounded-2xl p-8 text-white overflow-hidden shadow-xl"
//           >
//             <div className="absolute inset-0 bg-noise opacity-10"></div>
//             <div className="absolute -right-20 -top-20 w-64 h-64 bg-white bg-opacity-10 rounded-full"></div>
//             <div className="absolute -left-20 -bottom-20 w-80 h-80 bg-white bg-opacity-5 rounded-full"></div>
            
//             <div className="relative z-10 flex flex-col md:flex-row items-center">
//               <div className="md:w-1/2 mb-8 md:mb-0">
//                 <motion.div 
//                   initial={{ x: -20 }}
//                   animate={{ x: 0 }}
//                   transition={{ delay: 0.2 }}
//                   className="inline-block px-4 py-2 bg-white bg-opacity-20 rounded-full text-sm mb-6 backdrop-blur-sm"
//                 >
//                   {featuredDeal.categoryName}
//                 </motion.div>
//                 <motion.h3 
//                   initial={{ opacity: 0, y: 10 }}
//                   animate={{ opacity: 1, y: 0 }}
//                   transition={{ delay: 0.3 }}
//                   className="text-3xl md:text-4xl font-bold mb-4 leading-tight"
//                 >
//                   {featuredDeal.name}
//                 </motion.h3>
//                 <motion.p 
//                   initial={{ opacity: 0 }}
//                   animate={{ opacity: 1 }}
//                   transition={{ delay: 0.4 }}
//                   className="mb-6 text-white text-opacity-90 max-w-md"
//                 >
//                   Don't miss out on this exclusive offer! Limited time only.
//                 </motion.p>
//                 <motion.div
//                   initial={{ opacity: 0 }}
//                   animate={{ opacity: 1 }}
//                   transition={{ delay: 0.5 }}
//                 >
//                   <Link 
//                     to={`/subcategory/${featuredDeal.id}`}
//                     className="inline-flex items-center bg-white text-indigo-600 px-8 py-3 rounded-xl font-bold hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
//                   >
//                     Shop Now <FaChevronRight className="ml-2" />
//                   </Link>
//                 </motion.div>
//               </div>
//               <div className="md:w-1/2 flex justify-center">
//                 <motion.div
//                   initial={{ scale: 0.9, rotate: -5 }}
//                   animate={{ scale: 1, rotate: 0 }}
//                   transition={{ type: "spring", stiffness: 100, damping: 10 }}
//                   whileHover={{ scale: 1.05, rotate: 5 }}
//                   className="relative"
//                 >
//                   <div className="absolute -inset-4 bg-white bg-opacity-20 rounded-2xl blur-xl"></div>
//                   <img
//                     src={getValidImageUrl(featuredDeal.media)}
//                     alt={featuredDeal.name}
//                     className="relative z-10 w-72 h-72 object-contain drop-shadow-2xl"
//                   />
//                 </motion.div>
//               </div>
//             </div>
//           </motion.div>
//         )}

//         {/* Category Sections */}
//         <AnimatePresence>
//           {categories.map((category, index) => {
//             const categoryDiscount = getCategoryDiscount(category.id);
//             const discountedProductsInCategory = getDiscountedProductsForCategory(category.id);
            
//             return (
//               <motion.div 
//                 key={category.id}
//                 initial={{ opacity: 0, y: 30 }}
//                 animate={{ opacity: 1, y: 0 }}
//                 transition={{ delay: index * 0.1, type: "spring" }}
//                 className="bg-white p-8 rounded-2xl shadow-md border border-gray-100 overflow-hidden"
//               >
//                 {/* Section Header */}
//                 <div className="flex justify-between items-center mb-8">
//                   <div className="flex items-center">
//                     <motion.h2 
//                       whileHover={{ x: 5 }}
//                       className="text-3xl font-bold text-gray-900 flex items-center"
//                     >
//                       <span className="w-3 h-8 bg-gradient-to-b from-indigo-500 to-purple-600 rounded-full mr-4"></span>
//                       {category.name}
//                     </motion.h2>
                    
//                     {/* Display category discount if available */}
//                     {categoryDiscount > 0 && (
//                       <motion.div 
//                         initial={{ scale: 0 }}
//                         animate={{ scale: 1 }}
//                         className="ml-4 bg-gradient-to-r from-red-500 to-orange-500 text-white px-3 py-1 rounded-full text-sm font-bold flex items-center"
//                       >
//                         <FaPercent className="mr-1" /> Save up to {categoryDiscount}%
//                       </motion.div>
//                     )}
//                   </div>
                  
//                   <Link 
//                     to={`/category/${category.id}`} 
//                     className="flex items-center text-indigo-600 font-medium hover:text-indigo-800 transition-colors group"
//                   >
//                     <motion.span 
//                       animate={{ x: [0, 3, 0] }}
//                       transition={{ repeat: Infinity, duration: 1.5 }}
//                       className="mr-2 group-hover:mr-3 transition-all"
//                     >
//                       View All
//                     </motion.span>
//                     <FaChevronRight className="group-hover:translate-x-1 transition-transform" />
//                   </Link>
//                 </div>
                
//                 {/* Discounted Products Preview (if available) */}
//                 {discountedProductsInCategory.length > 0 && (
//                   <motion.div 
//                     initial={{ opacity: 0, height: 0 }}
//                     animate={{ opacity: 1, height: "auto" }}
//                     className="mb-6 bg-gradient-to-r from-orange-50 to-red-50 p-4 rounded-lg border border-orange-200"
//                   >
//                     <h3 className="text-lg font-semibold text-orange-800 mb-2 flex items-center">
//                       <FaFire className="mr-2 text-orange-500" /> Hot Deals in {category.name}
//                     </h3>
//                     <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
//                       {discountedProductsInCategory.slice(0, 4).map(product => (
//                         <motion.div 
//                           key={product._id}
//                           whileHover={{ y: -5 }}
//                           className="bg-white p-3 rounded-lg border border-orange-100 shadow-sm flex items-center"
//                         >
//                           <img 
//                             src={getValidImageUrl(product.variants?.[0]?.images?.[0])} 
//                             alt={product.name}
//                             className="w-12 h-12 object-contain mr-3"
//                           />
//                           <div>
//                             <p className="text-sm font-medium text-gray-800 line-clamp-1">{product.name}</p>
//                             <p className="text-xs text-orange-600 font-bold">
//                               {product.variants?.[0]?.discount}% OFF
//                             </p>
//                           </div>
//                         </motion.div>
//                       ))}
//                     </div>
//                   </motion.div>
//                 )}
                
//                 {/* Subcategories Grid - Now showing exactly 6 items */}
//                 <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-6">
//                   {category.subcategories.map((subcategory) => {
//                     // Find products in this subcategory to determine discount
//                     const subcategoryProducts = discountedProducts.filter(
//                       product => product.category === category.id
//                     );
                    
//                     const subcategoryDiscount = subcategoryProducts.length > 0 
//                       ? Math.max(...subcategoryProducts.map(p => p.variants?.[0]?.discount || 0))
//                       : 0;
                    
//                     // Update subcategory with real discount
//                     const updatedSubcategory = {
//                       ...subcategory,
//                       discount: subcategoryDiscount,
//                       tag: generateRandomTag(subcategoryDiscount)
//                     };
                    
//                     return (
//                       <motion.div
//                         key={updatedSubcategory.id}
//                         initial={{ opacity: 0, scale: 0.9 }}
//                         animate={{ opacity: 1, scale: 1 }}
//                         transition={{ type: "spring" }}
//                         whileHover={{ y: -8, scale: 1.02 }}
//                         whileTap={{ scale: 0.98 }}
//                         className="relative"
//                       >
//                         <div className="absolute inset-0 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
//                         <Link
//                           to={`/subcategory/${updatedSubcategory.id}`}
//                           className="group block p-5 rounded-xl transition-all duration-300 flex flex-col h-full bg-white relative z-10 border border-gray-200 hover:border-transparent hover:shadow-lg"
//                         >
//                           {/* Product Image */}
//                           <div className="w-full h-40 mb-4 overflow-hidden flex items-center justify-center">
//                             <motion.img
//                               src={getValidImageUrl(updatedSubcategory.media)}
//                               alt={updatedSubcategory.name}
//                               className="max-h-full max-w-full object-contain transition-transform duration-500 group-hover:scale-110"
//                               initial={{ opacity: 0 }}
//                               animate={{ opacity: 1 }}
//                               transition={{ duration: 0.5 }}
//                             />
//                           </div>
                          
//                           {/* Product Title */}
//                           <h3 className="text-lg font-semibold text-gray-800 mb-2 line-clamp-2 text-center group-hover:text-indigo-600 transition-colors">
//                             {updatedSubcategory.name}
//                           </h3>
                          
//                           {/* Discount Badge */}
//                           {updatedSubcategory.discount > 0 && (
//                             <motion.div 
//                               initial={{ scale: 0 }}
//                               animate={{ scale: 1 }}
//                               className="text-center mt-auto mb-2"
//                             >
//                               <span className="inline-block bg-gradient-to-r from-red-500 to-orange-500 text-white text-sm font-bold px-3 py-1 rounded-full">
//                                 Up to {updatedSubcategory.discount}% OFF
//                               </span>
//                             </motion.div>
//                           )}
                          
//                           {/* Tag */}
//                           {updatedSubcategory.tag && (
//                             <motion.div 
//                               whileHover={{ scale: 1.05 }}
//                               className={`text-xs text-center mt-3 px-3 py-1.5 rounded-full ${updatedSubcategory.tag.color} shadow-sm`}
//                             >
//                               <div className="flex items-center justify-center">
//                                 {updatedSubcategory.tag.icon}
//                                 {updatedSubcategory.tag.text}
//                               </div>
//                             </motion.div>
//                           )}
//                         </Link>
//                       </motion.div>
//                     );
//                   })}
//                 </div>
//               </motion.div>
//             );
//           })}
//         </AnimatePresence>
//       </div>
//     </motion.div>
//   );
// };

// export default DealsPage;

// import React, { useState, useEffect } from "react";
// import { Link } from "react-router-dom";
// import axios from "axios";
// import { FaChevronRight, FaFire, FaTag, FaStar, FaBolt } from "react-icons/fa";
// import { motion, AnimatePresence } from "framer-motion";

// const DealsPage = () => {
//   const [categories, setCategories] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [featuredDeal, setFeaturedDeal] = useState(null);

//   useEffect(() => {
//     const fetchCategories = async () => {
//       try {
//         const response = await axios.get(
//           `${process.env.REACT_APP_BACKEND_URL}/api/seller/category/list`
//         );
        
//         if (response.data.success) {
//           const processedCategories = response.data.categories
//             .filter(cat => cat.published)
//             .map(category => {
//               const topLevelSubs = (category.subcategories || [])
//                 .filter(sub => sub.published)
//                 .map(sub => ({
//                   ...sub,
//                   price: Math.floor(Math.random() * 10000) + 1000,
//                   discount: Math.random() > 0.5 ? Math.floor(Math.random() * 70) + 10 : 0,
//                   tag: generateRandomTag()
//                 }));

//               let allSubcategories = [...topLevelSubs];
//               if (topLevelSubs.length < 6) {
//                 const getNestedSubs = (subs) => {
//                   let result = [];
//                   subs.forEach(sub => {
//                     if (sub.subcategories && sub.subcategories.length > 0) {
//                       const nested = sub.subcategories
//                         .filter(nestedSub => nestedSub.published)
//                         .map(nestedSub => ({
//                           ...nestedSub,
//                           price: Math.floor(Math.random() * 10000) + 1000,
//                           discount: Math.random() > 0.5 ? Math.floor(Math.random() * 70) + 10 : 0,
//                           tag: generateRandomTag()
//                         }));
//                       result = result.concat(nested);
//                     }
//                   });
//                   return result;
//                 };

//                 const nestedSubs = getNestedSubs(category.subcategories || []);
//                 const needed = 6 - topLevelSubs.length;
//                 allSubcategories = allSubcategories.concat(nestedSubs.slice(0, needed));
//               }

//               return {
//                 ...category,
//                 subcategories: allSubcategories.slice(0, 6) // Limit to 6 subcategories
//               };
//             })
//             .filter(category => category.subcategories.length > 0);
          
//           setCategories(processedCategories);
          
//           if (processedCategories.length > 0) {
//             const validCategories = processedCategories.filter(c => c.subcategories.length > 0);
//             if (validCategories.length > 0) {
//               const randomCategory = validCategories[
//                 Math.floor(Math.random() * validCategories.length)
//               ];
//               const randomSubcategory = randomCategory.subcategories[
//                 Math.floor(Math.random() * randomCategory.subcategories.length)
//               ];
//               setFeaturedDeal({
//                 ...randomSubcategory,
//                 categoryName: randomCategory.name
//               });
//             }
//           }
//         }
//         setLoading(false);
//       } catch (error) {
//         console.error("Error fetching categories:", error);
//         setError("Failed to load categories. Please try again later.");
//         setLoading(false);
//       }
//     };

//     fetchCategories();
//   }, []);

//   const generateRandomTag = () => {
//     const tags = [
//       { text: "Hot Deal", icon: <FaFire className="mr-1" />, color: "bg-gradient-to-r from-red-500 to-orange-500 text-white" },
//       { text: `From ₹${Math.floor(Math.random() * 5000) + 500}`, icon: <FaTag className="mr-1" />, color: "bg-gradient-to-r from-blue-500 to-indigo-500 text-white" },
//       { text: "Limited Time", icon: <FaBolt className="mr-1" />, color: "bg-gradient-to-r from-purple-500 to-pink-500 text-white" },
//       { text: `Upto ${Math.floor(Math.random() * 70) + 10}% off`, icon: null, color: "bg-gradient-to-r from-green-500 to-teal-500 text-white" }
//     ];
//     return tags[Math.floor(Math.random() * tags.length)];
//   };

//   const getValidImageUrl = (url) => {
//     if (!url) return "https://via.placeholder.com/150?text=No+Image";
//     if (url.includes('google.com') || url.includes('googleusercontent')) {
//       return "https://via.placeholder.com/150?text=No+Image";
//     }
//     return url;
//   };

//   if (loading) {
//     return (
//       <motion.div 
//         initial={{ opacity: 0 }}
//         animate={{ opacity: 1 }}
//         className="bg-gradient-to-b from-gray-50 to-gray-100 py-12"
//       >
//         <div className="container mx-auto px-4 space-y-12">
//           {[...Array(3)].map((_, i) => (
//             <div key={i}>
//               <div className="h-8 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full w-1/4 mb-8 animate-pulse"></div>
//               <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-6">
//                 {[...Array(6)].map((_, j) => (
//                   <motion.div 
//                     key={j} 
//                     initial={{ opacity: 0.5 }}
//                     animate={{ opacity: 0.8 }}
//                     transition={{ repeat: Infinity, repeatType: "reverse", duration: 1, delay: j * 0.1 }}
//                     className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl h-64 shadow-sm"
//                   ></motion.div>
//                 ))}
//               </div>
//             </div>
//           ))}
//         </div>
//       </motion.div>
//     );
//   }

//   if (error) {
//     return (
//       <motion.div 
//         initial={{ opacity: 0 }}
//         animate={{ opacity: 1 }}
//         className="bg-gradient-to-b from-gray-50 to-gray-100 py-12 container mx-auto px-4 text-center"
//       >
//         <div className="max-w-md mx-auto bg-white p-8 rounded-xl shadow-lg">
//           <div className="text-red-500 text-5xl mb-4">⚠️</div>
//           <h3 className="text-xl font-bold text-gray-800 mb-2">Oops! Something went wrong</h3>
//           <p className="text-gray-600 mb-6">{error}</p>
//           <motion.button
//             whileHover={{ scale: 1.05 }}
//             whileTap={{ scale: 0.95 }}
//             className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-6 py-3 rounded-lg font-medium shadow-md"
//             onClick={() => window.location.reload()}
//           >
//             Try Again
//           </motion.button>
//         </div>
//       </motion.div>
//     );
//   }

//   if (categories.length === 0) {
//     return (
//       <motion.div 
//         initial={{ opacity: 0 }}
//         animate={{ opacity: 1 }}
//         className="bg-gradient-to-b from-gray-50 to-gray-100 py-12 container mx-auto px-4 text-center"
//       >
//         <div className="max-w-md mx-auto bg-white p-8 rounded-xl shadow-lg">
//           <div className="text-gray-400 text-5xl mb-4">🛍️</div>
//           <h3 className="text-xl font-bold text-gray-800 mb-2">No Deals Available</h3>
//           <p className="text-gray-600 mb-6">Check back later for exciting offers!</p>
//           <Link to="/">
//             <motion.button
//               whileHover={{ scale: 1.05 }}
//               whileTap={{ scale: 0.95 }}
//               className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-6 py-3 rounded-lg font-medium shadow-md"
//             >
//               Go to Home
//             </motion.button>
//           </Link>
//         </div>
//       </motion.div>
//     );
//   }

//   return (
//     <motion.div 
//       initial={{ opacity: 0 }}
//       animate={{ opacity: 1 }}
//       transition={{ duration: 0.5 }}
//       className="bg-gradient-to-b from-gray-50 to-gray-100 py-12"
//     >
//       <div className="container mx-auto px-4 space-y-12">
//         {/* Featured Deals Banner */}
//         {featuredDeal && (
//           <motion.div
//             initial={{ opacity: 0, y: 20 }}
//             animate={{ opacity: 1, y: 0 }}
//             transition={{ duration: 0.6 }}
//             className="relative bg-gradient-to-r from-indigo-600 to-purple-700 rounded-2xl p-8 text-white overflow-hidden shadow-xl"
//           >
//             <div className="absolute inset-0 bg-noise opacity-10"></div>
//             <div className="absolute -right-20 -top-20 w-64 h-64 bg-white bg-opacity-10 rounded-full"></div>
//             <div className="absolute -left-20 -bottom-20 w-80 h-80 bg-white bg-opacity-5 rounded-full"></div>
            
//             <div className="relative z-10 flex flex-col md:flex-row items-center">
//               <div className="md:w-1/2 mb-8 md:mb-0">
//                 <motion.div 
//                   initial={{ x: -20 }}
//                   animate={{ x: 0 }}
//                   transition={{ delay: 0.2 }}
//                   className="inline-block px-4 py-2 bg-white bg-opacity-20 rounded-full text-sm mb-6 backdrop-blur-sm"
//                 >
//                   {featuredDeal.categoryName}
//                 </motion.div>
//                 <motion.h3 
//                   initial={{ opacity: 0, y: 10 }}
//                   animate={{ opacity: 1, y: 0 }}
//                   transition={{ delay: 0.3 }}
//                   className="text-3xl md:text-4xl font-bold mb-4 leading-tight"
//                 >
//                   {featuredDeal.name}
//                 </motion.h3>
//                 <motion.p 
//                   initial={{ opacity: 0 }}
//                   animate={{ opacity: 1 }}
//                   transition={{ delay: 0.4 }}
//                   className="mb-6 text-white text-opacity-90 max-w-md"
//                 >
//                   Don't miss out on this exclusive offer! Limited time only.
//                 </motion.p>
//                 <motion.div
//                   initial={{ opacity: 0 }}
//                   animate={{ opacity: 1 }}
//                   transition={{ delay: 0.5 }}
//                 >
//                   <Link 
//                     to={`/subcategory/${featuredDeal.id}`}
//                     className="inline-flex items-center bg-white text-indigo-600 px-8 py-3 rounded-xl font-bold hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
//                   >
//                     Shop Now <FaChevronRight className="ml-2" />
//                   </Link>
//                 </motion.div>
//               </div>
//               <div className="md:w-1/2 flex justify-center">
//                 <motion.div
//                   initial={{ scale: 0.9, rotate: -5 }}
//                   animate={{ scale: 1, rotate: 0 }}
//                   transition={{ type: "spring", stiffness: 100, damping: 10 }}
//                   whileHover={{ scale: 1.05, rotate: 5 }}
//                   className="relative"
//                 >
//                   <div className="absolute -inset-4 bg-white bg-opacity-20 rounded-2xl blur-xl"></div>
//                   <img
//                     src={getValidImageUrl(featuredDeal.media)}
//                     alt={featuredDeal.name}
//                     className="relative z-10 w-72 h-72 object-contain drop-shadow-2xl"
//                   />
//                 </motion.div>
//               </div>
//             </div>
//           </motion.div>
//         )}

//         {/* Category Sections */}
//         <AnimatePresence>
//           {categories.map((category, index) => (
//             <motion.div 
//               key={category.id}
//               initial={{ opacity: 0, y: 30 }}
//               animate={{ opacity: 1, y: 0 }}
//               transition={{ delay: index * 0.1, type: "spring" }}
//               className="bg-white p-8 rounded-2xl shadow-md border border-gray-100 overflow-hidden"
//             >
//               {/* Section Header */}
//               <div className="flex justify-between items-center mb-8">
//                 <motion.h2 
//                   whileHover={{ x: 5 }}
//                   className="text-3xl font-bold text-gray-900 flex items-center"
//                 >
//                   <span className="w-3 h-8 bg-gradient-to-b from-indigo-500 to-purple-600 rounded-full mr-4"></span>
//                   {category.name}
//                 </motion.h2>
//                 <Link 
//                   to={`/category/${category.id}`} 
//                   className="flex items-center text-indigo-600 font-medium hover:text-indigo-800 transition-colors group"
//                 >
//                   <motion.span 
//                     animate={{ x: [0, 3, 0] }}
//                     transition={{ repeat: Infinity, duration: 1.5 }}
//                     className="mr-2 group-hover:mr-3 transition-all"
//                   >
//                     View All
//                   </motion.span>
//                   <FaChevronRight className="group-hover:translate-x-1 transition-transform" />
//                 </Link>
//               </div>
              
//               {/* Subcategories Grid - Now showing exactly 6 items */}
//               <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-6">
//                 {category.subcategories.map((subcategory) => (
//                   <motion.div
//                     key={subcategory.id}
//                     initial={{ opacity: 0, scale: 0.9 }}
//                     animate={{ opacity: 1, scale: 1 }}
//                     transition={{ type: "spring" }}
//                     whileHover={{ y: -8, scale: 1.02 }}
//                     whileTap={{ scale: 0.98 }}
//                     className="relative"
//                   >
//                     <div className="absolute inset-0 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
//                     <Link
//                       to={`/subcategory/${subcategory.id}`}
//                       className="group block p-5 rounded-xl transition-all duration-300 flex flex-col h-full bg-white relative z-10 border border-gray-200 hover:border-transparent hover:shadow-lg"
//                     >
//                       {/* Product Image */}
//                       <div className="w-full h-40 mb-4 overflow-hidden flex items-center justify-center">
//                         <motion.img
//                           src={getValidImageUrl(subcategory.media)}
//                           alt={subcategory.name}
//                           className="max-h-full max-w-full object-contain transition-transform duration-500 group-hover:scale-110"
//                           initial={{ opacity: 0 }}
//                           animate={{ opacity: 1 }}
//                           transition={{ duration: 0.5 }}
//                         />
//                       </div>
                      
//                       {/* Product Title */}
//                       <h3 className="text-lg font-semibold text-gray-800 mb-2 line-clamp-2 text-center group-hover:text-indigo-600 transition-colors">
//                         {subcategory.name}
//                       </h3>
                      
//                       {/* Price */}
//                       {/* <div className="flex justify-center items-baseline mt-auto">
//                         <span className="text-xl font-bold text-gray-900">
//                           ₹{subcategory.price.toLocaleString()}
//                         </span>
//                         {subcategory.discount > 0 && (
//                           <span className="ml-2 text-sm line-through text-gray-500">
//                             ₹{(subcategory.price * (1 + subcategory.discount/100)).toLocaleString()}
//                           </span>
//                         )}
//                       </div> */}
                      
//                       {/* Tag */}
//                       {/* {subcategory.tag && (
//                         <motion.div 
//                           whileHover={{ scale: 1.05 }}
//                           className={`text-xs text-center mt-3 px-3 py-1.5 rounded-full ${subcategory.tag.color} shadow-sm`}
//                         >
//                           <div className="flex items-center justify-center">
//                             {subcategory.tag.icon}
//                             {subcategory.tag.text}
//                           </div>
//                         </motion.div>
//                       )} */}
//                     </Link>
//                   </motion.div>
//                 ))}
//               </div>
//             </motion.div>
//           ))}
//         </AnimatePresence>
//       </div>
//     </motion.div>
//   );
// };

// export default DealsPage;

// import React, { useState, useEffect } from "react";
// import { Link } from "react-router-dom";
// import axios from "axios";
// import { FaChevronRight, FaFire, FaTag, FaStar, FaBolt } from "react-icons/fa";
// import { motion, AnimatePresence } from "framer-motion";

// const DealsPage = () => {
//   const [categories, setCategories] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [featuredDeal, setFeaturedDeal] = useState(null);

//   useEffect(() => {
//     const fetchCategories = async () => {
//       try {
//         const response = await axios.get(
//           `${process.env.REACT_APP_BACKEND_URL}/api/seller/category/list`
//         );
        
//         if (response.data.success) {
//           const processedCategories = response.data.categories
//             .filter(cat => cat.published)
//             .map(category => {
//               const topLevelSubs = (category.subcategories || [])
//                 .filter(sub => sub.published)
//                 .map(sub => ({
//                   ...sub,
//                   price: Math.floor(Math.random() * 10000) + 1000,
//                   discount: Math.random() > 0.5 ? Math.floor(Math.random() * 70) + 10 : 0,
//                   tag: generateRandomTag()
//                 }));

//               let allSubcategories = [...topLevelSubs];
//               if (topLevelSubs.length < 8) {
//                 const getNestedSubs = (subs) => {
//                   let result = [];
//                   subs.forEach(sub => {
//                     if (sub.subcategories && sub.subcategories.length > 0) {
//                       const nested = sub.subcategories
//                         .filter(nestedSub => nestedSub.published)
//                         .map(nestedSub => ({
//                           ...nestedSub,
//                           price: Math.floor(Math.random() * 10000) + 1000,
//                           discount: Math.random() > 0.5 ? Math.floor(Math.random() * 70) + 10 : 0,
//                           tag: generateRandomTag()
//                         }));
//                       result = result.concat(nested);
//                     }
//                   });
//                   return result;
//                 };

//                 const nestedSubs = getNestedSubs(category.subcategories || []);
//                 const needed = 8 - topLevelSubs.length;
//                 allSubcategories = allSubcategories.concat(nestedSubs.slice(0, needed));
//               }

//               return {
//                 ...category,
//                 subcategories: allSubcategories.slice(0, 8)
//               };
//             })
//             .filter(category => category.subcategories.length > 0);
          
//           setCategories(processedCategories);
          
//           if (processedCategories.length > 0) {
//             const validCategories = processedCategories.filter(c => c.subcategories.length > 0);
//             if (validCategories.length > 0) {
//               const randomCategory = validCategories[
//                 Math.floor(Math.random() * validCategories.length)
//               ];
//               const randomSubcategory = randomCategory.subcategories[
//                 Math.floor(Math.random() * randomCategory.subcategories.length)
//               ];
//               setFeaturedDeal({
//                 ...randomSubcategory,
//                 categoryName: randomCategory.name
//               });
//             }
//           }
//         }
//         setLoading(false);
//       } catch (error) {
//         console.error("Error fetching categories:", error);
//         setError("Failed to load categories. Please try again later.");
//         setLoading(false);
//       }
//     };

//     fetchCategories();
//   }, []);

//   const generateRandomTag = () => {
//     const tags = [
//       { text: "Hot Deal", icon: <FaFire className="mr-1" />, color: "bg-gradient-to-r from-red-500 to-orange-500 text-white" },
//       { text: `From ₹${Math.floor(Math.random() * 5000) + 500}`, icon: <FaTag className="mr-1" />, color: "bg-gradient-to-r from-blue-500 to-indigo-500 text-white" },
//       { text: "Limited Time", icon: <FaBolt className="mr-1" />, color: "bg-gradient-to-r from-purple-500 to-pink-500 text-white" },
//       { text: `Upto ${Math.floor(Math.random() * 70) + 10}% off`, icon: null, color: "bg-gradient-to-r from-green-500 to-teal-500 text-white" }
//     ];
//     return tags[Math.floor(Math.random() * tags.length)];
//   };

//   const getValidImageUrl = (url) => {
//     if (!url) return "https://via.placeholder.com/150?text=No+Image";
//     if (url.includes('google.com') || url.includes('googleusercontent')) {
//       return "https://via.placeholder.com/150?text=No+Image";
//     }
//     return url;
//   };

//   if (loading) {
//     return (
//       <motion.div 
//         initial={{ opacity: 0 }}
//         animate={{ opacity: 1 }}
//         className="bg-gradient-to-b from-gray-50 to-gray-100 py-12"
//       >
//         <div className="container mx-auto px-4 space-y-12">
//           {[...Array(3)].map((_, i) => (
//             <div key={i}>
//               <div className="h-8 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full w-1/4 mb-8 animate-pulse"></div>
//               <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-6">
//                 {[...Array(6)].map((_, j) => (
//                   <motion.div 
//                     key={j} 
//                     initial={{ opacity: 0.5 }}
//                     animate={{ opacity: 0.8 }}
//                     transition={{ repeat: Infinity, repeatType: "reverse", duration: 1, delay: j * 0.1 }}
//                     className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl h-64 shadow-sm"
//                   ></motion.div>
//                 ))}
//               </div>
//             </div>
//           ))}
//         </div>
//       </motion.div>
//     );
//   }

//   if (error) {
//     return (
//       <motion.div 
//         initial={{ opacity: 0 }}
//         animate={{ opacity: 1 }}
//         className="bg-gradient-to-b from-gray-50 to-gray-100 py-12 container mx-auto px-4 text-center"
//       >
//         <div className="max-w-md mx-auto bg-white p-8 rounded-xl shadow-lg">
//           <div className="text-red-500 text-5xl mb-4">⚠️</div>
//           <h3 className="text-xl font-bold text-gray-800 mb-2">Oops! Something went wrong</h3>
//           <p className="text-gray-600 mb-6">{error}</p>
//           <motion.button
//             whileHover={{ scale: 1.05 }}
//             whileTap={{ scale: 0.95 }}
//             className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-6 py-3 rounded-lg font-medium shadow-md"
//             onClick={() => window.location.reload()}
//           >
//             Try Again
//           </motion.button>
//         </div>
//       </motion.div>
//     );
//   }

//   if (categories.length === 0) {
//     return (
//       <motion.div 
//         initial={{ opacity: 0 }}
//         animate={{ opacity: 1 }}
//         className="bg-gradient-to-b from-gray-50 to-gray-100 py-12 container mx-auto px-4 text-center"
//       >
//         <div className="max-w-md mx-auto bg-white p-8 rounded-xl shadow-lg">
//           <div className="text-gray-400 text-5xl mb-4">🛍️</div>
//           <h3 className="text-xl font-bold text-gray-800 mb-2">No Deals Available</h3>
//           <p className="text-gray-600 mb-6">Check back later for exciting offers!</p>
//           <Link to="/">
//             <motion.button
//               whileHover={{ scale: 1.05 }}
//               whileTap={{ scale: 0.95 }}
//               className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-6 py-3 rounded-lg font-medium shadow-md"
//             >
//               Go to Home
//             </motion.button>
//           </Link>
//         </div>
//       </motion.div>
//     );
//   }

//   return (
//     <motion.div 
//       initial={{ opacity: 0 }}
//       animate={{ opacity: 1 }}
//       transition={{ duration: 0.5 }}
//       className="bg-gradient-to-b from-gray-50 to-gray-100 py-12"
//     >
//       <div className="container mx-auto px-4 space-y-12">
//         {/* Featured Deals Banner */}
//         {featuredDeal && (
//           <motion.div
//             initial={{ opacity: 0, y: 20 }}
//             animate={{ opacity: 1, y: 0 }}
//             transition={{ duration: 0.6 }}
//             className="relative bg-gradient-to-r from-indigo-600 to-purple-700 rounded-2xl p-8 text-white overflow-hidden shadow-xl"
//           >
//             <div className="absolute inset-0 bg-noise opacity-10"></div>
//             <div className="absolute -right-20 -top-20 w-64 h-64 bg-white bg-opacity-10 rounded-full"></div>
//             <div className="absolute -left-20 -bottom-20 w-80 h-80 bg-white bg-opacity-5 rounded-full"></div>
            
//             <div className="relative z-10 flex flex-col md:flex-row items-center">
//               <div className="md:w-1/2 mb-8 md:mb-0">
//                 <motion.div 
//                   initial={{ x: -20 }}
//                   animate={{ x: 0 }}
//                   transition={{ delay: 0.2 }}
//                   className="inline-block px-4 py-2 bg-white bg-opacity-20 rounded-full text-sm mb-6 backdrop-blur-sm"
//                 >
//                   {featuredDeal.categoryName}
//                 </motion.div>
//                 <motion.h3 
//                   initial={{ opacity: 0, y: 10 }}
//                   animate={{ opacity: 1, y: 0 }}
//                   transition={{ delay: 0.3 }}
//                   className="text-3xl md:text-4xl font-bold mb-4 leading-tight"
//                 >
//                   {featuredDeal.name}
//                 </motion.h3>
//                 <motion.p 
//                   initial={{ opacity: 0 }}
//                   animate={{ opacity: 1 }}
//                   transition={{ delay: 0.4 }}
//                   className="mb-6 text-white text-opacity-90 max-w-md"
//                 >
//                   Don't miss out on this exclusive offer! Limited time only.
//                 </motion.p>
//                 <motion.div
//                   initial={{ opacity: 0 }}
//                   animate={{ opacity: 1 }}
//                   transition={{ delay: 0.5 }}
//                 >
//                   <Link 
//                     to={`/subcategory/${featuredDeal.id}`}
//                     className="inline-flex items-center bg-white text-indigo-600 px-8 py-3 rounded-xl font-bold hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
//                   >
//                     Shop Now <FaChevronRight className="ml-2" />
//                   </Link>
//                 </motion.div>
//               </div>
//               <div className="md:w-1/2 flex justify-center">
//                 <motion.div
//                   initial={{ scale: 0.9, rotate: -5 }}
//                   animate={{ scale: 1, rotate: 0 }}
//                   transition={{ type: "spring", stiffness: 100, damping: 10 }}
//                   whileHover={{ scale: 1.05, rotate: 5 }}
//                   className="relative"
//                 >
//                   <div className="absolute -inset-4 bg-white bg-opacity-20 rounded-2xl blur-xl"></div>
//                   <img
//                     src={getValidImageUrl(featuredDeal.media)}
//                     alt={featuredDeal.name}
//                     className="relative z-10 w-72 h-72 object-contain drop-shadow-2xl"
//                   />
//                 </motion.div>
//               </div>
//             </div>
//           </motion.div>
//         )}

//         {/* Category Sections */}
//         <AnimatePresence>
//           {categories.map((category, index) => (
//             <motion.div 
//               key={category.id}
//               initial={{ opacity: 0, y: 30 }}
//               animate={{ opacity: 1, y: 0 }}
//               transition={{ delay: index * 0.1, type: "spring" }}
//               className="bg-white p-8 rounded-2xl shadow-md border border-gray-100 overflow-hidden"
//             >
//               {/* Section Header */}
//               <div className="flex justify-between items-center mb-8">
//                 <motion.h2 
//                   whileHover={{ x: 5 }}
//                   className="text-3xl font-bold text-gray-900 flex items-center"
//                 >
//                   <span className="w-3 h-8 bg-gradient-to-b from-indigo-500 to-purple-600 rounded-full mr-4"></span>
//                   {category.name}
//                 </motion.h2>
//                 <Link 
//                   to={`/category/${category.id}`} 
//                   className="flex items-center text-indigo-600 font-medium hover:text-indigo-800 transition-colors group"
//                 >
//                   <motion.span 
//                     animate={{ x: [0, 3, 0] }}
//                     transition={{ repeat: Infinity, duration: 1.5 }}
//                     className="mr-2 group-hover:mr-3 transition-all"
//                   >
//                     View All
//                   </motion.span>
//                   <FaChevronRight className="group-hover:translate-x-1 transition-transform" />
//                 </Link>
//               </div>
              
//               {/* Subcategories Grid */}
//               <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-6">
//                 {category.subcategories.map((subcategory) => (
//                   <motion.div
//                     key={subcategory.id}
//                     initial={{ opacity: 0, scale: 0.9 }}
//                     animate={{ opacity: 1, scale: 1 }}
//                     transition={{ type: "spring" }}
//                     whileHover={{ y: -8, scale: 1.02 }}
//                     whileTap={{ scale: 0.98 }}
//                     className="relative"
//                   >
//                     <div className="absolute inset-0 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
//                     <Link
//                       to={`/subcategory/${subcategory.id}`}
//                       className="group block p-5 rounded-xl transition-all duration-300 flex flex-col h-full bg-white relative z-10 border border-gray-200 hover:border-transparent hover:shadow-lg"
//                     >
//                       {/* Product Image */}
//                       <div className="w-full h-40 mb-4 overflow-hidden flex items-center justify-center">
//                         <motion.img
//                           src={getValidImageUrl(subcategory.media)}
//                           alt={subcategory.name}
//                           className="max-h-full max-w-full object-contain transition-transform duration-500 group-hover:scale-110"
//                           initial={{ opacity: 0 }}
//                           animate={{ opacity: 1 }}
//                           transition={{ duration: 0.5 }}
//                         />
//                       </div>
                      
//                       {/* Product Title */}
//                       <h3 className="text-lg font-semibold text-gray-800 mb-2 line-clamp-2 text-center group-hover:text-indigo-600 transition-colors">
//                         {subcategory.name}
//                       </h3>
                      
//                       {/* Price */}
//                       <div className="flex justify-center items-baseline mt-auto">
//                         <span className="text-xl font-bold text-gray-900">
//                           ₹{subcategory.price.toLocaleString()}
//                         </span>
//                         {subcategory.discount > 0 && (
//                           <span className="ml-2 text-sm line-through text-gray-500">
//                             ₹{(subcategory.price * (1 + subcategory.discount/100)).toLocaleString()}
//                           </span>
//                         )}
//                       </div>
                      
//                       {/* Tag */}
//                       {subcategory.tag && (
//                         <motion.div 
//                           whileHover={{ scale: 1.05 }}
//                           className={`text-xs text-center mt-3 px-3 py-1.5 rounded-full ${subcategory.tag.color} shadow-sm`}
//                         >
//                           <div className="flex items-center justify-center">
//                             {subcategory.tag.icon}
//                             {subcategory.tag.text}
//                           </div>
//                         </motion.div>
//                       )}
//                     </Link>
//                   </motion.div>
//                 ))}
//               </div>
//             </motion.div>
//           ))}
//         </AnimatePresence>
//       </div>
//     </motion.div>
//   );
// };

// export default DealsPage;

// import React, { useState, useEffect } from "react";
// import { Link } from "react-router-dom";
// import axios from "axios";
// import { FaChevronRight, FaFire, FaTag, FaStar } from "react-icons/fa";
// import { motion, AnimatePresence } from "framer-motion";

// const DealsPage = () => {
//   const [categories, setCategories] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [featuredDeal, setFeaturedDeal] = useState(null);

//   useEffect(() => {
//     const fetchCategories = async () => {
//       try {
//         const response = await axios.get(
//           `${process.env.REACT_APP_BACKEND_URL}/api/seller/category/list`
//         );
        
//         if (response.data.success) {
//           // Process categories to add mock prices and tags
//           const processedCategories = response.data.categories
//             .filter(cat => cat.published)
//             .map(category => {
//               // First get all top-level published subcategories
//               const topLevelSubs = (category.subcategories || [])
//                 .filter(sub => sub.published)
//                 .map(sub => ({
//                   ...sub,
//                   price: Math.floor(Math.random() * 10000) + 1000,
//                   discount: Math.random() > 0.5 ? Math.floor(Math.random() * 70) + 10 : 0,
//                   rating: (Math.random() * 2 + 3).toFixed(1),
//                   tag: generateRandomTag()
//                 }));

//               // If we have less than 8 top-level subcategories, include nested ones
//               let allSubcategories = [...topLevelSubs];
//               if (topLevelSubs.length < 8) {
//                 // Function to get nested subcategories
//                 const getNestedSubs = (subs) => {
//                   let result = [];
//                   subs.forEach(sub => {
//                     if (sub.subcategories && sub.subcategories.length > 0) {
//                       const nested = sub.subcategories
//                         .filter(nestedSub => nestedSub.published)
//                         .map(nestedSub => ({
//                           ...nestedSub,
//                           price: Math.floor(Math.random() * 10000) + 1000,
//                           discount: Math.random() > 0.5 ? Math.floor(Math.random() * 70) + 10 : 0,
//                           rating: (Math.random() * 2 + 3).toFixed(1),
//                           tag: generateRandomTag()
//                         }));
//                       result = result.concat(nested);
//                     }
//                   });
//                   return result;
//                 };

//                 const nestedSubs = getNestedSubs(category.subcategories || []);
//                 // Add nested subs until we reach 8 or run out
//                 const needed = 8 - topLevelSubs.length;
//                 allSubcategories = allSubcategories.concat(nestedSubs.slice(0, needed));
//               }

//               // Limit to maximum 8 subcategories
//               return {
//                 ...category,
//                 subcategories: allSubcategories.slice(0, 8)
//               };
//             })
//             // Filter out categories with no subcategories after processing
//             .filter(category => category.subcategories.length > 0);
          
//           setCategories(processedCategories);
          
//           // Set a random featured deal
//           if (processedCategories.length > 0) {
//             const validCategories = processedCategories.filter(c => c.subcategories.length > 0);
//             if (validCategories.length > 0) {
//               const randomCategory = validCategories[
//                 Math.floor(Math.random() * validCategories.length)
//               ];
//               const randomSubcategory = randomCategory.subcategories[
//                 Math.floor(Math.random() * randomCategory.subcategories.length)
//               ];
//               setFeaturedDeal({
//                 ...randomSubcategory,
//                 categoryName: randomCategory.name
//               });
//             }
//           }
//         }
//         setLoading(false);
//       } catch (error) {
//         console.error("Error fetching categories:", error);
//         setError("Failed to load categories. Please try again later.");
//         setLoading(false);
//       }
//     };

//     fetchCategories();
//   }, []);

//   // Helper function to generate random tags
//   const generateRandomTag = () => {
//     const tags = [
//       { text: "Grab Now", icon: <FaFire className="mr-1" />, color: "bg-red-100 text-red-600" },
//       { text: `From ₹${Math.floor(Math.random() * 5000) + 500}`, icon: <FaTag className="mr-1" />, color: "bg-blue-100 text-blue-600" },
//       { text: "Limited Deal", icon: <FaStar className="mr-1" />, color: "bg-yellow-100 text-yellow-600" },
//       { text: `Upto ${Math.floor(Math.random() * 70) + 10}% off`, icon: null, color: "bg-green-100 text-green-600" }
//     ];
//     return tags[Math.floor(Math.random() * tags.length)];
//   };

//   // Improved image URL validation
//   const getValidImageUrl = (url) => {
//     if (!url) return "https://via.placeholder.com/150?text=No+Image";
//     if (url.includes('google.com') || url.includes('googleusercontent')) {
//       return "https://via.placeholder.com/150?text=No+Image";
//     }
//     return url;
//   };

//   if (loading) {
//     return (
//       <motion.div 
//         initial={{ opacity: 0 }}
//         animate={{ opacity: 1 }}
//         className="bg-gradient-to-b from-gray-50 to-gray-100 py-8"
//       >
//         <div className="container mx-auto px-4 space-y-10">
//           {[...Array(4)].map((_, i) => (
//             <div key={i}>
//               <div className="h-8 bg-gray-200 rounded w-1/4 mb-6 animate-pulse"></div>
//               <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-7 gap-4">
//                 {[...Array(7)].map((_, j) => (
//                   <motion.div 
//                     key={j} 
//                     initial={{ opacity: 0.5 }}
//                     animate={{ opacity: 0.8 }}
//                     transition={{ repeat: Infinity, repeatType: "reverse", duration: 1 }}
//                     className="bg-gray-100 rounded-lg h-48"
//                   ></motion.div>
//                 ))}
//               </div>
//             </div>
//           ))}
//         </div>
//       </motion.div>
//     );
//   }

//   if (error) {
//     return (
//       <motion.div 
//         initial={{ opacity: 0 }}
//         animate={{ opacity: 1 }}
//         className="bg-gray-50 py-8 container mx-auto px-4 text-center text-red-500"
//       >
//         {error}
//       </motion.div>
//     );
//   }

//   if (categories.length === 0) {
//     return (
//       <motion.div 
//         initial={{ opacity: 0 }}
//         animate={{ opacity: 1 }}
//         className="bg-gray-50 py-8 container mx-auto px-4 text-center text-gray-500"
//       >
//         No categories available at the moment.
//       </motion.div>
//     );
//   }

//   return (
//     <motion.div 
//       initial="hidden"
//       animate="show"
//       className="bg-gradient-to-b from-gray-50 to-gray-100 py-8"
//     >
//       <div className="container mx-auto px-4 space-y-10">
//         {/* Featured Deals Banner */}
//         {featuredDeal && (
//           <motion.div
//             initial={{ opacity: 0 }}
//             animate={{ opacity: 1 }}
//             transition={{ duration: 0.5 }}
//             className="relative bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-xl p-6 text-white overflow-hidden"
//           >
//             <div className="absolute inset-0 bg-noise opacity-10"></div>
//             <div className="relative z-10 flex flex-col md:flex-row items-center">
//               <div className="md:w-1/2 mb-6 md:mb-0">
//                 <div className="inline-block px-3 py-1 bg-white bg-opacity-20 rounded-full text-sm mb-4">
//                   {featuredDeal.categoryName}
//                 </div>
//                 <h3 className="text-2xl md:text-3xl font-bold mb-2">
//                   {featuredDeal.name}
//                 </h3>
//                 <p className="mb-4 text-white text-opacity-90">
//                   Special offer just for you!
//                 </p>
//                 <div>
//                   <Link 
//                     to={`/subcategory/${featuredDeal.id}`}
//                     className="inline-flex items-center bg-white text-indigo-600 px-6 py-3 rounded-lg font-bold hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl"
//                   >
//                     Shop Now <FaChevronRight className="ml-2" />
//                   </Link>
//                 </div>
//               </div>
//               <div className="md:w-1/2 flex justify-center">
//                 <motion.div
//                   initial={{ scale: 0.9 }}
//                   animate={{ scale: 1 }}
//                   whileHover={{ scale: 1.05 }}
//                   className="relative"
//                 >
//                   <div className="absolute -inset-4 bg-white bg-opacity-20 rounded-full blur-md"></div>
//                   <img
//                     src={getValidImageUrl(featuredDeal.media)}
//                     alt={featuredDeal.name}
//                     className="relative z-10 w-64 h-64 object-contain"
//                   />
//                 </motion.div>
//               </div>
//             </div>
//           </motion.div>
//         )}

//         {/* Category Sections */}
//         <AnimatePresence>
//           {categories.map((category, index) => (
//             <motion.div 
//               key={category.id}
//               initial={{ opacity: 0, y: 20 }}
//               animate={{ opacity: 1, y: 0 }}
//               transition={{ delay: index * 0.1 }}
//               className="bg-white p-6 rounded-xl shadow-sm border border-gray-100"
//             >
//               {/* Section Header */}
//               <div className="flex justify-between items-center mb-6">
//                 <motion.h2 
//                   whileHover={{ x: 5 }}
//                   className="text-2xl font-bold text-gray-800 flex items-center"
//                 >
//                   <span className="w-2 h-6 bg-indigo-500 rounded-full mr-3"></span>
//                   {category.name}
//                 </motion.h2>
//                 <Link 
//                   to={`/category/${category.id}`} 
//                   className="flex items-center text-indigo-600 font-medium text-sm hover:text-indigo-800 transition-colors group"
//                 >
//                   View All 
//                   <motion.span 
//                     animate={{ x: [0, 3, 0] }}
//                     transition={{ repeat: Infinity, duration: 1.5 }}
//                     className="ml-1 group-hover:ml-2 transition-all"
//                   >
//                     <FaChevronRight />
//                   </motion.span>
//                 </Link>
//               </div>
              
//               {/* Subcategories Grid */}
//               <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-7 gap-4">
//                 {category.subcategories.map((subcategory) => (
//                   <motion.div
//                     key={subcategory.id}
//                     whileHover={{ y: -5 }}
//                     whileTap={{ scale: 0.98 }}
//                   >
//                     <Link
//                       to={`/subcategory/${subcategory.id}`}
//                       className="group block p-4 border border-gray-200 rounded-lg hover:shadow-lg transition-all duration-300 flex flex-col h-full bg-white hover:border-transparent"
//                     >
//                       {/* Product Image */}
//                       <div className="w-full h-32 mb-3 overflow-hidden flex items-center justify-center relative">
//                         <motion.div
//                           initial={{ opacity: 0 }}
//                           animate={{ opacity: 1 }}
//                           transition={{ duration: 0.5 }}
//                           className="absolute inset-0 bg-gradient-to-b from-transparent to-gray-100 opacity-0 group-hover:opacity-20 transition-opacity"
//                         ></motion.div>
//                         <motion.img
//                           src={getValidImageUrl(subcategory.media)}
//                           alt={subcategory.name}
//                           className="max-h-full max-w-full object-contain group-hover:scale-105 transition-transform duration-300"
//                           whileHover={{ scale: 1.05 }}
//                         />
//                       </div>
                      
//                       {/* Product Title */}
//                       <h3 className="text-sm font-medium text-gray-800 mb-1 line-clamp-2 text-center group-hover:text-indigo-600 transition-colors">
//                         {subcategory.name.length > 20 
//                           ? `${subcategory.name.substring(0, 17)}...` 
//                           : subcategory.name}
//                       </h3>
                      
//                       {/* Rating */}
//                       <div className="flex justify-center items-center mt-1">
//                         <div className="flex items-center text-yellow-400 text-xs">
//                           <FaStar className="mr-1" />
//                           {subcategory.rating}
//                         </div>
//                       </div>
                      
//                       {/* Price */}
//                       <div className="flex justify-center items-baseline mt-2">
//                         <span className="text-lg font-bold text-gray-900">
//                           ₹{subcategory.price.toLocaleString()}
//                         </span>
//                         {subcategory.discount > 0 && (
//                           <span className="ml-2 text-xs line-through text-gray-500">
//                             ₹{(subcategory.price * (1 + subcategory.discount/100)).toLocaleString()}
//                           </span>
//                         )}
//                       </div>
                      
//                       {/* Tag */}
//                       {subcategory.tag && (
//                         <motion.div 
//                           whileHover={{ scale: 1.05 }}
//                           className={`text-xs text-center mt-2 px-2 py-1 rounded-full ${subcategory.tag.color}`}
//                         >
//                           <div className="flex items-center justify-center">
//                             {subcategory.tag.icon}
//                             {subcategory.tag.text}
//                           </div>
//                         </motion.div>
//                       )}
//                     </Link>
//                   </motion.div>
//                 ))}
//               </div>
//             </motion.div>
//           ))}
//         </AnimatePresence>
//       </div>
//     </motion.div>
//   );
// };

// export default DealsPage;

// import React, { useState, useEffect } from "react";
// import { Link } from "react-router-dom";
// import axios from "axios";
// import { FaChevronRight, FaFire, FaTag, FaStar } from "react-icons/fa";
// import { motion, AnimatePresence } from "framer-motion";

// const DealsPage = () => {
//   const [categories, setCategories] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [featuredDeal, setFeaturedDeal] = useState(null);

//   useEffect(() => {
//     const fetchCategories = async () => {
//       try {
//         const response = await axios.get(
//           `${process.env.REACT_APP_BACKEND_URL}/api/seller/category/list`
//         );
        
//         if (response.data.success) {
//           // Process categories to add mock prices and tags
//           const processedCategories = response.data.categories
//             .filter(cat => cat.published)
//             .map(category => ({
//               ...category,
//               // Only take top-level subcategories that are published
//               subcategories: (category.subcategories || [])
//                 .filter(sub => sub.published && !sub.subcategories?.length)
//                 .map(subcategory => ({
//                   ...subcategory,
//                   price: Math.floor(Math.random() * 10000) + 1000,
//                   discount: Math.random() > 0.5 ? Math.floor(Math.random() * 70) + 10 : 0,
//                   rating: (Math.random() * 2 + 3).toFixed(1),
//                   tag: generateRandomTag()
//                 }))
//             }))
//             // Filter out categories with no subcategories after processing
//             .filter(category => category.subcategories.length > 0);
          
//           setCategories(processedCategories);
          
//           // Set a random featured deal
//           if (processedCategories.length > 0) {
//             const validCategories = processedCategories.filter(c => c.subcategories.length > 0);
//             if (validCategories.length > 0) {
//               const randomCategory = validCategories[
//                 Math.floor(Math.random() * validCategories.length)
//               ];
//               const randomSubcategory = randomCategory.subcategories[
//                 Math.floor(Math.random() * randomCategory.subcategories.length)
//               ];
//               setFeaturedDeal({
//                 ...randomSubcategory,
//                 categoryName: randomCategory.name
//               });
//             }
//           }
//         }
//         setLoading(false);
//       } catch (error) {
//         console.error("Error fetching categories:", error);
//         setError("Failed to load categories. Please try again later.");
//         setLoading(false);
//       }
//     };

//     fetchCategories();
//   }, []);

//   // Helper function to generate random tags
//   const generateRandomTag = () => {
//     const tags = [
//       { text: "Grab Now", icon: <FaFire className="mr-1" />, color: "bg-red-100 text-red-600" },
//       { text: `From ₹${Math.floor(Math.random() * 5000) + 500}`, icon: <FaTag className="mr-1" />, color: "bg-blue-100 text-blue-600" },
//       { text: "Limited Deal", icon: <FaStar className="mr-1" />, color: "bg-yellow-100 text-yellow-600" },
//       { text: `Upto ${Math.floor(Math.random() * 70) + 10}% off`, icon: null, color: "bg-green-100 text-green-600" }
//     ];
//     return tags[Math.floor(Math.random() * tags.length)];
//   };

//   // Improved image URL validation
//   const getValidImageUrl = (url) => {
//     if (!url) return "https://via.placeholder.com/150?text=No+Image";
//     if (url.includes('google.com') || url.includes('googleusercontent')) {
//       return "https://via.placeholder.com/150?text=No+Image";
//     }
//     return url;
//   };

//   if (loading) {
//     return (
//       <motion.div 
//         initial={{ opacity: 0 }}
//         animate={{ opacity: 1 }}
//         className="bg-gradient-to-b from-gray-50 to-gray-100 py-8"
//       >
//         <div className="container mx-auto px-4 space-y-10">
//           {[...Array(4)].map((_, i) => (
//             <div key={i}>
//               <div className="h-8 bg-gray-200 rounded w-1/4 mb-6 animate-pulse"></div>
//               <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-7 gap-4">
//                 {[...Array(7)].map((_, j) => (
//                   <motion.div 
//                     key={j} 
//                     initial={{ opacity: 0.5 }}
//                     animate={{ opacity: 0.8 }}
//                     transition={{ repeat: Infinity, repeatType: "reverse", duration: 1 }}
//                     className="bg-gray-100 rounded-lg h-48"
//                   ></motion.div>
//                 ))}
//               </div>
//             </div>
//           ))}
//         </div>
//       </motion.div>
//     );
//   }

//   if (error) {
//     return (
//       <motion.div 
//         initial={{ opacity: 0 }}
//         animate={{ opacity: 1 }}
//         className="bg-gray-50 py-8 container mx-auto px-4 text-center text-red-500"
//       >
//         {error}
//       </motion.div>
//     );
//   }

//   if (categories.length === 0) {
//     return (
//       <motion.div 
//         initial={{ opacity: 0 }}
//         animate={{ opacity: 1 }}
//         className="bg-gray-50 py-8 container mx-auto px-4 text-center text-gray-500"
//       >
//         No categories available at the moment.
//       </motion.div>
//     );
//   }

//   return (
//     <motion.div 
//       initial="hidden"
//       animate="show"
//       className="bg-gradient-to-b from-gray-50 to-gray-100 py-8"
//     >
//       <div className="container mx-auto px-4 space-y-10">
//         {/* Featured Deals Banner */}
//         {featuredDeal && (
//           <motion.div
//             initial={{ opacity: 0 }}
//             animate={{ opacity: 1 }}
//             transition={{ duration: 0.5 }}
//             className="relative bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-xl p-6 text-white overflow-hidden"
//           >
//             <div className="absolute inset-0 bg-noise opacity-10"></div>
//             <div className="relative z-10 flex flex-col md:flex-row items-center">
//               <div className="md:w-1/2 mb-6 md:mb-0">
//                 <div className="inline-block px-3 py-1 bg-white bg-opacity-20 rounded-full text-sm mb-4">
//                   {featuredDeal.categoryName}
//                 </div>
//                 <h3 className="text-2xl md:text-3xl font-bold mb-2">
//                   {featuredDeal.name}
//                 </h3>
//                 <p className="mb-4 text-white text-opacity-90">
//                   Special offer just for you!
//                 </p>
//                 <div>
//                   <Link 
//                     to={`/subcategory/${featuredDeal.id}`}
//                     className="inline-flex items-center bg-white text-indigo-600 px-6 py-3 rounded-lg font-bold hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl"
//                   >
//                     Shop Now <FaChevronRight className="ml-2" />
//                   </Link>
//                 </div>
//               </div>
//               <div className="md:w-1/2 flex justify-center">
//                 <motion.div
//                   initial={{ scale: 0.9 }}
//                   animate={{ scale: 1 }}
//                   whileHover={{ scale: 1.05 }}
//                   className="relative"
//                 >
//                   <div className="absolute -inset-4 bg-white bg-opacity-20 rounded-full blur-md"></div>
//                   <img
//                     src={getValidImageUrl(featuredDeal.media)}
//                     alt={featuredDeal.name}
//                     className="relative z-10 w-64 h-64 object-contain"
//                   />
//                 </motion.div>
//               </div>
//             </div>
//           </motion.div>
//         )}

//         {/* Category Sections */}
//         <AnimatePresence>
//           {categories.map((category, index) => (
//             <motion.div 
//               key={category.id}
//               initial={{ opacity: 0, y: 20 }}
//               animate={{ opacity: 1, y: 0 }}
//               transition={{ delay: index * 0.1 }}
//               className="bg-white p-6 rounded-xl shadow-sm border border-gray-100"
//             >
//               {/* Section Header */}
//               <div className="flex justify-between items-center mb-6">
//                 <motion.h2 
//                   whileHover={{ x: 5 }}
//                   className="text-2xl font-bold text-gray-800 flex items-center"
//                 >
//                   <span className="w-2 h-6 bg-indigo-500 rounded-full mr-3"></span>
//                   {category.name}
//                 </motion.h2>
//                 <Link 
//                   to={`/category/${category.id}`} 
//                   className="flex items-center text-indigo-600 font-medium text-sm hover:text-indigo-800 transition-colors group"
//                 >
//                   View All 
//                   <motion.span 
//                     animate={{ x: [0, 3, 0] }}
//                     transition={{ repeat: Infinity, duration: 1.5 }}
//                     className="ml-1 group-hover:ml-2 transition-all"
//                   >
//                     <FaChevronRight />
//                   </motion.span>
//                 </Link>
//               </div>
              
//               {/* Subcategories Grid */}
//               <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-7 gap-4">
//                 {category.subcategories.slice(0, 7).map((subcategory) => (
//                   <motion.div
//                     key={subcategory.id}
//                     whileHover={{ y: -5 }}
//                     whileTap={{ scale: 0.98 }}
//                   >
//                     <Link
//                       to={`/subcategory/${subcategory.id}`}
//                       className="group block p-4 border border-gray-200 rounded-lg hover:shadow-lg transition-all duration-300 flex flex-col h-full bg-white hover:border-transparent"
//                     >
//                       {/* Product Image */}
//                       <div className="w-full h-32 mb-3 overflow-hidden flex items-center justify-center relative">
//                         <motion.div
//                           initial={{ opacity: 0 }}
//                           animate={{ opacity: 1 }}
//                           transition={{ duration: 0.5 }}
//                           className="absolute inset-0 bg-gradient-to-b from-transparent to-gray-100 opacity-0 group-hover:opacity-20 transition-opacity"
//                         ></motion.div>
//                         <motion.img
//                           src={getValidImageUrl(subcategory.media)}
//                           alt={subcategory.name}
//                           className="max-h-full max-w-full object-contain group-hover:scale-105 transition-transform duration-300"
//                           whileHover={{ scale: 1.05 }}
//                         />
//                       </div>
                      
//                       {/* Product Title */}
//                       <h3 className="text-sm font-medium text-gray-800 mb-1 line-clamp-2 text-center group-hover:text-indigo-600 transition-colors">
//                         {subcategory.name.length > 20 
//                           ? `${subcategory.name.substring(0, 17)}...` 
//                           : subcategory.name}
//                       </h3>
                      
//                       {/* Rating */}
//                       <div className="flex justify-center items-center mt-1">
//                         <div className="flex items-center text-yellow-400 text-xs">
//                           <FaStar className="mr-1" />
//                           {subcategory.rating}
//                         </div>
//                       </div>
                      
//                       {/* Price */}
//                       <div className="flex justify-center items-baseline mt-2">
//                         <span className="text-lg font-bold text-gray-900">
//                           ₹{subcategory.price.toLocaleString()}
//                         </span>
//                         {subcategory.discount > 0 && (
//                           <span className="ml-2 text-xs line-through text-gray-500">
//                             ₹{(subcategory.price * (1 + subcategory.discount/100)).toLocaleString()}
//                           </span>
//                         )}
//                       </div>
                      
//                       {/* Tag */}
//                       {subcategory.tag && (
//                         <motion.div 
//                           whileHover={{ scale: 1.05 }}
//                           className={`text-xs text-center mt-2 px-2 py-1 rounded-full ${subcategory.tag.color}`}
//                         >
//                           <div className="flex items-center justify-center">
//                             {subcategory.tag.icon}
//                             {subcategory.tag.text}
//                           </div>
//                         </motion.div>
//                       )}
//                     </Link>
//                   </motion.div>
//                 ))}
//               </div>
//             </motion.div>
//           ))}
//         </AnimatePresence>
//       </div>
//     </motion.div>
//   );
// };

// export default DealsPage;

// import React, { useState, useEffect } from "react";
// import { Link } from "react-router-dom";
// import axios from "axios";
// import { FaChevronRight, FaFire, FaTag, FaStar } from "react-icons/fa";
// import { motion, AnimatePresence } from "framer-motion";
// import { fadeIn, staggerContainer, slideIn, zoomIn } from "./Animations"; // Custom animations (defined below)

// const DealsPage = () => {
//   const [categories, setCategories] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [featuredDeal, setFeaturedDeal] = useState(null);

//   useEffect(() => {
//     const fetchCategories = async () => {
//       try {
//         const response = await axios.get(
//           `${process.env.REACT_APP_BACKEND_URL}/api/seller/category/list`
//         );
        
//         if (response.data.success) {
//           // Process categories to add mock prices and tags
//           const processedCategories = response.data.categories
//             .filter(cat => cat.published && cat.subcategories?.length > 0)
//             .map(category => ({
//               ...category,
//               subcategories: category.subcategories
//                 .filter(sub => sub.published)
//                 .map(subcategory => ({
//                   ...subcategory,
//                   price: Math.floor(Math.random() * 10000) + 1000,
//                   discount: Math.random() > 0.5 ? Math.floor(Math.random() * 70) + 10 : 0,
//                   rating: (Math.random() * 2 + 3).toFixed(1),
//                   tag: generateRandomTag()
//                 }))
//             }));
          
//           setCategories(processedCategories);
          
//           // Set a random featured deal
//           if (processedCategories.length > 0) {
//             const randomCategory = processedCategories[
//               Math.floor(Math.random() * processedCategories.length)
//             ];
//             if (randomCategory.subcategories.length > 0) {
//               const randomSubcategory = randomCategory.subcategories[
//                 Math.floor(Math.random() * randomCategory.subcategories.length)
//               ];
//               setFeaturedDeal({
//                 ...randomSubcategory,
//                 categoryName: randomCategory.name
//               });
//             }
//           }
//         }
//         setLoading(false);
//       } catch (error) {
//         console.error("Error fetching categories:", error);
//         setError("Failed to load categories. Please try again later.");
//         setLoading(false);
//       }
//     };

//     fetchCategories();
//   }, []);

//   // Helper function to generate random tags
//   const generateRandomTag = () => {
//     const tags = [
//       { text: "Grab Now", icon: <FaFire className="mr-1" />, color: "bg-red-100 text-red-600" },
//       { text: `From ₹${Math.floor(Math.random() * 5000) + 500}`, icon: <FaTag className="mr-1" />, color: "bg-blue-100 text-blue-600" },
//       { text: "Limited Deal", icon: <FaStar className="mr-1" />, color: "bg-yellow-100 text-yellow-600" },
//       { text: `Upto ${Math.floor(Math.random() * 70) + 10}% off`, icon: null, color: "bg-green-100 text-green-600" }
//     ];
//     return tags[Math.floor(Math.random() * tags.length)];
//   };

//   if (loading) {
//     return (
//       <motion.div 
//         initial={{ opacity: 0 }}
//         animate={{ opacity: 1 }}
//         className="bg-gradient-to-b from-gray-50 to-gray-100 py-8"
//       >
//         <div className="container mx-auto px-4 space-y-10">
//           {[...Array(4)].map((_, i) => (
//             <div key={i}>
//               <div className="h-8 bg-gray-200 rounded w-1/4 mb-6 animate-pulse"></div>
//               <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-7 gap-4">
//                 {[...Array(7)].map((_, j) => (
//                   <motion.div 
//                     key={j} 
//                     initial={{ opacity: 0.5 }}
//                     animate={{ opacity: 0.8 }}
//                     transition={{ repeat: Infinity, repeatType: "reverse", duration: 1 }}
//                     className="bg-gray-100 rounded-lg h-48"
//                   ></motion.div>
//                 ))}
//               </div>
//             </div>
//           ))}
//         </div>
//       </motion.div>
//     );
//   }

//   if (error) {
//     return (
//       <motion.div 
//         initial={{ opacity: 0 }}
//         animate={{ opacity: 1 }}
//         className="bg-gray-50 py-8 container mx-auto px-4 text-center text-red-500"
//       >
//         {error}
//       </motion.div>
//     );
//   }

//   return (
//     <motion.div 
//       initial="hidden"
//       animate="show"
//       variants={staggerContainer}
//       className="bg-gradient-to-b from-gray-50 to-gray-100 py-8"
//     >
//       <div className="container mx-auto px-4 space-y-10">
//         {/* Featured Deals Banner */}
//         {featuredDeal && (
//           <motion.div
//             variants={fadeIn('up', 'tween', 0.1, 0.5)}
//             className="relative bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-xl p-6 text-white overflow-hidden"
//           >
//             <div className="absolute inset-0 bg-noise opacity-10"></div>
//             <div className="relative z-10 flex flex-col md:flex-row items-center">
//               <div className="md:w-1/2 mb-6 md:mb-0">
//                 <motion.div 
//                   variants={slideIn('left', 'tween', 0.2, 1)}
//                   className="inline-block px-3 py-1 bg-white bg-opacity-20 rounded-full text-sm mb-4"
//                 >
//                   {featuredDeal.categoryName}
//                 </motion.div>
//                 <motion.h3 
//                   variants={slideIn('left', 'tween', 0.3, 1)}
//                   className="text-2xl md:text-3xl font-bold mb-2"
//                 >
//                   {featuredDeal.name}
//                 </motion.h3>
//                 <motion.p 
//                   variants={slideIn('left', 'tween', 0.4, 1)}
//                   className="mb-4 text-white text-opacity-90"
//                 >
//                   Special offer just for you!
//                 </motion.p>
//                 <motion.div variants={slideIn('left', 'tween', 0.5, 1)}>
//                   <Link 
//                     to={`/subcategory/${featuredDeal.id}`}
//                     className="inline-flex items-center bg-white text-indigo-600 px-6 py-3 rounded-lg font-bold hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl"
//                   >
//                     Shop Now <FaChevronRight className="ml-2" />
//                   </Link>
//                 </motion.div>
//               </div>
//               <div className="md:w-1/2 flex justify-center">
//                 <motion.div
//                   variants={zoomIn(0.6, 1)}
//                   whileHover={{ scale: 1.05 }}
//                   className="relative"
//                 >
//                   <div className="absolute -inset-4 bg-white bg-opacity-20 rounded-full blur-md"></div>
//                   <img
//                     src={featuredDeal.media || "https://via.placeholder.com/300?text=Featured+Deal"}
//                     alt={featuredDeal.name}
//                     className="relative z-10 w-64 h-64 object-contain"
//                     onError={(e) => {
//                       e.target.src = "https://via.placeholder.com/300?text=No+Image";
//                     }}
//                   />
//                 </motion.div>
//               </div>
//             </div>
//           </motion.div>
//         )}

//         {/* Category Sections */}
//         <AnimatePresence>
//           {categories.map((category, index) => (
//             <motion.div 
//               key={category.id}
//               variants={fadeIn('up', 'tween', index * 0.1, 0.5)}
//               className="bg-white p-6 rounded-xl shadow-sm border border-gray-100"
//             >
//               {/* Section Header */}
//               <div className="flex justify-between items-center mb-6">
//                 <motion.h2 
//                   whileHover={{ x: 5 }}
//                   className="text-2xl font-bold text-gray-800 flex items-center"
//                 >
//                   <span className="w-2 h-6 bg-indigo-500 rounded-full mr-3"></span>
//                   {category.name}
//                 </motion.h2>
//                 <Link 
//                   to={`/category/${category.id}`} 
//                   className="flex items-center text-indigo-600 font-medium text-sm hover:text-indigo-800 transition-colors group"
//                 >
//                   View All 
//                   <motion.span 
//                     animate={{ x: [0, 3, 0] }}
//                     transition={{ repeat: Infinity, duration: 1.5 }}
//                     className="ml-1 group-hover:ml-2 transition-all"
//                   >
//                     <FaChevronRight />
//                   </motion.span>
//                 </Link>
//               </div>
              
//               {/* Subcategories Grid */}
//               <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-7 gap-4">
//                 {category.subcategories.slice(0, 7).map((subcategory) => (
//                   <motion.div
//                     key={subcategory.id}
//                     whileHover={{ y: -5 }}
//                     whileTap={{ scale: 0.98 }}
//                   >
//                     <Link
//                       to={`/subcategory/${subcategory.id}`}
//                       className="group block p-4 border border-gray-200 rounded-lg hover:shadow-lg transition-all duration-300 flex flex-col h-full bg-white hover:border-transparent"
//                     >
//                       {/* Product Image */}
//                       <div className="w-full h-32 mb-3 overflow-hidden flex items-center justify-center relative">
//                         <motion.div
//                           initial={{ opacity: 0 }}
//                           animate={{ opacity: 1 }}
//                           transition={{ duration: 0.5 }}
//                           className="absolute inset-0 bg-gradient-to-b from-transparent to-gray-100 opacity-0 group-hover:opacity-20 transition-opacity"
//                         ></motion.div>
//                         {subcategory.media ? (
//                           <motion.img
//                             src={subcategory.media}
//                             alt={subcategory.name}
//                             className="max-h-full max-w-full object-contain group-hover:scale-105 transition-transform duration-300"
//                             onError={(e) => {
//                               e.target.src = "https://via.placeholder.com/150?text=No+Image";
//                             }}
//                             whileHover={{ scale: 1.05 }}
//                           />
//                         ) : (
//                           <div className="w-full h-full bg-gray-50 flex items-center justify-center text-gray-400 rounded">
//                             No Image
//                           </div>
//                         )}
//                       </div>
                      
//                       {/* Product Title */}
//                       <h3 className="text-sm font-medium text-gray-800 mb-1 line-clamp-2 text-center group-hover:text-indigo-600 transition-colors">
//                         {subcategory.name.length > 20 
//                           ? `${subcategory.name.substring(0, 17)}...` 
//                           : subcategory.name}
//                       </h3>
                      
//                       {/* Rating */}
//                       <div className="flex justify-center items-center mt-1">
//                         <div className="flex items-center text-yellow-400 text-xs">
//                           <FaStar className="mr-1" />
//                           {subcategory.rating}
//                         </div>
//                       </div>
                      
//                       {/* Price */}
//                       <div className="flex justify-center items-baseline mt-2">
//                         <span className="text-lg font-bold text-gray-900">
//                           ₹{subcategory.price.toLocaleString()}
//                         </span>
//                         {subcategory.discount > 0 && (
//                           <span className="ml-2 text-xs line-through text-gray-500">
//                             ₹{(subcategory.price * (1 + subcategory.discount/100)).toLocaleString()}
//                           </span>
//                         )}
//                       </div>
                      
//                       {/* Tag */}
//                       {subcategory.tag && (
//                         <motion.div 
//                           whileHover={{ scale: 1.05 }}
//                           className={`text-xs text-center mt-2 px-2 py-1 rounded-full ${subcategory.tag.color}`}
//                         >
//                           <div className="flex items-center justify-center">
//                             {subcategory.tag.icon}
//                             {subcategory.tag.text}
//                           </div>
//                         </motion.div>
//                       )}
//                     </Link>
//                   </motion.div>
//                 ))}
//               </div>
//             </motion.div>
//           ))}
//         </AnimatePresence>

//         {/* Additional Categories (condensed view) */}
//         {categories.length > 4 && (
//           <motion.div
//             variants={fadeIn('up', 'tween', 0.5, 0.5)}
//             className="bg-white p-6 rounded-xl shadow-sm border border-gray-100"
//           >
//             <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
//               <span className="w-2 h-6 bg-indigo-500 rounded-full mr-3"></span>
//               More Categories
//             </h2>
//             <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
//               {categories.slice(4).map(category => (
//                 <motion.div 
//                   key={category.id} 
//                   whileHover={{ y: -3 }}
//                   className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-all bg-white"
//                 >
//                   <h3 className="font-bold text-lg mb-3 text-gray-800">{category.name}</h3>
//                   <div className="grid grid-cols-2 gap-2">
//                     {category.subcategories.slice(0, 4).map(subcategory => (
//                       <Link
//                         key={subcategory.id}
//                         to={`/subcategory/${subcategory.id}`}
//                         className="text-sm text-gray-700 hover:text-indigo-600 line-clamp-1 transition-colors flex items-center"
//                       >
//                         <motion.span 
//                           whileHover={{ x: 3 }}
//                           className="inline-block w-1 h-1 bg-gray-400 rounded-full mr-2"
//                         ></motion.span>
//                         {subcategory.name}
//                       </Link>
//                     ))}
//                   </div>
//                   {category.subcategories.length > 4 && (
//                     <Link
//                       to={`/category/${category.id}`}
//                       className="inline-flex items-center mt-3 text-sm text-indigo-600 font-medium hover:text-indigo-800 group transition-colors"
//                     >
//                       +{category.subcategories.length - 4} more
//                       <FaChevronRight className="ml-1 text-xs opacity-0 group-hover:opacity-100 transition-opacity" />
//                     </Link>
//                   )}
//                 </motion.div>
//               ))}
//             </div>
//           </motion.div>
//         )}
//       </div>
//     </motion.div>
//   );
// };


// export default DealsPage;

// import React, { useState, useEffect } from "react";
// import { Link } from "react-router-dom";
// import axios from "axios";
// import { FaChevronRight } from "react-icons/fa";

// const DealsPage = () => {
//   const [categories, setCategories] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);

//   useEffect(() => {
//     const fetchCategories = async () => {
//       try {
//         const response = await axios.get(
//           `${process.env.REACT_APP_BACKEND_URL}/api/seller/category/list`
//         );
        
//         if (response.data.success) {
//           // Process categories to add mock prices and tags
//           const processedCategories = response.data.categories
//             .filter(cat => cat.published && cat.subcategories?.length > 0)
//             .map(category => ({
//               ...category,
//               subcategories: category.subcategories
//                 .filter(sub => sub.published)
//                 .map(subcategory => ({
//                   ...subcategory,
//                   // Mock data - replace with actual products in a real app
//                   price: Math.floor(Math.random() * 10000) + 1000,
//                   tag: generateRandomTag()
//                 }))
//             }));
          
//           setCategories(processedCategories);
//         }
//         setLoading(false);
//       } catch (error) {
//         console.error("Error fetching categories:", error);
//         setError("Failed to load categories. Please try again later.");
//         setLoading(false);
//       }
//     };

//     fetchCategories();
//   }, []);

//   // Helper function to generate random tags like "Grab Now", "From ₹XXXX", etc.
//   const generateRandomTag = () => {
//     const rand = Math.random();
//     if (rand > 0.7) return "Grab Now";
//     if (rand > 0.5) return `From ₹${Math.floor(Math.random() * 5000) + 500}`;
//     if (rand > 0.3) return `From ₹${Math.floor(Math.random() * 3000) + 499}*`;
//     return "";
//   };

//   if (loading) {
//     return (
//       <div className="bg-gray-50 py-8">
//         <div className="container mx-auto px-4 space-y-10">
//           {[...Array(4)].map((_, i) => (
//             <div key={i}>
//               <div className="h-8 bg-gray-200 rounded w-1/4 mb-6 animate-pulse"></div>
//               <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-7 gap-4">
//                 {[...Array(7)].map((_, j) => (
//                   <div key={j} className="bg-gray-100 rounded-lg h-48 animate-pulse"></div>
//                 ))}
//               </div>
//             </div>
//           ))}
//         </div>
//       </div>
//     );
//   }

//   if (error) {
//     return (
//       <div className="bg-gray-50 py-8 container mx-auto px-4 text-center text-red-500">
//         {error}
//       </div>
//     );
//   }

//   return (
//     <div className="bg-gray-50 py-8">
//       <div className="container mx-auto px-4 space-y-10">
//         {/* Featured Deals Banner */}
//         {/* <div className="bg-gradient-to-r from-orange-500 to-pink-500 rounded-lg p-6 text-white">
//           <h3 className="text-2xl font-bold mb-2">Summer Special Deals!</h3>
//           <p className="mb-4">Up to 70% off on top categories</p>
//           <button className="bg-white text-orange-600 px-4 py-2 rounded font-medium hover:bg-gray-100 transition-colors">
//             Shop Now
//           </button>
//         </div> */}

//         {/* Category Sections */}
//         {categories.map((category) => (
//           <div key={category.id} className="bg-white p-6 rounded-lg shadow-sm">
//             {/* Section Header */}
//             <div className="flex justify-between items-center mb-6">
//               <h2 className="text-xl font-bold text-gray-800">{category.name}</h2>
//               <Link 
//                 to={`/category/${category.id}`} 
//                 className="flex items-center text-blue-600 font-medium text-sm"
//               >
//                 View All <FaChevronRight className="ml-1" />
//               </Link>
//             </div>
            
//             {/* Subcategories Grid */}
//             <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-7 gap-4">
//               {category.subcategories.slice(0, 7).map((subcategory) => (
//                 <Link
//                   key={subcategory.id}
//                   to={`/subcategory/${subcategory.id}`}
//                   className="group p-4 border rounded-lg hover:shadow-md transition-all duration-200 flex flex-col"
//                 >
//                   {/* Product Image */}
//                   <div className="w-full h-32 mb-3 overflow-hidden flex items-center justify-center">
//                     {subcategory.media ? (
//                       <img
//                         src={subcategory.media}
//                         alt={subcategory.name}
//                         className="max-h-full max-w-full object-contain group-hover:scale-105 transition-transform duration-200"
//                         onError={(e) => {
//                           e.target.src = "https://via.placeholder.com/150?text=No+Image";
//                         }}
//                       />
//                     ) : (
//                       <div className="w-full h-full bg-gray-100 flex items-center justify-center text-gray-400">
//                         No Image
//                       </div>
//                     )}
//                   </div>
                  
//                   {/* Product Title */}
//                   <h3 className="text-sm font-medium text-gray-800 mb-1 line-clamp-2 text-center">
//                     {subcategory.name.length > 20 
//                       ? `${subcategory.name.substring(0, 17)}...` 
//                       : subcategory.name}
//                   </h3>
                  
//                   {/* Price or Tag */}
//                   {subcategory.tag && (
//                     <p className={`text-xs text-center mt-auto ${
//                       subcategory.tag.includes("Grab Now") 
//                         ? "text-green-600 font-semibold" 
//                         : "text-gray-600"
//                     }`}>
//                       {subcategory.tag}
//                     </p>
//                   )}
//                 </Link>
//               ))}
//             </div>
//           </div>
//         ))}

//         {/* Additional Categories (condensed view) */}
//         {categories.length > 4 && (
//           <div className="bg-white p-6 rounded-lg shadow-sm">
//             <h2 className="text-xl font-bold text-gray-800 mb-6">More Categories</h2>
//             <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
//               {categories.slice(4).map(category => (
//                 <div key={category.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
//                   <h3 className="font-bold text-lg mb-3">{category.name}</h3>
//                   <div className="grid grid-cols-2 gap-2">
//                     {category.subcategories.slice(0, 4).map(subcategory => (
//                       <Link
//                         key={subcategory.id}
//                         to={`/subcategory/${subcategory.id}`}
//                         className="text-sm text-gray-700 hover:text-blue-600 line-clamp-1"
//                       >
//                         {subcategory.name}
//                       </Link>
//                     ))}
//                   </div>
//                   {category.subcategories.length > 4 && (
//                     <Link
//                       to={`/category/${category.id}`}
//                       className="inline-block mt-3 text-sm text-blue-600 font-medium"
//                     >
//                       +{category.subcategories.length - 4} more
//                     </Link>
//                   )}
//                 </div>
//               ))}
//             </div>
//           </div>
//         )}
//       </div>
//     </div>
//   );
// };

// export default DealsPage;